<template>
  <div ref="examRoot" class="exam-root notranslate" translate="no">
    <!--  -->
    <div class="loading-overlay" v-if="examDataLoading && !showloadingView">
      <div class="loading-container">
        <div class="loading-header">
          <div class="loading-exam-name">Loading Exam Data</div>
        </div>
        <div class="loading-countdown-section">
          <i class="el-icon-loading" style="font-size: 40px; color: #0e409f; margin-bottom: 20px;"></i>
          <div class="loading-countdown-hint">
            Please wait while the exam is being prepared.<br/>
            Do not close or refresh this page.
          </div>
        </div>
      </div>
    </div>
    <div class="loading-overlay" v-if="showloadingView">
      <div class="loading-container">
        <div class="loading-header">
          <div class="loading-project" v-if="test_project_name">{{ test_project_name }}</div>
          <div class="loading-exam-name">{{ test_event }}</div>
        </div>

        <div class="loading-countdown-section">
          <div class="loading-countdown-label">Exam starts in</div>
          <div class="loading-countdown-boxes">
            <div class="countdown-box">{{ hourLab }}</div>
            <span class="countdown-sep">:</span>
            <div class="countdown-box">{{ minuteLab }}</div>
            <span class="countdown-sep">:</span>
            <div class="countdown-box">{{ secondLab }}</div>
          </div>
          <div class="loading-countdown-hint">
            To avoid entry congestion, please be patient and wait for a random buffer time.<br/>
            Waiting time is not counted as exam time.
          </div>
        </div>

      </div>
    </div>
    <TextChat
      v-if="isConnect"
      :user_id="user_id"
      :group_id="group_id"
      @correctSessionPicSync="onCorrectSessionPicSync"
      @correctQuestionSync="onCorrectQuestionSync"
      @teamChatMessage="handleTeamChatNotification"
      @teamSubmitNotification="handleTeamSubmitNotification"
      @onlineStatusUpdate="handleOnlineStatusUpdate"
      @getWsOut="onGetWsOut"
    />
    <div class="exam-top">
      <div class="exam-top-main">
        <div class="exam-proctor-summary">
          <Invigilate
            ref="invigilate"
            v-if="!DEBUG_DEV && signStatus"
            v-model="status"
            :template_id="template_id"
            :project-code="project_code"
            :screen-only="screenOnlyProctoring"
            @invigilateCallback="invigilateCallback"
            @visibleChange="refreshVisible"
            @penaltySummary="applyPenaltySummary"
            class="invigilateStyle"
          ></Invigilate>
        </div>

        <div class="exam-session-summary">
          <div class="projectStyle" data-exam-integrity-field="project-name">
            {{ test_project_name ? (test_project_name + ' - ' + test_event) : test_event }}
          </div>
          <div class="exam-submit-warning" role="note">
            <i class="el-icon-warning" aria-hidden="true"></i>
            <span>You must submit before the exam ends. Failure to submit manually will result in a 5-point deduction.</span>
          </div>
        </div>
      </div>

      <section
        class="exam-penalty-bar"
        :class="'is-' + penaltyTone"
        role="status"
        aria-live="polite"
        aria-label="Exam penalty summary"
      >
        <div class="exam-penalty-total">
          <span>Total Deduction</span>
          <strong>{{ penaltySummary.total_deduction }}<small> pts</small></strong>
          <em v-if="!penaltySummary.score_valid">Invalid score</em>
        </div>
        <div class="exam-penalty-metrics">
          <div class="exam-penalty-metric">
            <span>Talking</span>
            <strong>-{{ penaltySummary.talking_points }}<small> pts</small></strong>
            <small>{{ talkingDurationLabel }}</small>
          </div>
          <div class="exam-penalty-metric">
            <span>Camera Leave</span>
            <strong>-{{ penaltySummary.camera_leave_points }}<small> pts</small></strong>
            <small>{{ penaltySummary.camera_leave_count }} {{ penaltySummary.camera_leave_count === 1 ? 'time' : 'times' }}</small>
          </div>
          <div class="exam-penalty-metric">
            <span>Network Disconnect</span>
            <strong>-{{ penaltySummary.network_disconnect_points }}<small> pts</small></strong>
            <small>{{ penaltySummary.network_disconnect_count }} {{ penaltySummary.network_disconnect_count === 1 ? 'time' : 'times' }}</small>
          </div>
        </div>
        <p class="exam-penalty-rule">
          Penalty: -1pt/5s talking · -2pt/camera leave · -1pt/disconnect · 20pts invalidates the score.
        </p>
      </section>
    </div>
    <div class="exam-content" id="exam-content">
      <!-- Station Navigation Tabs -->
      <div class="station-nav-tabs" v-if="sections.length > 1">
        <div
          v-for="(section, idx) in sections"
          :key="idx"
          :class="['station-tab', {
            'station-tab-active': currentSectionIndex === idx,
            'station-tab-locked': individualMode && getIndividualSectionStatus(idx) === 'locked',
            'station-tab-completed': individualMode && ['submitted', 'expired'].indexOf(getIndividualSectionStatus(idx)) !== -1
          }]"
          @click="switchSection(idx)"
        >
          <div class="station-tab-number">{{ idx + 1 }}</div>
          <div class="station-tab-label">{{ section.section_title || (individualMode ? 'Stage ' : 'Station ') + (idx + 1) }}</div>
        </div>
      </div>
      <div class="exam-empty-width-view" />
      <div style="flex: 1; display: flex; flex-direction: column; overflow: hidden">
        <div class="exam-empty-height-view" />
        <div style="width: 100%; flex: 1; overflow: hidden;">
          <div
            v-if="canExam && (havePicUrl || (individualMode && hasCurrentModuleMaterial))"
            class="exam-content-page"
          >
            <div class="exam-material-pane">
              <CandidateWatermark :watermark-text="candidateEmail" />
              <div
              style="width: 100%; flex: 1; overflow-y: scroll"
              id="exam-image"
              ref="materialScroll"
              class="exam-material-scroll"
              @scroll.passive="handleMaterialScroll"
            >
              <section v-if="individualMode && hasCurrentModuleMaterial" class="module-material">
                <div class="module-material-kicker">Stage {{ currentSectionIndex + 1 }} Material</div>
                <div ref="moduleMaterialContent" class="module-material-content" data-exam-integrity-field="stage-material">
                  <div
                    v-if="currentModuleInstruction"
                    class="module-material-copy"
                    v-html="formatContent(currentModuleInstruction)"
                  ></div>
                  <div
                    v-if="currentModuleTextMaterial"
                    class="module-material-copy"
                    v-html="formatContent(currentModuleTextMaterial)"
                  ></div>
                  <div
                    v-for="(material, materialIndex) in currentModuleHtmlMaterials"
                    :key="'stage-material-' + materialIndex"
                    class="module-material-copy"
                    v-html="formatContent(material.content || material)"
                  ></div>
                </div>
                <div v-if="candidateName" class="candidate-inline-mark candidate-material-mark" data-exam-integrity-ignore>
                  Candidate · {{ candidateName }}
                </div>
                <audio
                  v-if="currentModuleAudioUrl"
                  class="module-material-audio"
                  :src="currentModuleAudioUrl"
                  controls
                  controlsList="nodownload"
                ></audio>
              </section>
              <div v-if="havePicUrl" class="exam-image">
                <PDF
                  ref="pdfView"
                  @pdfCallback="pdfCallback"
                  :parentReloadPic="parentReloadPic"
                  :parentShowBigPic="parentShowBigPic"
                  :candidate-name="candidateName"
                  :candidate-email="candidateEmail"
                ></PDF>
              </div>
            </div>
            </div>
            <div v-if="havePicUrl" class="exam-page-bottom">

              <div class="exam-page-bottom">

                <div class="reloadDiv" @click="showReload">
                  Reload
                </div>

                <div class="reloadDiv" @click="showPreImage">
                  {{ getShowWord("page_pre") }}
                </div>

                <div class="reloadDiv" >
                  {{
                    examImageSize > 0
                      ? currentSelectedImageIndex + "/" + examImageSize
                      : "0/0"
                  }}
                </div>

                <div class="reloadDiv" @click="showNextImage">
                  {{ getShowWord("page_next") }}
                </div>

                <div class="reloadDiv" @click="enlargeClick">
                  Enlarge
                </div>


                <!-- <div class="exam-page-bottom-text" @click="showPreImage">
                  {{ getShowWord("page_pre") }}
                </div>
                <div class="exam-page-bottom-text">
                  {{
                    examImageSize > 0
                      ? currentSelectedImageIndex + "/" + examImageSize
                      : "0/0"
                  }}
                </div>
                <div class="exam-page-bottom-text" @click="showNextImage">
                  {{ getShowWord("page_next") }}
                </div> -->
              </div>
              <!-- <div class="exam-goto-page" @click="beforeShowSelectImageDialog">
                {{ getShowWord("gotoPage") }}
              </div> -->
              
            </div>

          </div>

          <div v-if="canExam && havePicUrl == false && relayMode" class="exam-content-nourl-page" >
            <span style="font-size: 18px;font-weight: 500;">Instructions:</span> <br>
            1. Click on the Station tabs to switch between different Stations.<br><br>
            2. After completing a question, click the Confirm button below the question. The system will score your response in real time, update the total score, and automatically navigate to the next question.<br><br>
            3. The remaining time and total score displayed in the status bar on the right represent the shared remaining time and cumulative score for your entire team.<br><br>
            4. You may use the Team Chat function on the right control bar to discuss your team's competition strategy or coordinate the next team member to take over the quiz. However, discussion of question-related content is strictly prohibited.<br><br>
            5. Only one team member is allowed to access the exam interface at a time. If another team member logs in, the member currently using the quiz interface will be automatically logged out.<br><br>
            6. During the exam, you are permitted to use scratch paper and take restroom breaks. Please ensure you return within 2 minutes if you leave for a restroom break.<br><br>
            7. "Submit Entire Exam" refers to the submission of the entire team's exam paper. Once any three members of the team confirm this action, the system will submit the team's exam paper and conclude the exam.
          </div>

          <div v-if="canExam && havePicUrl == false && individualMode" class="exam-content-nourl-page" >
            <span style="font-size: 18px;font-weight: 500;">Instructions:</span> <br>
            1. Complete the five stages in order. A later stage remains locked until the current stage is submitted or its time expires.<br><br>
            2. Each stage has its own 2-hour countdown. Leaving the page does not pause the current stage timer.<br><br>
            3. Your overall exam window lasts 24 hours from the moment you enter Stage 1. You cannot enter after that window closes.<br><br>
            4. Submitted or expired stages are final and cannot be reopened.<br><br>
            5. A response is submitted and scored only after you click Confirm. When you submit a stage, you can start the next stage immediately or return to the exam list and start it later.
          </div>

        </div>
        <div class="exam-empty-height-view" />

      </div>
      <div class="exam-empty-width-view" />
      <div style="flex: 1; display: flex; flex-direction: column">
        <div class="exam-empty-height-view" />
        <div
          style="
            width: 100%;
            height: 100%;
            flex: 1;
            display: flex;
            flex-direction: column;
          "
        >
          <div
            class="exam-content-page"
            style="background: white"
          >
            <CandidateWatermark :watermark-text="candidateEmail" />
            <div style="flex: 1; width: 100%; height: 100%; overflow-y: scroll">
              <!-- Station Completion State -->
              <div v-if="currentSectionCompleted" class="exam-completed-state">
                <div class="completion-icon">&#10003;</div>
                <div class="completion-title">You have completed this {{ individualMode ? 'stage' : 'station' }}.</div>
                <div class="completion-subtitle">
                  {{ individualMode
                    ? 'All questions in this stage have been answered. Submit this stage when you are ready to continue.'
                    : 'All questions in this station have been answered. You may switch to another station or submit your exam when ready.' }}
                </div>
              </div>
              <!-- Question Content -->
              <div v-else class="exam-question-content;">
                <div style="margin: 20px">
                  <div class="questionTitle" data-exam-integrity-field="question-title">
                    <div
                      v-html="formatContent(currentSelectedQuestion['question_question'])"
                    ></div>
                    <!-- <kats style="width: 50px;" :infoStr="currentSelectedQuestion['question_question_string']"></kats> -->

                  </div>
                  <div v-if="candidateName" class="candidate-inline-mark candidate-question-mark" data-exam-integrity-ignore>
                    Candidate · {{ candidateName }}
                  </div>
                  <div v-if="currentSelectedQuestion.question_type == 'input'">
                    <el-input
                      class="inputAnswerStyle"
                      type="textarea"
                      :rows="5"
                      :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                      placeholder="Please input answers here"
                      v-model="currentSelectedQuestion.inputAnswer"
                      @change="
                        saveAnswer(
                          currentSelectedQuestion.inputAnswer,
                          currentSelectedQuestion
                        )
                      "
                    >
                    </el-input>

                    <div style="display: flex">
                      <a
                        v-if="
                          currentSelectedQuestion.answer_img_url != '' &&
                          currentSelectedQuestion.answer_img_url != undefined
                        "
                        style="margin-top: 20px; margin-left: 20px"
                        class="attach"
                        target="_blank"
                        :href="currentSelectedQuestion.answer_img_url"
                        slot="reference"
                        >Show Pic
                      </a>
                      <div
                        style="
                          display: flex;
                          margin-top: 20px;
                          margin-left: 20px;
                        "
                      >
                        <el-upload
                          class="upload-demo mt-2 attach"
                          ref="upload"
                          action="#"
                          :limit="1"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          :before-upload="beforeAttachUpload"
                        >
                          <el-button
                            slot="trigger"
                            size="small"
                            type="primary"
                            :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                            >Select File
                          </el-button>
                        </el-upload>
                        <el-button
                          size="small"
                          type="danger"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          v-if="
                            currentSelectedQuestion.answer_img_url != '' &&
                            currentSelectedQuestion.answer_img_url != undefined
                          "
                          style="margin-left: 20px"
                          @click="clickRemoveQuestionUrlAnswer"
                          >Remove</el-button
                        >
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div
                      class="option"
                      data-exam-integrity-option="A"
                      v-if="
                        currentSelectedQuestion.question_optionA &&
                        currentSelectedQuestion.question_optionA != ''
                      "
                    >
                      <div class="option-main">
                        <el-radio
                          @change="saveAnswer('A', currentSelectedQuestion)"
                          :value="currentSelectedQuestion.choice"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          label="A"
                        >
                          <span class="optionTitle">A</span>
                        </el-radio>
                        <div
                          class="div_p"
                          v-html="formatContent(currentSelectedQuestion.question_optionA)"
                        ></div>
                      </div>
                      <!-- <le-editor  v-model="testAss" :indent-unit="4" :fullscreen="false" hljs-css="agate" :image-uploader="imageUploader" @save="ssssave"></le-editor> -->

                      <!-- <p>Consider the function \(f(x) = \frac[x^2 + 4x + 4}{x^2 - 4}\). What is the domain of ¡(f(x)\)?</p> -->
                      <!-- <kats class="div_p" :infoStr="currentSelectedQuestion['question_optionA_string']"></kats> -->

                      <div v-if="candidateName" class="candidate-inline-mark" data-exam-integrity-ignore>
                        Candidate · {{ candidateName }}
                      </div>
                    </div>
                    <div
                      class="option"
                      data-exam-integrity-option="B"
                      v-if="
                        currentSelectedQuestion.question_optionB &&
                        currentSelectedQuestion.question_optionB != ''
                      "
                    >
                      <div class="option-main">
                        <el-radio
                          @change="saveAnswer('B', currentSelectedQuestion)"
                          :value="currentSelectedQuestion.choice"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          label="B"
                        >
                          <span class="optionTitle">B</span>
                        </el-radio>
                        <div
                          class="div_p"
                          v-html="formatContent(currentSelectedQuestion.question_optionB)"
                        ></div>
                      </div>
                      <!-- <kats class="div_p" :infoStr="currentSelectedQuestion['question_optionB_string']"></kats> -->

                      <div v-if="candidateName" class="candidate-inline-mark" data-exam-integrity-ignore>
                        Candidate · {{ candidateName }}
                      </div>
                    </div>
                    <div
                      class="option"
                      data-exam-integrity-option="C"
                      v-if="
                        currentSelectedQuestion.question_optionC &&
                        currentSelectedQuestion.question_optionC != ''
                      "
                    >
                      <div class="option-main">
                        <el-radio
                          @change="saveAnswer('C', currentSelectedQuestion)"
                          :value="currentSelectedQuestion.choice"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          label="C"
                        >
                          <span class="optionTitle">C</span>
                        </el-radio>
                        <div
                          class="div_p"
                          v-html="formatContent(currentSelectedQuestion.question_optionC)"
                        ></div>
                      </div>

                      <!-- <kats class="div_p" :infoStr="currentSelectedQuestion['question_optionC_string']"></kats> -->

                      <div v-if="candidateName" class="candidate-inline-mark" data-exam-integrity-ignore>
                        Candidate · {{ candidateName }}
                      </div>
                    </div>
                    <div
                      class="option"
                      data-exam-integrity-option="D"
                      v-if="
                        currentSelectedQuestion.question_optionD &&
                        currentSelectedQuestion.question_optionD != ''
                      "
                    >
                      <div class="option-main">
                        <el-radio
                          @change="saveAnswer('D', currentSelectedQuestion)"
                          :value="currentSelectedQuestion.choice"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          label="D"
                        >
                          <span class="optionTitle">D</span>
                        </el-radio>
                        <div
                          class="div_p"
                          v-html="formatContent(currentSelectedQuestion.question_optionD)"
                        ></div>
                      </div>
                      <!-- <kats class="div_p" :infoStr="currentSelectedQuestion['question_optionD_string']"></kats> -->

                      <div v-if="candidateName" class="candidate-inline-mark" data-exam-integrity-ignore>
                        Candidate · {{ candidateName }}
                      </div>
                    </div>
                    <div
                      class="option"
                      data-exam-integrity-option="E"
                      v-if="
                        currentSelectedQuestion.question_optionE &&
                        currentSelectedQuestion.question_optionE != ''
                      "
                    >
                      <div class="option-main">
                        <el-radio
                          @change="saveAnswer('E', currentSelectedQuestion)"
                          :value="currentSelectedQuestion.choice"
                          :disabled="isQuestionConfirmed(currentSelectedQuestion) || isConfirmingAnswer"
                          label="E"
                        >
                          <span class="optionTitle">E</span>
                        </el-radio>
                        <div
                          class="div_p"
                          v-html="formatContent(currentSelectedQuestion.question_optionE)"
                        ></div>
                      </div>
                      <!-- <kats class="div_p" :infoStr="currentSelectedQuestion['question_optionE_string']"></kats> -->

                      <div v-if="candidateName" class="candidate-inline-mark" data-exam-integrity-ignore>
                        Candidate · {{ candidateName }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="exam-page-bottom">
              <div class="exam-page-bottom">

                <div class="reloadDiv" @click="loadExam">
                  Reload
                </div>

                <div class="reloadDiv" >
                  {{
                    getCurrentSectionQuestionCount() > 0
                      ? getCurrentSectionQuestionIndex() +
                        1 +
                        "/" +
                        getCurrentSectionQuestionCount()
                      : "0/0"
                  }}
                </div>

                <div
                  v-if="!currentSectionCompleted"
                  class="reloadDiv"
                  :class="{ 'is-disabled': isConfirmingAnswer }"
                  @click="showNextQuestion"
                >
                  {{ isConfirmingAnswer ? 'Saving...' : (isQuestionConfirmed(currentSelectedQuestion) ? 'Next' : 'Confirm') }}
                </div>
                <div v-else class="reloadDiv" style="background-color: #999; cursor: not-allowed;">
                  Completed
                </div>
                
                <!-- <div class="exam-page-bottom-text" @click="showPreQuestion">
                  {{ getShowWord("question_pre") }}
                </div>
                <div class="exam-page-bottom-text">
                  {{
                    examQuestionSize > 0
                      ? currentSelectedQuestionIndex +
                        1 +
                        "/" +
                        examQuestionSize
                      : "0/0"
                  }}
                </div>
                <div class="exam-page-bottom-text" @click="showNextQuestion">
                  {{ getShowWord("question_next") }}
                </div> -->


              </div>

              <!-- <div class="exam-menu" @click="showMenu">
                {{ getShowWord("menu") }}
              </div> -->
              <!-- <div
                class="exam-menu"
                style="width: 6vw; background-color: #00d523"
                @click="showMessage"
              >
                <div class="redTip" v-if="showRedTip"></div>
                {{ getShowWord("question") }}
              </div>
              <div class="exam-menu" @click="showQADrawer = true">FAQ</div> -->
            </div>
          </div>
        </div>

        <div class="exam-empty-height-view" />

      </div>
      <div class="exam-empty-width-view" />


      <div class="exam-right-rail">

        <div class="exam-empty-height-view" />

        <div style="margin:1px;background-color: #f2f2f2;">


          <div @click="showExchangeTip" class="changeCountStyle">
            {{ getShowWord("exchange_screen_counts") }}: {{ screenChangeCount }}
          </div>


          <div class="rightMiddleDiv">

            <div class="exam-count-down-lable">Remaining time:</div>
            <div class="exam-count-down">
              {{ countDownTimes   }}
            </div>

            <template v-if="individualMode">
              <div style="width: 100%;height: 2px;margin-top: 10px;background-color: white;"></div>
              <div class="exam-count-down-lable">24-hour window:</div>
              <div class="exam-count-down">{{ windowCountDownTimes }}</div>
            </template>

            <template v-if="keep_score_type === 'real_time'">
              <div style="width: 100%;height: 2px;margin-top: 10px;background-color: white;"></div>
              <div class="exam-count-down-lable">Total Score:</div>
              <div class="exam-progess-down" style="color: red;margin-bottom: 10px;">
                {{ student_score   }}
              </div>
            </template>

            <div style="width: 100%;height: 2px;margin-top: 10px;background-color: white;"></div>

            <div v-if="relayMode" class="exam-message exam-team-chat" @click="openTeamChat">
              Team Chat
            </div>

            <div
              class="exam-message"
              @click="showMessage"
            >
              <div class="redTip" v-if="showRedTip"></div>
              Message
            </div>
            <div class="exam-message" @click="openFaq">FAQ</div>
            <div class="exam-message" @click="showCalculator = true">Calculator</div>

            <div v-if="!individualMode" class="exam-leave" @click="handleLeaveExam">
              Leave Exam
            </div>

            <div class="exam-submit" @click="doSubmitAnswer">
              {{ individualMode ? 'Submit Stage' : 'Submit Entire Exam' }}
            </div>

            <div>
              <img
                src="../../assets/jiangbei.png"
                style="width: 100%; height: auto"
              />
            </div>

          </div>



          <!-- <div class="bottomDiv">
            

            
          </div> -->



        </div>

      </div>



    </div>
    <ScientificCalculator :visible.sync="showCalculator" />

    <!-- 老的提交弹窗 -->
    <!-- <el-dialog
      :visible.sync="showSubmitAnswerDialog"
      :modal="true"
      :show-close="false"
      :modal-append-to-body="true"
      custom-class="showSubmitAnswerDialogStyle"
    >
      <div class="submitAnswer-root">
        <div style="position: absolute; right: 1vw; top: 1vw">
          <img
            src="../../assets/close_1.png"
            style="cursor: pointer; width: 43px; height: auto"
            @click="showSubmitAnswerDialog = false"
          />
        </div>
        <div
          style="
            width: 100%;
            justify-content: center;
            display: flex;
            flex-direction: column;
            position: absolute;
            bottom: 5vh;
          "
        >
          <div style="color: white; font-size: 20px; font-weight: 400;word-break: normal;">
            {{ getShowWord("want_to_submit_answer") }}
          </div>
          <div
            style="
              margin-top: 6vh;
              display: flex;
              justify-content: center;
              flex-direction: row;
              height: 5vh;
              line-height: 5vh;
            "
          >
            <div
              @click="showSubmitAnswerDialog = false"
              style="
                font-weight: 600;
                font-size: 1.67vw;
                cursor: pointer;
                color: #0e409f;
                width: 9vw;
                background-color: #b1ccff;
                border-radius: 31px;
                text-align: center;
              "
            >
              {{ getShowWord("btn_cancel") }}
            </div>
            <div
              @click="uploadAnswers('manual_submit_old_dialog')"
              style="
                font-weight: 600;
                font-size: 1.67vw;
                cursor: pointer;
                margin-left: 40px;
                color: #0e409f;
                width: 9vw;
                background-color: white;
                border-radius: 31px;
                text-align: center;
              "
            >
              {{ getShowWord("btn_confirm") }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog> -->


    <el-dialog
      title="Submit Entire Exam"
      :visible.sync="showSubmitAnswerDialog"
      width="500px"
      @open="onSubmitDialogOpen"
      >
      <div class="submit-dialog-content">
        <p style="word-break: normal; margin-bottom: 20px;">{{ getShowWord("want_to_submit_answer") }}</p>

        <!-- Team Members Submission Status (only show in relay mode) -->
        <div v-if="relayMode" class="team-members-status">
          <div class="status-header">Team Members Submission Status</div>
          <div v-if="loadingTeamMembers" class="loading-members">
            Loading team members...
          </div>
          <div v-else-if="teamMembers.length === 0" class="no-members">
            No team members found.
          </div>
          <div v-else class="members-list">
            <div
              v-for="member in teamMembers"
              :key="member.student_id"
              class="member-status-item"
            >
              <div class="member-name">{{ member.student_name }}</div>
              <div class="member-submission-status">
                <span v-if="member.has_submitted" class="status-submitted">
                  Submitted
                </span>
                <span v-else class="status-not-submitted">
                  Not Submitted
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelSubmitDialog('entire_exam')">Cancel</el-button>
        <el-button type="primary" @click="confirmExamUpload('manual_submit_dialog')">Confirm Submission</el-button>
      </span>
    </el-dialog>

    <!-- Leave Exam Confirm Dialog -->
    <el-dialog
      title="Leave Exam"
      :visible.sync="showLeaveExamDialog"
      width="480px"
      :close-on-click-modal="false"
    >
      <p style="word-break: normal; overflow-wrap: break-word; font-size: 15px; line-height: 1.6;">
        {{ leaveExamMessage }}
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelLeaveExam">Cancel</el-button>
        <el-button type="primary" @click="confirmLeaveExam">Confirm</el-button>
      </span>
    </el-dialog>

    <!-- Relay Mode: Kicked Out Dialog -->
    <el-dialog
      title="Notice"
      :visible.sync="showRelayKickedDialog"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <p><strong>{{ relayKickedByName }}</strong> has taken over the exam.</p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleRelayKickedConfirm">OK</el-button>
      </span>
    </el-dialog>

    <el-drawer
      :modal="true"
      :show-close="false"
      :modal-append-to-body="true"
      :visible.sync="showDrawer"
      direction="rtl"
      :withHeader="false"
      size="60%"
      custom-class="showMenuDialogStyle"
    >
      <div class="menu-content">
        <div style="margin-top: 100px">
          <img
            src="../../assets/drawer-left.png"
            style="cursor: pointer; width: 28px; height: auto"
            @click="showDrawer = false"
          />
        </div>
        <div class="drawer-question-list" style="margin-left: -2px">
          <div style="padding-top: 30px; padding-bottom: 15px">
            <p
              style="
                letter-spacing: 3px;
                color: white;
                font-size: 20px;
                font-weight: bold;
                font-family: Montserrat-SemiBold;
              "
            >
              {{ drawerAnswerInfo }}
            </p>
          </div>
          <div style="width: 100%; height: 100%; overflow-y: scroll">
            <el-tabs v-model="activeName" stretch="true">
              <el-tab-pane :label="getShowWord('all_questions')" name="first">
                <div>
                  <div
                    v-for="(item, index) in drawerQuestionList"
                    :key="index"
                    class="drawer-question-item-root"
                    @click="doSelectQuestion(index)"
                  >
                    <div
                      :class="{
                        'drawer-question-item': true,
                        selected: item.choice !== '',
                      }"
                    >
                      <div style="flex: 1; display: flex; flex-direction: row">
                        <div
                          v-html="formatContent(item.question_question)"
                          style="
                            margin-left: 10px;
                            text-align: left;
                            font-size: 14px;
                          "
                        ></div>
                      </div>
                      <div class="drawer-answered" v-if="item.choice !== ''">
                        {{ getShowWord("answered") }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
              <el-tab-pane :label="getShowWord('not_answer')" name="second">
                <div>
                  <div
                    v-for="(item, index) in drawerNoChoiceQuestionList"
                    :key="index"
                    class="drawer-question-item-root"
                    @click="doSelectQuestion(item['parentIndex'])"
                  >
                    <div :class="{ 'drawer-question-item': true }">
                      <div style="flex: 1; display: flex; flex-direction: row">
                        <div
                          v-html="formatContent(item.question_question)"
                          style="margin-left: 10px; text-align: left"
                        ></div>
                      </div>
                      <!--                      <div class="drawer-answered" v-if="item.choice !==''">-->
                      <!--                        已做答-->
                      <!--                      </div>-->
                    </div>
                  </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </div>
      </div>
    </el-drawer>
    <el-dialog :visible.sync="showBigPicDialog" width="100%">
      <img :src="currentSelectedPicPath" />
    </el-dialog>
    <el-dialog
      :visible.sync="showAnswerResultDialog"
      width="100%"
      :modal="true"
      :show-close="false"
      :modal-append-to-body="true"
      custom-class="showAnswerResultDialogStyle"
    >
      <div class="answer-result-root">
        <div class="awardTitle">Congratulations</div>
        <div class="scoreLab2">{{ "/   " + answerResultDta.full_score }}</div>
        <div class="scoreLab1">{{ answerResultDta.total_score }}</div>
        <div class="awardTitle1">{{ answerResultDta.application_event }}</div>
        <div class="awardLine"></div>
        <div class="awardSubTitle"></div>
        <div class="box">
          <div
            v-for="(item, index) in answerResultDta.achievements"
            :key="index"
            class="child"
          >
            <img
              class="infoImg"
              :src="require('../../assets/selectInfoImage.png')"
            />
            <div class="infoStyle">
              {{ "&nbsp;&nbsp;" + item + "&nbsp;&nbsp;" }}
            </div>
          </div>
        </div>
        <div @click="closeView" class="returnStyle">Return Home</div>
      </div>
    </el-dialog>

    <el-drawer
      title="Messages"
      :visible.sync="messageDrawer"
      direction="ltr"
      size="500px"
      :before-close="handleClose"
    >
      <!-- <span>消息列表</span> -->

      <div class="messageList">
        <div class="inputStyle">
          <el-select type="text" class="inputSelect" v-model="messageType">
            <el-option
              v-for="typeOption in supportMessageTypes"
              :key="typeOption"
              :value="typeOption"
              :label="typeOption"
            ></el-option>
          </el-select>

          <el-input v-model="messageStr" class="inputStyle1"></el-input>
          <el-button class="sendStyle" @click="sendMessage">Send</el-button>
        </div>

        <div style="margin-top: 20px; height: 20px"></div>

        <div
          v-for="(x, index) in messageList"
          :key="index"
          class="messageDetail"
        >
          <div style="color: #001d9f; font-size: 15px">
            {{ x.type }}
          </div>
          <div style="color: #00359f; font-size: 13px; margin-top: 10px">
            {{ x.message_content }}
          </div>

          <div
            v-if="x.reply_content != ''"
            style="color: #00359f; font-size: 13px; margin-top: 10px"
          >
            {{ "Reply：" + x.reply_content }}
          </div>

          <div
            style="background-color: #00359f; height: 1px; margin-top: 10px"
          ></div>
        </div>
      </div>
    </el-drawer>
    <el-dialog :visible.sync="showGotoPageDialog" title="Tips" width="30%">
      <div>
        {{ getShowWord("gotoPage") }}
        <el-input-number
          v-model="gotoImageIndex"
          :min="1"
          :max="examImageSize"
          @change="handleGotoImagChanged"
        ></el-input-number>
        / {{ examImageSize }}
      </div>
      <div style="margin-top: 25px">
        <el-button size="small" type="primary" @click="gotoSelectedImage">
          {{ getShowWord("btn_confirm") }}</el-button
        >
        <el-button
          size="small"
          style="margin-left: 20px"
          @click="showGotoPageDialog = false"
        >
          {{ getShowWord("btn_cancel") }}</el-button
        >
      </div>
    </el-dialog>
    <el-drawer
      title="FAQ"
      :visible.sync="showQADrawer"
      direction="rtl"
      size="40%"
    >
      <div style="text-align: left; margin: 15px">
        <QA />
      </div>
    </el-drawer>

    <!-- Team Chat Drawer for Relay Mode -->
    <el-drawer
      v-if="relayMode"
      title="Team Chat"
      :visible.sync="showTeamChatDrawer"
      direction="ltr"
      size="500px"
      :before-close="handleTeamChatClose"
    >
      <div class="team-chat-container">
        <div class="team-chat-warning">
          <i class="el-icon-warning" style="color: #E6A23C; margin-right: 5px;"></i>
          <span>Note: Team chat is only for team strategy discussions—question-related talk is strictly forbidden. All chat content is audited; violations will result in team penalties.</span>
        </div>

        <div class="team-chat-members">
          <div class="team-chat-members-title">Team Members Online Status</div>
          <div class="team-chat-members-list">
            <div v-for="member in displayMembers" :key="member.student_id" class="team-member-item">
              <span class="exam-member-dot" :class="{ 'dot-online': member.is_online && !member.is_in_exam, 'dot-in-exam': member.is_in_exam }"></span>
              <span class="exam-member-name">{{ member.student_name }}</span>
              <span class="exam-member-status-text" v-if="member.is_in_exam" style="color: #ff9800;">In Exam</span>
              <span class="exam-member-status-text" v-else-if="member.is_online" style="color: #4caf50;">Online</span>
              <span class="exam-member-status-text" v-else style="color: #9e9e9e;">Offline</span>
            </div>
          </div>
        </div>


        <div class="chat-section">
          <div class="section-header">Chat Messages</div>
          <!-- <div class="chat-warning">
            Note: Team chat is only for team strategy discussions—question-related talk is strictly forbidden.
          </div> -->
          <div class="chat-messages" ref="teamChatMessages">
            <div
              v-for="(message, index) in teamChatMessages"
              :key="index"
              class="chat-message"
              :class="{ 'my-message': message.sender_id === user_id }"
            >
              <div class="message-sender">{{ message.sender_name }}</div>
              <div class="message-content">{{ message.content }}</div>
              <div class="message-time">{{ formatChatTime(message.send_time) }}</div>
            </div>
            <div v-if="teamChatMessages.length === 0" class="no-messages">
              No messages yet. Start the conversation!
            </div>
          </div>
        </div>

        <div class="send-message-section">
          <el-input
            type="textarea"
            :rows="2"
            placeholder="Type your message here..."
            v-model="newTeamMessage"
            maxlength="500"
            show-word-limit
          ></el-input>
          <el-button
            type="primary"
            @click="sendTeamMessage"
            :loading="sendingTeamMessage"
            style="margin-top: 10px; width: 100%"
          >
            Send
          </el-button>
        </div>
      </div>
    </el-drawer>


        <!-- <div class="team-chat-members">
          <div class="team-chat-members-title">Team Members Online Status</div>
          <div class="team-chat-members-list">
            <div v-for="(member, idx) in teamMembers" :key="idx" class="team-member-item">
              <span class="member-status" :class="{ 'online': teamMembersOnline[member.id] }"></span>
              <span>{{ member.name }}</span>
            </div>
          </div>
        </div>

        <div class="team-chat-messages" ref="teamChatMessagesRef">
          <div v-for="(msg, idx) in teamChatMessages" :key="idx"
               :class="['chat-message', msg.sender_id === user_id ? 'chat-message-self' : 'chat-message-other']">
            <div class="chat-message-header">
              <span class="chat-message-name">{{ msg.sender_name }}</span>
              <span class="chat-message-time">{{ formatChatTime(msg.time) }}</span>
            </div>
            <div class="chat-message-content">{{ msg.content }}</div>
          </div>
        </div>

        <div class="team-chat-input">
          <el-input
            v-model="teamChatInput"
            placeholder="Type your message..."
            @keyup.enter.native="sendTeamChatMessage"
          ></el-input>
          <el-button type="primary" @click="sendTeamChatMessage">Send</el-button>
        </div>
      </div>
    </el-drawer> -->

    <!-- Team Submit Dialog -->
    <el-dialog
      :title="individualMode ? ('Submit Stage ' + (currentSectionIndex + 1)) : 'Submit Entire Exam'"
      :visible.sync="showTeamSubmitDialog"
      :width="individualMode ? '620px' : '40%'"
      :custom-class="individualMode ? 'stage-submit-dialog' : ''"
      @open="onSubmitDialogOpen"
    >
      <div class="team-submit-content">
        <p v-if="relayMode" class="team-submit-notice">Once 3 team members confirm this action, the system will submit your team's exam paper and conclude the exam.</p>
        <p v-if="individualMode" class="team-submit-notice">
          Once submitted, this stage is final and cannot be reopened.
          <template v-if="hasNextIndividualSection">
            Would you like to start Stage {{ currentSectionIndex + 2 }} now? Its 2-hour timer starts immediately if you continue.
          </template>
          <template v-else>
            This is your final stage. Submitting it will complete your exam.
          </template>
        </p>

        <div v-if="individualMode" class="stage-submit-confirmation">
          <label class="stage-submit-label" for="stage-submit-email">
            Enter the email address used to sign in.
          </label>
          <el-input
            id="stage-submit-email"
            ref="stageSubmitEmailInput"
            v-model="stageSubmitConfirmationEmail"
            type="email"
            placeholder="Login email"
            autocomplete="off"
            autocapitalize="none"
            spellcheck="false"
            :disabled="submittingIndividualSection"
            @blur="stageSubmitEmailTouched = true"
          ></el-input>
          <div
            class="stage-submit-validation"
            :class="{ 'is-visible': stageSubmitEmailError }"
            role="status"
            aria-live="polite"
          >
            {{ stageSubmitEmailError || "\u00a0" }}
          </div>
        </div>

        <!-- <div class="team-submit-members">
          <div class="team-submit-title">Team Members Submission Status:</div>
          <div v-for="(member, idx) in teamMembers" :key="idx" class="team-submit-member-item">
            <span class="member-name">{{ member.name }}</span>
            <span class="member-submit-status" :class="{ 'submitted': teamSubmissionStatus[member.id] }">
              {{ teamSubmissionStatus[member.id] ? 'Confirmed' : 'Not Confirmed' }}
            </span>
          </div>
        </div> -->


        <div v-if="relayMode" class="team-members-status">
          <div class="status-header">Team Members Submission Status</div>
          <div v-if="loadingTeamMembers" class="loading-members">
            Loading team members...
          </div>
          <div v-else-if="teamMembers.length === 0" class="no-members">
            No team members found.
          </div>
          <div v-else class="members-list">
            <div
              v-for="member in teamMembers"
              :key="member.student_id"
              class="member-status-item"
            >
              <div class="member-name">{{ member.student_name }}</div>
              <div class="member-submission-status">
                <span v-if="member.has_submitted" class="status-submitted">
                  Submitted
                </span>
                <span v-else class="status-not-submitted">
                  Not Submitted
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="relayMode" class="team-submit-count">
          <span>Confirmed: {{ teamSubmissionCount }} / 3</span>
        </div>
      </div>
      <span
        slot="footer"
        class="dialog-footer"
        :class="{ 'stage-submit-actions': individualMode }"
      >
        <el-button
          :class="{ 'stage-submit-cancel': individualMode }"
          @click="cancelSubmitDialog(individualMode ? 'stage' : 'team_exam')"
        >
          Cancel
        </el-button>
        <!-- <el-button @click="backWaitingRoom">Back Waiting Room</el-button> -->
        <el-button v-if="relayMode" type="primary" @click="confirmTeamSubmit" :disabled="teamSubmissionStatus[user_id]">
          {{ teamSubmissionStatus[user_id] ? 'Already Confirmed' : 'Confirm Submission' }}
        </el-button>
        <el-button
          v-if="individualMode && hasNextIndividualSection"
          type="primary"
          class="stage-submit-exit"
          :disabled="!isStageSubmitEmailMatch || submittingIndividualSection"
          @click="confirmIndividualSectionSubmit(false)"
        >
          Submit &amp; Exit
        </el-button>
        <el-button
          v-if="individualMode"
          type="primary"
          :class="hasNextIndividualSection ? 'stage-submit-start-next' : 'stage-submit-exit'"
          :disabled="!isStageSubmitEmailMatch || submittingIndividualSection"
          :loading="submittingIndividualSection"
          @click="confirmIndividualSectionSubmit(true)"
        >
          {{ hasNextIndividualSection ? 'Submit & Start Next Stage' : 'Submit Final Stage' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- Debug: Network Error Test Button -->
    <div v-if="debugNetwork" style="position: fixed; top: 10px; left: 10px; z-index: 9999;">
      <el-button type="danger" size="mini" @click="showNetworkErrorModal = true">
        Simulate Network Error
      </el-button>
    </div>

    <div
      v-if="requiresProctoringConnection"
      class="proctoring-required-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="proctoring-required-title"
      aria-describedby="proctoring-required-description"
    >
      <div class="proctoring-required-modal">
        <div class="proctoring-required-icon" aria-hidden="true">
          <i class="el-icon-video-camera"></i>
        </div>
        <div id="proctoring-required-title" class="proctoring-required-title">
          Connect proctoring to continue
        </div>
        <div
          id="proctoring-required-description"
          class="proctoring-required-desc"
        >
          <template v-if="screenOnlyProctoring">
            Screen proctoring is not connected. Click the button below and
            choose <strong>Entire Screen</strong> to continue the exam.
          </template>
          <template v-else>
            Proctoring is not connected. Click the button below, allow camera
            access, and choose <strong>Entire Screen</strong> to continue the exam.
          </template>
        </div>
        <div class="proctoring-required-clock">
          Exam time remaining: <strong>{{ countDownTimes }}</strong>
        </div>
        <div
          v-if="proctoringReconnectError"
          class="proctoring-required-error"
          role="alert"
        >
          {{ proctoringReconnectError }}
        </div>
        <el-button
          type="primary"
          :loading="proctoringReconnectLoading"
          @click="reconnectProctoring"
        >
          {{ proctoringReconnectLoading ? "Connecting..." : "Connect Proctoring" }}
        </el-button>
        <div class="proctoring-required-note">
          The exam clock continues while proctoring is disconnected.
        </div>
      </div>
    </div>

    <!-- Network Error Blocking Modal -->
    <div class="network-error-overlay" v-if="showNetworkErrorModal">
      <div class="network-error-modal">
        <div class="network-error-icon">
          <i class="el-icon-warning" style="font-size: 48px; color: #E6A23C;"></i>
        </div>
        <div class="network-error-title">Network Connection Lost</div>
        <div class="network-error-desc">
          Your network connection has been interrupted. Your answers are saved locally but cannot be uploaded to the server.<br/>
          Please check your network connection and click the button below to verify.<br/><br/>
          Once your network is restored and the verification passes, this window will close and you can continue your exam.
        </div>
        <el-button
          type="primary"
          :loading="networkChecking"
          @click="doNetworkHealthCheck"
          style="margin-top: 20px; width: 200px;"
        >
          {{ networkChecking ? 'Checking...' : 'Check Network' }}
        </el-button>
        <report-problem-button
          button-text="Report a problem"
          class="network-error-report"
          :context="networkReportContext"
          :template-name="test_subject || template_id"
        />
      </div>
    </div>

    <div
      class="exam-integrity-overlay"
      v-if="showIntegrityWarning"
      data-exam-integrity-ignore
    >
      <div class="exam-integrity-modal">
        <div class="exam-integrity-icon">
          <i class="el-icon-warning" style="font-size: 48px; color: #E6A23C;"></i>
        </div>
        <div class="exam-integrity-title">Exam content changed</div>
        <div class="exam-integrity-desc">
          The exam page must match the original exam data. Please turn off browser translation and restore the page before continuing.
        </div>
        <div class="exam-integrity-detail" v-if="integrityWarningDetail">
          {{ integrityWarningDetail }}
        </div>
        <el-button
          type="primary"
          :loading="integrityRechecking"
          @click="verifyIntegrityRecovery"
          style="margin-top: 20px; width: 200px;"
        >
          Recheck
        </el-button>
      </div>
    </div>


  </div>
</template>

<script>
import {
  getExerciseTemplateByStudent,
  getExerciseTemplateByTeam,
  getMyMessages,
  getTestVisibleChangeCount,
  sendMessage,
  submitAnswer,
  checkIsUploadTest,
  uploadTestHeartBeat,
  studentSignWithTemplate,
  uploadAnswersV3,
  healthCheck,
  getGroupId,
  getQiniuToken,
  studentUpdateTemplateWithIndex,
  getTeamMembersWithId,
  getTeamSubmissionStatus,
  confirmTeamSubmission,
  updateSectionProgress,
  getSectionProgress,
  completeIndividualExamSection,
  checkAMIndividualExamSubmitStatus,
  leaveRelayExam,
  getRelayTeamMembers,
  getRelayTeamChat,
  sendRelayTeamMessage,
  checkRelayEntryCount,
  confirmRelayEntry,
  getRelayExamStatus,
  saveExamNavigationLog,
  saveExamInteractionLog,
  saveProctorEvent,
  getProctorPenaltySummary,
  getExamProctoringPolicy,
  getUser
} from "../../api/index";
import "../../assets/common/font.css";
import {
  getAppId,
  resolveAndSetExamProjectCode,
  getUserId,
  getUserIP,
  getTestEvent,
  getProjectName,
  getProjectEname,
  getAccountName,
  getUserName,
  getUserEmail,
  setUserEmail
} from "../../utils/store";
import Invigilate from "./Invigilate";
import QA from "./QA";
import scroll from "../../utils/scroll";
import TextChat from "../CommonChat/txt.vue";
import PDF from "../pdf/PDF.vue";
import { Loading } from "element-ui";
import * as qiniu from "qiniu-js";
import kats from "../../assets/common/kats.vue"
import CandidateWatermark from "./CandidateWatermark.vue";
import ReportProblemButton from "../Support/ReportProblemButton.vue";
import ScientificCalculator from "./ScientificCalculator.vue";
import {
  compareVisibleTextWithExpected,
  htmlToPlainText
} from "../../utils/exam-integrity";
import {
  protectCurrencyDollarsInHtml,
  repairMathEmphasisMarkupInHtml
} from "../../utils/math-content";
import { SUPPORT_MESSAGE_TYPES } from "../../utils/support-message-types";



export default {
  name: "StudentExam",
  components: {
    Invigilate,
    QA,
    TextChat,
    PDF,
    kats,
    CandidateWatermark,
    ScientificCalculator,
    ReportProblemButton
  },
  watch: {
    showNetworkErrorModal(val) {
      if (val) {
        console.trace('showNetworkErrorModal was set to true');
      }
    },
    showCalculator(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (newValue) {
        this.queueExamInteraction("support", "calculator_opened");
      } else if (oldValue) {
        this.queueExamInteraction("support", "calculator_closed");
      }
    },
    status(newStatus, oldStatus) {
      if (this.invigilateStatus == "true") {
        this.canExam = true;
        this.fetchData(1);
      } else {
        if (!newStatus) {
          //禁止做题目了
          this.canExam = false;
          this.proctoringReconnectLoading = false;
          console.log("---sorry--");
          this.clearCountDown();
          //停止一切
        } else {
          console.log("---yes --");
          this.canExam = true;
          this.proctoringAutoAttemptPending = false;
          this.proctoringAutoAttemptFinished = true;
          this.proctoringReconnectLoading = false;
          this.proctoringReconnectError = "";
          this.fetchData(1);
        }
      }
    },
    showTeamChatDrawer(newVal) {
      if (newVal) {
        // When drawer opens, fetch data and start refresh
        this.fetchTeamMembers();
        this.fetchTeamChatMessages();
        this.startTeamChatRefresh();
      } else {
        // When drawer closes, stop refresh
        this.stopTeamChatRefresh();
      }
    },
  },
  data() {
    return {
      // imageUploader: {
      //               custom: false,
      //               fileType: 'file',
      //               fileNameType: '',
      //               imagePrefix: 'http://106.54.92.121', // 图片前缀地址
      //               type: 'server',
      //               url: 'http://106.54.92.121:82/upload'
      //           },
      testAss:"$\\frac{1}{2}$ good udea",
      questionAnswers: {},
      showCalculator: false,
      showQADrawer: false,
      gotoImageIndex: 1,
      showGotoPageDialog: false,
      activeName: "first",
      examDataLoading: true,
      canExam: true,
      answerResultDta: {},
      showAnswerResultDialog: false,
      showDrawer: false,
      messageDrawer: false,
      showSubmitAnswerDialog: false,
      stageSubmitConfirmationEmail: "",
      stageSubmitExpectedEmail: "",
      stageSubmitEmailTouched: false,
      stageSubmitEmailLoading: false,
      user_id: "",
      template_id: "",
      test_subject: "",
      countDownThread: undefined,
      sections: [],
      countDownTimes: "00:00:00",
      windowCountDownTimes: "24:00:00",
      test_event: getTestEvent(),
      test_project_name: getProjectEname() || getProjectName() || '',
      student_name: getUserName() + ' | ' + (getAccountName() && getAccountName() !== 'undefined' ? getAccountName() : ''),
      candidateName: getUserName() || "",
      candidateEmail: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(getUserEmail() || "")
        ? getUserEmail()
        : "",
      test_type:'',
      remainStamp: 0,
      exam_start_time: 0,
      status: false,
      exam_images: [],
      currentSelectedImageIndex: 1,
      examImageSize: 0,
      signStatus: false,
      currentSelectedQuestionIndex: 0,
      questionList: [],
      drawerQuestionList: [],
      drawerChoiceQuestionList: [],
      drawerNoChoiceQuestionList: [],
      examQuestionSize: 0,
      currentSelectedQuestion: {
        question_id: "",
        question_index: "",
        question_question: "",
        question_optionA: "",
        question_optionB: "",
        question_optionC: "",
        question_optionD: "",
        question_optionE: "",
        choice: "",
        inputAnswer:"",
        question_type:"",
        answer_img_url:""
      },
      showBigPicDialog: false,
      currentSelectedPicPath: "",
      app_id: "",
      deviceStatus: 0,
      DEBUG_DEV: false,
      drawerAnswerInfo: "",
      progess_num:0,
      progess_num_total:0,
      project_code: resolveAndSetExamProjectCode(
        this.$route && this.$route.query ? this.$route.query.project_code : ""
      ),
      student_score:0,
      ip: "",
      messageList: [],
      messageType: "Message Types",
      supportMessageTypes: SUPPORT_MESSAGE_TYPES,
      messageStr: "",
      timer: "",
      showRedTip: false,
      screenChangeCount: 0,
      heartBeatTimer: undefined,
      currentLang: "英文",
      WORDS: {
        中文: {
          exchange_lang: "English",
          page_pre: "上一页",
          page_next: "下一页",
          question_pre: "上一题",
          question_next: "下一题",
          menu: "目录",
          question: "咨询",
          submit_exam: "提交试卷",
          count_down: "倒计时",
          exchange_screen_counts: "切屏次数",
          btn_cancel: "取消",
          btn_confirm: "确定",
          want_to_submit_answer: "是否提交试卷",
          all_questions: "所有",
          not_answer: "未答",
          answered: "已答",
          gotoPage: "跳转到",
        },
        英文: {
          exchange_lang: "中文",
          page_pre: "Previous",
          page_next: "Next",
          question_pre: "Previous Question",
          question_next: "Next Question",
          menu: "Menu",
          question: "Messages",
          submit_exam: "Submit Answers",
          count_down: "Count Down",
          exchange_screen_counts: "Screen Switches",
          btn_cancel: "Cancel",
          btn_confirm: "Confirm",
          want_to_submit_answer: "Are you sure you want to submit your exam? Once you click 'Confirm', your answers will be final, and you will not be able to re-enter the exam.",
          all_questions: "All Questions",
          not_answer: "Not Answers",
          answered: "Answered",
          gotoPage: "Go To",
        },
      },
      showAnswerResultClick: false,
      forceShutDown: false,
      isConnect: false,
      showloadingView: false,
      proctoringAutoAttemptPending: false,
      proctoringAutoAttemptFinished: false,
      proctoringReconnectLoading: false,
      proctoringReconnectError: "",
      countDownInternal: undefined,
      timeOffset: 0,
      serverClockAnchor: 0,
      serverMonotonicAnchor: 0,
      waitingCountDownDeadline: 0,
      waitingCountDownComplete: null,
      examCountDownDeadline: 0,
      countDownTimeoutHandling: false,
      countDownRetryTimer: null,
      windowCountDownThread: undefined,
      hourLab: "00",
      minuteLab: "00",
      secondLab: "00",
      invigilateStatus: false,
      screenOnlyProctoring: false,
      group_id: "",
      browerStr:'',
      havePicUrl:true,
      parentShowBigPic:0,
      parentReloadPic:0,
      submitAnswrQuestionId:'',
      submitAnswrQuestionChoice:'',
      submitAnswrQuestionImg_url:'',
      isConfirmingAnswer:false,
      initial_score:0,
      keep_score_type:'post_exam',
      answer_deduction_points:'YES',
      // Leave Exam
      showLeaveExamDialog: false,
      leaveExamMessage: '',
      currentRelayEntryCount: 0,
      // Section-based navigation
      currentSectionIndex: 0,
      sectionProgress: {}, // { sectionIndex: { currentIndex: 0, totalQuestions: 0 } }
      currentSectionCompleted: false, // Whether current section's questions are all answered
      // Team Chat
      showTeamChatDrawer: false,
      teamChatMessages: [],
      teamChatInput: '',
      teamMembers: [],
      onlineMembers: [],
      // Team Submission
      showTeamSubmitDialog: false,
      teamSubmissionStatus: {}, // { memberId: boolean }
      teamSubmissionCount: 0,
      // WebSocket reference
      ws: null,
      // Relay mode fields
      relayMode: false,
      individualMode: false,
      individualExam: null,
      individualServerOffset: 0,
      submittingIndividualSection: false,
      individualTimeUpHandled: false,
      relayWs: null,
      showRelayKickedDialog: false,
      relayKickedByName: '',
      // Team members for submit dialog
      loadingTeamMembers: false,
      newTeamMessage: '',
      sendingTeamMessage: false,
      teamChatRefreshTimer: null,
      relayStatusCheckTimer: null,
      showRelayExamSubmittedDialog: false,
      // Network error blocking modal
      showNetworkErrorModal: false,
      lastNetworkError: "",
      networkChecking: false,
      debugNetwork: false,
      examIntegrityEnabled: true,
      examIntegrityObserver: null,
      examIntegrityTimer: null,
      examIntegrityCheckTimeout: null,
      showIntegrityWarning: false,
      integrityWarningDetail: "",
      integrityRechecking: false,
      integrityIncidentCount: 0,
      examIntegrityPendingRiskKey: "",
      examIntegrityPendingRiskAt: 0,
      examIntegrityMathJobs: 0,
      penaltySummary: {
        talking_duration_ms: 0,
        talking_points: 0,
        camera_leave_count: 0,
        camera_leave_points: 0,
        network_disconnect_count: 0,
        network_disconnect_points: 0,
        total_deduction: 0,
        score_valid: true,
        score_validity: "valid",
        invalid_threshold: 20,
      },
      networkDisconnectStartedAt: 0,
      networkDisconnectEventId: "",
      networkDisconnectSequence: 0,
      networkDisconnectQueue: [],
      networkDisconnectSending: false,
      interactionLogQueue: [],
      interactionLogSending: false,
      interactionLogSequence: 0,
      navigationLogQueue: [],
      navigationLogSending: false,
      navigationLogSequence: 0,
      materialScrollTimer: null,
      materialScrollStartTop: null,
      materialScrollStartPercent: 0,
      materialScrollLastTop: 0,
      materialScrollLastPercent: 0,
      materialScrollSuppressUntil: 0,
      materialScrollFrame: null,
      materialScrollPendingTarget: null,
    };
  },
  created() {
    window.addEventListener("offline", this.networkErrorCallback);
    window.addEventListener("online", this.networkSuccessCallback);
    window.addEventListener("focus", this.syncCountDownClock);
    window.addEventListener("pageshow", this.syncCountDownClock);
    document.addEventListener("visibilitychange", this.syncCountDownClock);
    document.addEventListener("resume", this.syncCountDownClock);
  },
  async mounted() {

    this.applyNoTranslateAttributes();
    this.startExamIntegrityGuard();


    // const script = document.createElement('script');
    // script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.5/MathJax.js?config=Tex-AMS-MML_HTMLorMML';
    // script.onload = () => {
    //   // 脚本加载完毕后的操作
    // };
    // document.head.appendChild(script);
  
    this.project_code = resolveAndSetExamProjectCode(this.$route.query.project_code);
    this.app_id = getAppId();
    let groupIdRes = await getGroupId(this.app_id);
    this.group_id = groupIdRes.data;
    this.template_id = this.$route.query.template_id;
    this.invigilateStatus = this.$route.query.invigilateStatus;
    this.debugNetwork = this.$route.query.debug_network === 'true';
    const routeMode = String(this.$route.query.exam_mode || '').toLowerCase();
    this.relayMode = routeMode === 'relay' || this.$route.query.relay_mode === true || this.$route.query.relay_mode === 'true';
    this.individualMode = routeMode === 'individual';
    // Older relay links did not include exam_mode, so preserve their behavior.
    if (!this.relayMode && !this.individualMode) {
      this.relayMode = true;
    }

    this.user_id = getUserId();
    await this.applyProctoringPolicy();
    this.loadCandidateIdentity();
    this.restoreNetworkDisconnectState();
    this.refreshPenaltySummary();

    if (this.relayMode && !this.debugNetwork) {
      try {
        const entryRes = await checkRelayEntryCount(this.user_id, this.template_id, this.project_code);
        if (entryRes.data.code === 0) {
          const entryCount = entryRes.data.data.entry_count || 0;
          this.currentRelayEntryCount = entryCount;
          const relayInExam = entryRes.data.data.relay_in_exam;

          if (entryCount >= 2 && !relayInExam) {
            this.$alert('You have used up your 2 chances to join the relay.', 'Entry Limit Reached', {
              confirmButtonText: 'Confirm',
              showClose: false,
              callback: () => {
                this.$router.replace({
                  path: "/waiting-room",
                  query: { template_id: this.template_id, exam_name: this.test_event }
                });
              }
            });
            return;
          }

          if (!relayInExam) {
            this.$alert('Another team member has taken over the exam. You will be redirected to the waiting room.', 'Notice', {
              confirmButtonText: 'OK',
              showClose: false,
              callback: () => {
                this.$router.replace({
                  path: "/waiting-room",
                  query: { template_id: this.template_id, exam_name: this.test_event }
                });
              }
            });
            return;
          }
        }
      } catch (e) {
        console.error('Failed to validate relay entry:', e);
      }
    }

    if (this.relayMode) {
      this.connectRelayWebSocket();
      this._handleBeforeUnload = () => {
        this.notifyLeaveExam();
      };
      window.addEventListener('beforeunload', this._handleBeforeUnload);
    }

    this.browerStr = this.browerType()

    // MathJax.typeset([this.$el], {
    //   showProcessingMessages: false // 是否显示MathJax处理消息
    // });
    //先签到
    let result = await this.studentSignIn();
    if (result) {
      if (this.invigilateStatus == "true") {
        this.signStatus = false;
      } else {
        this.signStatus = true;
        if (!this.DEBUG_DEV) {
          this.canExam = false;
          this.$nextTick(() => {
            this.attemptInitialProctoring();
          });
        }
      }
    } else {
      return;
    }

    //打开socket
    this.isConnect = true;
    // Load team members
    if (this.relayMode) {
      this.loadTeamMembers();
    }
    if (this.DEBUG_DEV) {
      this.fetchData(1);
    }
    getUserIP((ip) => {
      console.log(2222, ip);
      this.ip = ip;
    });

    if (this.invigilateStatus == "true") {
      this.signStatus = false;
      this.fetchData(1);
    } else {
      this.refreshVisible();
    }

    this.getMyMessages();

    // this.timer = setInterval(this.showRedMessage, 5000);
    // if (window.history && window.history.pushState) {
    //   history.pushState(null, null, document.URL);
    //   window.addEventListener("popstate", this.onBrowserBack, false);
    // }
    this.checkStudentExam();

    if (this.relayMode && !this.debugNetwork) {
      this.startRelayStatusCheck();
    }

    setTimeout(() => {
      this.pushHistory();
      window.addEventListener("popstate", this.goBack, false);
      window.history.pushState("forward", null, ""); //在IE中必须得有这两行
      window.history.forward(1);
    }, 1000);
  },
  computed: {
    networkReportContext() {
      return {
        page: "exam",
        exam_name: this.test_event || "",
        template_name: this.test_subject || this.template_id || "",
        last_error: this.lastNetworkError || "network unavailable",
      };
    },
    currentModule() {
      return this.sections[this.currentSectionIndex] || {};
    },
    currentModuleInstruction() {
      return this.currentModule.section_instruction || '';
    },
    currentModuleTextMaterial() {
      const type = String(this.currentModule.section_material_type || '').toLowerCase();
      if (type === 'audio') return '';
      return this.currentModule.section_material || '';
    },
    currentModuleHtmlMaterials() {
      return Array.isArray(this.currentModule.html_materials)
        ? this.currentModule.html_materials.filter(Boolean)
        : [];
    },
    currentModuleAudioUrl() {
      const type = String(this.currentModule.section_material_type || '').toLowerCase();
      if (type !== 'audio') return '';
      const url = this.currentModule.clean_material || this.currentModule.section_material || '';
      if (!url || /^(https?:)?\/\//i.test(url)) return url;
      return `https://${url}`;
    },
    hasCurrentModuleMaterial() {
      return Boolean(
        this.currentModuleInstruction
        || this.currentModuleTextMaterial
        || this.currentModuleAudioUrl
        || this.currentModuleHtmlMaterials.length
      );
    },
    hasNextIndividualSection() {
      return this.individualMode && this.currentSectionIndex < this.sections.length - 1;
    },
    normalizedStageSubmitEmail() {
      return (this.stageSubmitConfirmationEmail || "").trim().toLowerCase();
    },
    normalizedStageExpectedEmail() {
      return (this.stageSubmitExpectedEmail || "").trim().toLowerCase();
    },
    isStageSubmitEmailMatch() {
      return Boolean(
        this.normalizedStageExpectedEmail &&
        this.normalizedStageSubmitEmail === this.normalizedStageExpectedEmail
      );
    },
    stageSubmitEmailError() {
      if (!this.stageSubmitEmailTouched) return "";
      if (!this.normalizedStageSubmitEmail) return "Enter your login email.";
      if (!this.isStageSubmitEmailMatch) {
        return "This email does not match the account used for this exam.";
      }
      return "";
    },
    displayMembers() {
      if (!Array.isArray(this.teamMembers)) return [];
      return this.teamMembers.map(m => {
        const wsEntry = this.onlineMembers.find(e => e.id === m.student_id);
        return {
          ...m,
          is_online: !!wsEntry,
          is_in_exam: wsEntry ? wsEntry.status === 'exam' : false
        };
      });
    },
    requiresProctoringConnection() {
      return Boolean(
        !this.DEBUG_DEV &&
        this.invigilateStatus !== "true" &&
        this.signStatus &&
        !this.status &&
        this.proctoringAutoAttemptFinished &&
        !this.proctoringAutoAttemptPending &&
        !this.showloadingView &&
        !this.forceShutDown
      );
    },
    penaltyTone() {
      const total = Number(this.penaltySummary.total_deduction) || 0;
      if (total >= Number(this.penaltySummary.invalid_threshold || 20)) return "danger";
      if (total >= 10) return "warning";
      return "safe";
    },
    talkingDurationLabel() {
      const seconds = Math.floor((Number(this.penaltySummary.talking_duration_ms) || 0) / 1000);
      const minutes = Math.floor(seconds / 60);
      const rest = seconds % 60;
      return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
    }
  },
  methods: {
    formatContent(content) {
      if (!content) return "";
      let formatted = repairMathEmphasisMarkupInHtml(
        String(content).replace(/&lt;br&gt;/g, "<br>")
      );
      formatted = formatted.replace(
        /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])\s*<br\s*\/?>/gi,
        "$1"
      );
      return protectCurrencyDollarsInHtml(formatted);
    },
    applyNoTranslateAttributes() {
      document.documentElement.setAttribute("lang", "en");
      document.documentElement.setAttribute("translate", "no");
      document.documentElement.classList.add("notranslate");
      if (document.body) {
        document.body.setAttribute("translate", "no");
        document.body.classList.add("notranslate");
      }
    },
    startExamIntegrityGuard() {
      if (!this.examIntegrityEnabled || this.examIntegrityObserver) return;
      this.$nextTick(() => {
        const root = this.$refs.examRoot;
        if (!root || this.examIntegrityObserver) return;
        if (typeof MutationObserver !== "undefined") {
          this.examIntegrityObserver = new MutationObserver(() => {
            this.scheduleExamIntegrityCheck();
          });
          this.examIntegrityObserver.observe(root, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: [
              "class",
              "style",
              "data-imt-p",
              "data-immersive-translate-walked",
              "data-immersive-translate-translation-element-mark"
            ]
          });
        }
        this.examIntegrityTimer = window.setInterval(() => {
          this.checkExamIntegrity();
        }, 5000);
        this.scheduleExamIntegrityCheck();
      });
    },
    stopExamIntegrityGuard() {
      if (this.examIntegrityObserver) {
        this.examIntegrityObserver.disconnect();
        this.examIntegrityObserver = null;
      }
      if (this.examIntegrityTimer) {
        window.clearInterval(this.examIntegrityTimer);
        this.examIntegrityTimer = null;
      }
      if (this.examIntegrityCheckTimeout) {
        window.clearTimeout(this.examIntegrityCheckTimeout);
        this.examIntegrityCheckTimeout = null;
      }
      this.examIntegrityMathJobs = 0;
      this.resetExamIntegrityRiskCandidate();
    },
    addExamIntegrityCheck(checks, label, selector, expectedHtml) {
      const expected = htmlToPlainText(expectedHtml);
      const root = this.$refs.examRoot;
      const element = root ? root.querySelector(selector) : null;
      if (expected && element) checks.push({ label, element, expected });
    },
    getExamIntegrityChecks() {
      const checks = [];
      if (!this.$refs.examRoot) return checks;
      const projectTitle = this.test_project_name
        ? `${this.test_project_name} - ${this.test_event}`
        : this.test_event;
      this.addExamIntegrityCheck(
        checks,
        "Project name",
        '[data-exam-integrity-field="project-name"]',
        projectTitle
      );
      if (this.canExam && !this.showloadingView && !this.examDataLoading) {
        const question = this.currentSelectedQuestion || {};
        this.addExamIntegrityCheck(
          checks,
          "Question",
          '[data-exam-integrity-field="question-title"]',
          this.formatContent(question.question_question) || question.question_question
        );
        ["A", "B", "C", "D", "E"].forEach((optionName) => {
          const optionHtml = question[`question_option${optionName}`];
          this.addExamIntegrityCheck(
            checks,
            `Option ${optionName}`,
            `[data-exam-integrity-option="${optionName}"] .div_p`,
            this.formatContent(optionHtml) || optionHtml
          );
        });
        if (this.individualMode && this.hasCurrentModuleMaterial) {
          // Mirror the template: each piece is formatContent()'d into its own
          // .module-material-copy div. Joining raw HTML then formatting once
          // produces a different plain-text boundary than the live DOM.
          const materialHtml = [
            this.currentModuleInstruction,
            this.currentModuleTextMaterial
          ].concat(
            this.currentModuleHtmlMaterials.map((material) => material.content || material)
          ).filter(Boolean)
            .map((piece) => `<div>${this.formatContent(piece)}</div>`)
            .join("");
          this.addExamIntegrityCheck(
            checks,
            "Stage material",
            '[data-exam-integrity-field="stage-material"]',
            materialHtml
          );
        }
      }
      return checks;
    },
    scheduleExamIntegrityCheck(delay = 300) {
      if (!this.examIntegrityEnabled || this.examIntegrityCheckTimeout) return;
      this.examIntegrityCheckTimeout = window.setTimeout(() => {
        this.examIntegrityCheckTimeout = null;
        this.checkExamIntegrity();
      }, Math.max(0, Number(delay) || 0));
    },
    getExamIntegrityRiskKey(result) {
      return JSON.stringify([
        result.label,
        result.reason,
        result.expectedLength,
        result.actualLength,
        result.expectedSample || "",
        result.actualSample || ""
      ]);
    },
    resetExamIntegrityRiskCandidate() {
      this.examIntegrityPendingRiskKey = "";
      this.examIntegrityPendingRiskAt = 0;
    },
    checkExamIntegrity(options = {}) {
      if (!this.examIntegrityEnabled || this.forceShutDown) return null;
      if (this.examIntegrityMathJobs > 0) {
        this.resetExamIntegrityRiskCandidate();
        return null;
      }
      const confirmationDelay = 750;
      const checks = this.getExamIntegrityChecks();
      for (let i = 0; i < checks.length; i++) {
        const check = checks[i];
        const result = compareVisibleTextWithExpected(check.element, check.expected, {
          label: check.label
        });
        if (result.inconclusive) {
          if (
            options.confirmImmediately &&
            result.actualLength === 0 &&
            result.expectedLength > 0
          ) {
            result.inconclusive = false;
            result.suspicious = true;
          } else if (!options.confirmImmediately) {
            this.scheduleExamIntegrityCheck(confirmationDelay);
            return null;
          } else {
            return null;
          }
        }
        if (!result.suspicious) continue;
        if (!options.confirmImmediately) {
          const riskKey = this.getExamIntegrityRiskKey(result);
          const now = Date.now();
          if (riskKey !== this.examIntegrityPendingRiskKey) {
            this.examIntegrityPendingRiskKey = riskKey;
            this.examIntegrityPendingRiskAt = now;
            this.scheduleExamIntegrityCheck(confirmationDelay);
            return null;
          }
          const elapsed = now - this.examIntegrityPendingRiskAt;
          if (elapsed < confirmationDelay) {
            this.scheduleExamIntegrityCheck(confirmationDelay - elapsed);
            return null;
          }
        }
        this.resetExamIntegrityRiskCandidate();
        this.handleExamIntegrityRisk(result);
        return result;
      }
      this.resetExamIntegrityRiskCandidate();
      return null;
    },
    handleExamIntegrityRisk(result) {
      const reasonText = {
        extra_exam_content: "extra text detected",
        missing_exam_content: "missing text detected",
        exam_content_mismatch: "content mismatch"
      }[result.reason] || result.reason;
      this.integrityWarningDetail = `${result.label}: ${reasonText}`;
      if (!this.showIntegrityWarning) {
        this.integrityIncidentCount += 1;
        console.warn("Exam integrity warning", {
          reason: result.reason,
          label: result.label,
          expectedLength: result.expectedLength,
          actualLength: result.actualLength,
          expectedSample: result.expectedSample,
          actualSample: result.actualSample,
          incidentCount: this.integrityIncidentCount
        });
      }
      this.showIntegrityWarning = true;
    },
    verifyIntegrityRecovery() {
      if (this.examIntegrityMathJobs > 0) {
        this.$message({
          message: "Formula rendering is still finishing. Please wait a moment and try again.",
          type: "info",
          duration: 3000,
          showClose: true
        });
        return;
      }
      this.integrityRechecking = true;
      this.$nextTick(() => {
        const result = this.checkExamIntegrity({ confirmImmediately: true });
        this.integrityRechecking = false;
        if (result && result.suspicious) {
          this.$message({
            message: "The exam page content still does not match the original exam data. Please disable translation and try again.",
            type: "warning",
            duration: 5000,
            showClose: true
          });
          return;
        }
        this.showIntegrityWarning = false;
        this.integrityWarningDetail = "";
        this.$message({
          message: "The exam page content matches the original exam data.",
          type: "success",
          duration: 3000,
          showClose: true
        });
      });
    },
    applyPenaltySummary(summary) {
      if (!summary || typeof summary !== "object") return;
      const total = Number(summary.total_deduction) || 0;
      this.penaltySummary = {
        talking_duration_ms: Number(summary.talking_duration_ms) || 0,
        talking_points: Number(summary.talking_points) || 0,
        camera_leave_count: Number(summary.camera_leave_count) || 0,
        camera_leave_points: Number(summary.camera_leave_points) || 0,
        network_disconnect_count: Number(summary.network_disconnect_count) || 0,
        network_disconnect_points: Number(summary.network_disconnect_points) || 0,
        total_deduction: total,
        score_valid: summary.score_valid !== false && total < 20,
        score_validity: total >= 20 ? "invalid" : "valid",
        invalid_threshold: Number(summary.invalid_threshold) || 20,
      };
    },
    refreshPenaltySummary() {
      if (!this.user_id || !this.project_code || !this.template_id) {
        return Promise.resolve(false);
      }
      return getProctorPenaltySummary(
        this.user_id,
        this.project_code,
        this.template_id,
        { timeout: 8000, skipNetworkErrorMessage: true }
      ).then((response) => {
        const result = response && response.data ? response.data : null;
        if (!result || result.code !== 0 || !result.data) return false;
        this.applyPenaltySummary(result.data);
        return true;
      }).catch((error) => {
        console.warn("Unable to refresh proctor penalty summary", error);
        return false;
      });
    },
    flushProctorPenaltyEvents() {
      const invigilate = this.$refs.invigilate;
      if (!invigilate || typeof invigilate.flushPenaltyEvents !== "function") {
        return Promise.resolve(true);
      }
      return invigilate.flushPenaltyEvents();
    },
    getCurrentMaterialDisplayContext() {
      if (this.individualMode && this.hasCurrentModuleMaterial) {
        const materialId = `Stage${this.currentSectionIndex + 1}`;
        const scrollPercent = Math.max(
          0,
          Math.min(100, Number(this.materialScrollLastPercent) || 0)
        );
        return {
          material_id: materialId,
          material_scroll_percent: scrollPercent,
          material_display: `${materialId}，${scrollPercent}%`,
        };
      }

      const pdfView = this.$refs.pdfView;
      if (this.havePicUrl && pdfView && !pdfView.loadingPdf && pdfView.loadingResult === 0) {
        const materialId = `P${Number(pdfView.pdfCurrentPageIndex || this.currentSelectedImageIndex || 1)}`;
        return {
          material_id: materialId,
          material_scroll_percent: 0,
          material_display: `${materialId}，0%`,
        };
      }

      if (this.havePicUrl && this.currentSelectedImageIndex) {
        const materialId = `P${this.currentSelectedImageIndex}`;
        return {
          material_id: materialId,
          material_scroll_percent: 0,
          material_display: `${materialId}，0%`,
        };
      }

      return {
        material_id: "",
        material_scroll_percent: 0,
        material_display: "",
      };
    },
    queueExamNavigation(navigationType, action, fromIndex, toIndex, details = {}) {
      const normalizedFrom = Number(fromIndex);
      const normalizedTo = Number(toIndex);
      if (
        navigator.onLine === false ||
        !this.user_id ||
        !this.template_id ||
        !Number.isInteger(normalizedFrom) ||
        !Number.isInteger(normalizedTo) ||
        normalizedFrom === normalizedTo
      ) {
        return;
      }

      const materialContext = this.getCurrentMaterialDisplayContext();
      const clientTime = Date.now();
      const randomSuffix = Math.random().toString(36).slice(2, 12).padEnd(10, "0");
      this.navigationLogSequence += 1;
      this.navigationLogQueue.push({
        student_id: this.user_id,
        project_code: this.project_code,
        template_id: this.template_id,
        navigation_type: navigationType,
        action,
        from_index: normalizedFrom,
        to_index: normalizedTo,
        from_question_id: details.fromQuestionId || "",
        to_question_id: details.toQuestionId || "",
        material_id: details.materialId || materialContext.material_id,
        material_scroll_percent: Number.isFinite(Number(details.materialScrollPercent))
          ? Number(details.materialScrollPercent)
          : materialContext.material_scroll_percent,
        material_display: details.materialDisplay || materialContext.material_display,
        client_user_name: this.candidateName || this.student_name,
        client_time_ms: clientTime,
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: `nav-${clientTime}-${this.navigationLogSequence}-${randomSuffix}`,
      });
      if (this.navigationLogQueue.length > 200) {
        this.navigationLogQueue.splice(this.navigationLogSending ? 1 : 0, 1);
      }
      this.flushNavigationLogs();
    },
    flushNavigationLogs() {
      if (this.navigationLogSending || this.navigationLogQueue.length === 0) {
        return;
      }
      if (navigator.onLine === false) {
        this.navigationLogQueue = [];
        return;
      }

      const event = this.navigationLogQueue[0];
      this.navigationLogSending = true;
      saveExamNavigationLog(event, {
        timeout: 8000,
        skipNetworkErrorMessage: true,
      })
        .then((res) => {
          const result = res && res.data ? res.data : null;
          if (!result || result.code !== 0) {
            throw new Error(result && result.msg ? result.msg : "Navigation event was rejected");
          }
          if (
            this.navigationLogQueue[0] &&
            this.navigationLogQueue[0].client_event_id === event.client_event_id
          ) {
            this.navigationLogQueue.shift();
          }
        })
        .catch((error) => {
          if (
            this.navigationLogQueue[0] &&
            this.navigationLogQueue[0].client_event_id === event.client_event_id
          ) {
            this.navigationLogQueue.shift();
          }
          console.warn("Exam navigation event save failed; event dropped", error);
        })
        .finally(() => {
          this.navigationLogSending = false;
          this.flushNavigationLogs();
        });
    },
    queueExamInteraction(interactionType, action, details = {}) {
      if (
        navigator.onLine === false ||
        !this.user_id ||
        !this.template_id ||
        !this.project_code
      ) {
        return;
      }

      const clientTime = Date.now();
      const randomSuffix = Math.random().toString(36).slice(2, 12).padEnd(10, "0");
      const questionIndex = Number(details.questionIndex);
      const answerLength = Number(details.answerLength);
      const materialContext = this.getCurrentMaterialDisplayContext();
      this.interactionLogSequence += 1;
      this.interactionLogQueue.push({
        student_id: this.user_id,
        project_code: this.project_code,
        template_id: this.template_id,
        interaction_type: interactionType,
        action,
        question_index: Number.isInteger(questionIndex) && questionIndex > 0 ? questionIndex : 0,
        question_id: details.questionId || "",
        previous_choice: details.previousChoice || "",
        current_choice: details.currentChoice || "",
        answer_length: Number.isFinite(answerLength) && answerLength > 0 ? Math.floor(answerLength) : 0,
        scroll_start_percent: Number(details.scrollStartPercent) || 0,
        scroll_end_percent: Number(details.scrollEndPercent) || 0,
        scroll_distance_px: Number(details.scrollDistancePx) || 0,
        material_id: details.materialId || materialContext.material_id,
        material_scroll_percent: Number.isFinite(Number(details.materialScrollPercent))
          ? Number(details.materialScrollPercent)
          : materialContext.material_scroll_percent,
        material_display: details.materialDisplay || materialContext.material_display,
        source: details.source || "click",
        client_user_name: this.candidateName || this.student_name,
        client_time_ms: clientTime,
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: `interaction-${clientTime}-${this.interactionLogSequence}-${randomSuffix}`,
      });
      if (this.interactionLogQueue.length > 200) {
        this.interactionLogQueue.splice(this.interactionLogSending ? 1 : 0, 1);
      }
      this.flushInteractionLogs();
    },
    flushInteractionLogs() {
      if (this.interactionLogSending || this.interactionLogQueue.length === 0) return;
      if (navigator.onLine === false) {
        this.interactionLogQueue = [];
        return;
      }

      const event = this.interactionLogQueue[0];
      this.interactionLogSending = true;
      saveExamInteractionLog(event, {
        timeout: 8000,
        skipNetworkErrorMessage: true,
      }).then((res) => {
        const result = res && res.data ? res.data : null;
        if (!result || result.code !== 0) {
          throw new Error(result && result.msg ? result.msg : "Interaction event was rejected");
        }
        if (
          this.interactionLogQueue[0] &&
          this.interactionLogQueue[0].client_event_id === event.client_event_id
        ) {
          this.interactionLogQueue.shift();
        }
      }).catch((error) => {
        if (
          this.interactionLogQueue[0] &&
          this.interactionLogQueue[0].client_event_id === event.client_event_id
        ) {
          this.interactionLogQueue.shift();
        }
        console.warn("Exam interaction event save failed; event dropped", error);
      }).finally(() => {
        this.interactionLogSending = false;
        this.flushInteractionLogs();
      });
    },
    handleMaterialScroll(event) {
      const container = event && event.currentTarget ? event.currentTarget : this.$refs.materialScroll;
      if (!container) return;
      this.materialScrollPendingTarget = container;
      if (this.materialScrollFrame !== null) return;
      this.materialScrollFrame = window.requestAnimationFrame(() => {
        this.materialScrollFrame = null;
        const pendingTarget = this.materialScrollPendingTarget;
        this.materialScrollPendingTarget = null;
        this.captureMaterialScroll(pendingTarget);
      });
    },
    captureMaterialScroll(container) {
      if (!container) return;
      const scrollTop = Math.max(0, Number(container.scrollTop) || 0);
      const scrollRange = Math.max(0, container.scrollHeight - container.clientHeight);
      const scrollPercent = scrollRange > 0 ? Math.round((scrollTop / scrollRange) * 100) : 0;
      if (Date.now() < this.materialScrollSuppressUntil) {
        this.materialScrollLastTop = scrollTop;
        this.materialScrollLastPercent = scrollPercent;
        this.materialScrollStartTop = null;
        return;
      }
      if (this.materialScrollStartTop === null) {
        this.materialScrollStartTop = this.materialScrollLastTop;
        this.materialScrollStartPercent = this.materialScrollLastPercent;
      }
      this.materialScrollLastTop = scrollTop;
      this.materialScrollLastPercent = scrollPercent;
      if (this.materialScrollTimer) window.clearTimeout(this.materialScrollTimer);
      this.materialScrollTimer = window.setTimeout(() => {
        this.materialScrollTimer = null;
        this.flushMaterialScrollLog();
      }, 800);
    },
    flushMaterialScrollLog() {
      if (this.materialScrollFrame !== null) {
        window.cancelAnimationFrame(this.materialScrollFrame);
        this.materialScrollFrame = null;
        const pendingTarget = this.materialScrollPendingTarget;
        this.materialScrollPendingTarget = null;
        this.captureMaterialScroll(pendingTarget);
      }
      if (this.materialScrollTimer) {
        window.clearTimeout(this.materialScrollTimer);
        this.materialScrollTimer = null;
      }
      if (this.materialScrollStartTop === null) return;
      const startTop = this.materialScrollStartTop;
      const endTop = this.materialScrollLastTop;
      const distance = Math.round(endTop - startTop);
      const startPercent = this.materialScrollStartPercent;
      const endPercent = this.materialScrollLastPercent;
      this.materialScrollStartTop = null;
      if (Math.abs(distance) < 40) return;
      this.queueExamInteraction(
        "display",
        distance > 0 ? "material_scrolled_down" : "material_scrolled_up",
        {
          scrollStartPercent: startPercent,
          scrollEndPercent: endPercent,
          scrollDistancePx: distance,
          source: "scroll",
        }
      );
    },
    openTeamChat() {
      this.showTeamChatDrawer = true;
      this.queueExamInteraction("team", "chat_opened");
    },
    openFaq() {
      this.showQADrawer = true;
      this.queueExamInteraction("support", "faq_opened");
    },
    async reconnectProctoring() {
      if (this.proctoringReconnectLoading || this.status) {
        return;
      }
      if (document.hidden || (document.hasFocus && !document.hasFocus())) {
        this.proctoringReconnectError =
          "Proctoring did not connect. Return to the exam page and try again.";
        return;
      }

      const invigilate = this.$refs.invigilate;
      if (!invigilate || typeof invigilate.init !== "function") {
        this.proctoringReconnectError =
          "The proctoring controls are still loading. Please try again.";
        return;
      }

      this.proctoringReconnectLoading = true;
      this.proctoringReconnectError = "";
      try {
        const connected = await invigilate.init();
        if (!connected && !this.status) {
          this.proctoringReconnectError =
            this.screenOnlyProctoring
              ? "Proctoring did not connect. Choose Entire Screen and try again."
              : "Proctoring did not connect. Allow camera access, choose Entire Screen, and try again.";
        }
      } catch (error) {
        console.error("Unable to reconnect exam proctoring", error);
        this.proctoringReconnectError =
          "Proctoring could not be connected. Please try again.";
      } finally {
        this.proctoringReconnectLoading = false;
      }
    },
    // AM-01-07: when the school disabled multimedia proctoring, the exam runs
    // screen capture only (no camera / microphone). The backend policy is
    // authoritative so relay entries and page reloads behave the same way.
    async applyProctoringPolicy() {
      let screenOnly = this.$route.query.proctor_scope === "screen";
      try {
        const res = await getExamProctoringPolicy(this.project_code, getUserId());
        const policy = res && res.data && res.data.code === 0 ? res.data.data : null;
        if (policy && policy.disable_multimedia) {
          screenOnly = Boolean(policy.screen);
          if (!policy.screen) {
            this.invigilateStatus = "true";
          }
        } else if (policy) {
          screenOnly = false;
        }
      } catch (error) {
        console.warn("Unable to load exam proctoring policy", error);
      }
      this.screenOnlyProctoring = screenOnly;
      if (screenOnly) {
        this.invigilateStatus = "false";
      }
    },
    async attemptInitialProctoring() {
      if (
        this.DEBUG_DEV ||
        this.invigilateStatus === "true" ||
        this.status ||
        this.proctoringAutoAttemptPending ||
        this.proctoringAutoAttemptFinished
      ) {
        return;
      }

      const invigilate = this.$refs.invigilate;
      if (!invigilate || typeof invigilate.init !== "function") {
        this.proctoringAutoAttemptFinished = true;
        this.proctoringReconnectError = "";
        return;
      }

      this.proctoringAutoAttemptPending = true;
      this.proctoringReconnectError = "";
      try {
        const connected = await invigilate.init({ automatic: true });
        if (!connected && !this.status) {
          this.proctoringReconnectError = "";
        }
      } catch (error) {
        console.error("Automatic exam proctoring failed", error);
        this.proctoringReconnectError = "";
      } finally {
        this.proctoringAutoAttemptPending = false;
        this.proctoringAutoAttemptFinished = true;
      }
    },
    monotonicNow() {
      if (window.performance && typeof window.performance.now === "function") {
        return window.performance.now();
      }
      return Date.now();
    },
    setServerClockAnchor(serverTime) {
      const localTime = Date.now() / 1000;
      const parsedServerTime = Number(serverTime);
      this.serverClockAnchor = Number.isFinite(parsedServerTime) && parsedServerTime > 0
        ? parsedServerTime
        : localTime + (this.timeOffset || 0);
      this.serverMonotonicAnchor = this.monotonicNow();
      this.timeOffset = this.serverClockAnchor - localTime;
    },
    estimatedServerNow() {
      if (this.serverClockAnchor && this.serverMonotonicAnchor) {
        return this.serverClockAnchor +
          (this.monotonicNow() - this.serverMonotonicAnchor) / 1000;
      }
      return Date.now() / 1000 + (this.timeOffset || 0);
    },
    syncCountDownClock() {
      this.updateWaitingCountDown();
      this.updateExamCountDown();
    },
    clearWaitingCountDown() {
      if (this.countDownInternal) {
        window.clearInterval(this.countDownInternal);
        this.countDownInternal = null;
      }
      this.waitingCountDownDeadline = 0;
      this.waitingCountDownComplete = null;
    },
    startWaitingCountDown(seconds, onComplete) {
      this.clearWaitingCountDown();
      const waitSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
      this.showStartBtn = false;
      this.waitingCountDownDeadline = this.estimatedServerNow() + waitSeconds;
      this.waitingCountDownComplete = typeof onComplete === "function"
        ? onComplete
        : null;

      this.updateWaitingCountDown();
      if (this.waitingCountDownDeadline) {
        this.countDownInternal = window.setInterval(() => {
          this.updateWaitingCountDown();
        }, 1000);
      }
    },
    updateWaitingCountDown() {
      if (!this.waitingCountDownDeadline) {
        return;
      }
      const remaining = Math.max(
        0,
        Math.ceil(this.waitingCountDownDeadline - this.estimatedServerNow())
      );
      this.formatShowTime(remaining);
      if (remaining > 0) {
        return;
      }

      const onComplete = this.waitingCountDownComplete;
      this.clearWaitingCountDown();
      if (onComplete) {
        onComplete();
      }
    },
    async beforeAttachUpload(file) {
      if (this.isQuestionConfirmed(this.currentSelectedQuestion) || this.isConfirmingAnswer) {
        this.$message.warning("This answer has already been confirmed and cannot be changed.");
        return false;
      }
      console.log(123, file);
      let isJPG = false;
      if (file.type === "image/jpeg") {
        isJPG = true;
      }
      if (file.type === "image/png") {
        isJPG = true;
      }
      let isLt2M = file.size / 1024 / 1024 < 5;
      console.log(123, isJPG, isLt2M);
      if (!isJPG) {
        this.$message.error("The file can be JPG or PNG!");
        return;
      }
      if (!isLt2M) {
        this.$message.error("The file exceed 5MB!");
        return;
      }
      const result = await this.getQiniuToken();

      console.log('4444');
      if (result.data.code === 0) {
        const token = result.data.data;
        this.observable = qiniu.upload(
          file,
          new Date().getTime() + file.name,
          token
        );
        const observer = {
          complete: (res) => {
            let url = "http://mainbucket.reachable-edu.com/" + res.key;
            this.currentSelectedQuestion.answer_img_url = url;
            console.log(2222, url);
            this.saveUrlAnswer(url);
          },
        };
        this.observable.subscribe(observer);
      } else {
      }
    },
    async getQiniuToken() {
      return new Promise((resolve) => {
        getQiniuToken().then((res) => {
          resolve(res);
        });
      });
    },
    ssssave: function (val) {
                // 获取预览文本
                console.log(this.testAss)
                console.log(val)
            },
    //刷新页面
    loadExam(){

      this.gotoImageIndex = 1;

      this.fetchData(2)
    },
    enlargeClick(){
      this.parentShowBigPic++
      this.queueExamInteraction("display", "material_enlarged");
    },
    browerType() {
        var ua = navigator.userAgent.toLocaleLowerCase();
        // 判断是否为IE(第一个是正常的IE，第二个是Edge，第三个是IE11)
        var isIE = (ua.indexOf("compatible") > -1 && ua.indexOf("msie") > -1) || (ua.indexOf("edge") > -1) || (ua.indexOf(
            'trident') > -1 && ua.indexOf("rv:11.0") > -1);
        // 判断是否为IE5678，!+[1,] 在IE5678返回true，在IE9、IE10、IE11返回false
        var isLteIE8 = isIE && !+[1,];
        // 用于防止因通过IE8+的文档兼容性模式设置文档模式，导致版本判断失效
        var dm = document.documentMode,
            isIE5,
            isIE6,
            isIE7,
            isIE8,
            isIE9,
            isIE10,
            isIE11;
        if (dm) {
            isIE5 = dm === 5;
            isIE6 = dm === 6;
            isIE7 = dm === 7;
            isIE8 = dm === 8;
            isIE9 = dm === 9;
            isIE10 = dm === 10;
            isIE11 = dm === 11;
        } else {
            // 判断是否为IE5，IE5的文本模式为怪异模式(quirks),真实的IE5.5浏览器中没有document.compatMode属性  
            isIE5 = (isLteIE8 && (!document.compatMode || document.compatMode === 'BackCompat'));
            // 判断是否为IE6，IE7开始有XMLHttpRequest对象  
            isIE6 = isLteIE8 && !isIE5 && !XMLHttpRequest;
            // 判断是否为IE7，IE8开始有document.documentMode属性  
            isIE7 = isLteIE8 && !isIE6 && !document.documentMode;
            // 判断是否IE8  
            isIE8 = isLteIE8 && document.documentMode;
            // 判断IE9，IE9严格模式中函数内部this不为undefined  
            isIE9 = !isLteIE8 && (function () {
                "use strict";
                return !!this;
            }());
            // 判断IE10，IE10开始支持严格模式，严格模式中函数内部this为undefined   
            isIE10 = isIE && !!document.attachEvent && (function () {
                "use strict";
                return !this;
            }());
            // 判断IE11，IE11开始移除了attachEvent属性  
            isIE11 = isIE && !document.attachEvent;
        }
        // 因为字符串存在覆盖重复原因，判断顺序不可随意修改
        if (isIE5) return 'IE5';
        if (isIE6) return 'IE6';
        if (isIE7) return 'IE7';
        if (isIE8) return 'IE8';
        if (isIE9) return 'IE9';
        if (isIE10) return 'IE10';
        if (isIE11) return 'IE11';
        if (ua.indexOf('green') > -1) return 'green';
        if (ua.indexOf('qq') > -1) return 'qq';
        if (ua.indexOf('bidu') > -1) return 'baidu';
        if (ua.indexOf('lb') > -1) return 'lb';
        if (ua.indexOf('world') > -1) return 'world';
        if (ua.indexOf('2345') > -1) return '2345';
        if (ua.indexOf('maxthon') > -1) return 'aoyou';
        if (ua.indexOf('tao') > -1) return 'taobao';
        if (ua.indexOf('ubrowser') > -1) return 'UC';
        if (ua.indexOf('coolnovo') > -1) return 'coolnovo';
        if (ua.indexOf('opr') > -1) return 'opera';
        if (ua.indexOf('se') > -1) return 'sougou';
        if (ua.indexOf('firefox') > -1) return 'firefox';
        if (ua.indexOf('safari') > -1 && ua.indexOf("version") > -1) return ('safari');
        if (window.navigator.mimeTypes[40] || !window.navigator.mimeTypes.length) return '360';
        if (ua.indexOf("chrome") > -1 && window.chrome) return ('chrome');
        return 'Other';
    },
    clickRemoveQuestionUrlAnswer(){
      if (this.isQuestionConfirmed(this.currentSelectedQuestion) || this.isConfirmingAnswer) {
        this.$message.warning("This answer has already been confirmed and cannot be changed.");
        return;
      }
      let that = this
      this.$alert('Are you sure remove this picture ?','Tips',{
        confirmButtonText:'Confirm',
        callback:action => {
          if(action == 'confirm'){
            that.saveUrlAnswer('')
          }
        }
      })
    },
    saveUrlAnswer(url) {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      if (this.isQuestionConfirmed(this.currentSelectedQuestion) || this.isConfirmingAnswer) {
        this.$message.warning("This answer has already been confirmed and cannot be changed.");
        return;
      }

      let item = this.currentSelectedQuestion;
      let choice = item.question_type === "input"
        ? String(item.inputAnswer || item.choice || "")
        : String(item.choice || "");
      let question_id = item.question_id;
      let index = this.currentSelectedQuestionIndex;
      this.questionAnswers[question_id] = { choice: choice, status: "no" };
      this.$set(item, "choice", choice);
      this.$set(item, "answer_img_url", url);
      this.$set(this.drawerQuestionList[index], "choice", choice);
      this.$set(this.drawerQuestionList[index], "answer_img_url", url);
      this.submitAnswrQuestionChoice = choice;
      this.submitAnswrQuestionImg_url = url;
    },

    pdfCallback(numbers) {
      this.examImageSize = numbers;
    },
    reLoadImage(url) {
      var time = new Date().getTime();
      let new_url = url + "?" + time;
      this.exam_images[this.currentSelectedImageIndex] = new_url;
    },
    pushHistory() {
      history.pushState(null, null, document.URL);
    },
    networkSuccessCallback() {
      this.finishNetworkDisconnectIncident();
      this.flushProctorPenaltyEvents();
      this.showNetworkErrorModal = false;
      this.networkChecking = false;
      this.$message.success("Connected!");
    },
    networkErrorCallback() {
      // 延迟确认，避免浏览器瞬间offline误触发
      console.log('offline event fired, navigator.onLine:', navigator.onLine);
      setTimeout(() => {
        if (!navigator.onLine) {
          this.startNetworkDisconnectIncident();
          this.interactionLogQueue = [];
          this.navigationLogQueue = [];
          this.showNetworkErrorModal = true;
        }
      }, 1000);
    },
    doNetworkHealthCheck() {
      this.networkChecking = true;
      healthCheck()
        .then((res) => {
          this.finishNetworkDisconnectIncident();
          this.flushProctorPenaltyEvents();
          this.networkChecking = false;
          this.showNetworkErrorModal = false;
          this.$message.success("Network connection restored!");
        })
        .catch(() => {
          this.networkChecking = false;
          this.$message.error("Network is still unavailable. Please check your connection and try again.");
        });
    },
    getNetworkDisconnectStorageKey() {
      if (!this.user_id || !this.project_code || !this.template_id) return "";
      return `am-exam-network-disconnect:v1:${this.project_code}:${this.template_id}:${this.user_id}`;
    },
    persistNetworkDisconnectState() {
      const storageKey = this.getNetworkDisconnectStorageKey();
      if (!storageKey) return;
      try {
        if (
          !this.networkDisconnectStartedAt &&
          !this.networkDisconnectEventId &&
          this.networkDisconnectQueue.length === 0
        ) {
          window.localStorage.removeItem(storageKey);
          return;
        }
        window.localStorage.setItem(storageKey, JSON.stringify({
          active: this.networkDisconnectStartedAt && this.networkDisconnectEventId
            ? {
              started_at: this.networkDisconnectStartedAt,
              client_event_id: this.networkDisconnectEventId,
            }
            : null,
          sequence: this.networkDisconnectSequence,
          queue: this.networkDisconnectQueue,
        }));
      } catch (error) {
        console.warn("Unable to persist the network disconnect queue", error);
      }
    },
    restoreNetworkDisconnectState() {
      const storageKey = this.getNetworkDisconnectStorageKey();
      if (!storageKey) return;
      try {
        const rawState = window.localStorage.getItem(storageKey);
        if (rawState) {
          const storedState = JSON.parse(rawState);
          const storedQueue = Array.isArray(storedState.queue) ? storedState.queue : [];
          this.networkDisconnectQueue = storedQueue.filter((event) => (
            event &&
            event.event_type === "network_disconnect" &&
            event.student_id === this.user_id &&
            event.project_code === this.project_code &&
            event.template_id === this.template_id &&
            Number(event.start_time_ms) > 0 &&
            typeof event.client_event_id === "string" &&
            /^[A-Za-z0-9._-]{8,160}$/.test(event.client_event_id)
          )).map((event) => ({ ...event, _attempts: 0 }));
          const active = storedState.active;
          if (
            active &&
            Number(active.started_at) > 0 &&
            typeof active.client_event_id === "string" &&
            /^[A-Za-z0-9._-]{8,160}$/.test(active.client_event_id)
          ) {
            this.networkDisconnectStartedAt = Number(active.started_at);
            this.networkDisconnectEventId = active.client_event_id;
          }
          this.networkDisconnectSequence = Math.max(0, Number(storedState.sequence) || 0);
        }
      } catch (error) {
        console.warn("Unable to restore the network disconnect queue", error);
        try {
          window.localStorage.removeItem(storageKey);
        } catch (storageError) {
          console.warn("Unable to clear invalid network disconnect state", storageError);
        }
      }
      if (navigator.onLine === false) {
        this.startNetworkDisconnectIncident();
        return;
      }
      this.finishNetworkDisconnectIncident();
      this.flushNetworkDisconnectLogs();
    },
    startNetworkDisconnectIncident() {
      if (this.networkDisconnectStartedAt || !this.user_id || !this.template_id) return;
      const startedAt = Date.now();
      this.networkDisconnectSequence += 1;
      this.networkDisconnectStartedAt = startedAt;
      this.networkDisconnectEventId = `network-disconnect-${startedAt}-${this.networkDisconnectSequence}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      this.persistNetworkDisconnectState();
    },
    finishNetworkDisconnectIncident() {
      if (!this.networkDisconnectStartedAt || !this.networkDisconnectEventId) return;
      const endedAt = Date.now();
      this.networkDisconnectQueue.push({
        event_type: "network_disconnect",
        student_id: this.user_id,
        project_code: this.project_code,
        template_id: this.template_id,
        start_time_ms: this.networkDisconnectStartedAt,
        end_time_ms: Math.max(this.networkDisconnectStartedAt, endedAt),
        duration_ms: Math.max(0, endedAt - this.networkDisconnectStartedAt),
        confidence: 1,
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: this.networkDisconnectEventId,
        detector_version: "browser-online-status",
        _attempts: 0,
      });
      this.networkDisconnectStartedAt = 0;
      this.networkDisconnectEventId = "";
      this.persistNetworkDisconnectState();
      this.flushNetworkDisconnectLogs();
    },
    flushNetworkDisconnectLogs() {
      if (
        this.networkDisconnectSending ||
        this.networkDisconnectQueue.length === 0 ||
        navigator.onLine === false
      ) return;
      const event = this.networkDisconnectQueue[0];
      event._attempts += 1;
      const payload = { ...event };
      delete payload._attempts;
      this.networkDisconnectSending = true;
      saveProctorEvent(payload, {
        timeout: 8000,
        skipNetworkErrorMessage: true,
      }).then((response) => {
        const result = response && response.data ? response.data : null;
        if (!result || result.code !== 0) {
          throw new Error(result && result.msg ? result.msg : "Network event was rejected");
        }
        if (result.data && result.data.summary) this.applyPenaltySummary(result.data.summary);
        this.networkDisconnectQueue.shift();
        this.persistNetworkDisconnectState();
      }).catch((error) => {
        console.warn("Unable to record network disconnect", error);
        this.persistNetworkDisconnectState();
      }).finally(() => {
        this.networkDisconnectSending = false;
        if (this.networkDisconnectQueue.length > 0) {
          const retryDelay = Math.min(30000, 700 * Math.pow(2, Math.min(event._attempts - 1, 6)));
          window.setTimeout(() => this.flushNetworkDisconnectLogs(), retryDelay);
        }
      });
    },
    waitForNetworkDisconnectLogs() {
      this.finishNetworkDisconnectIncident();
      this.flushNetworkDisconnectLogs();
      return new Promise((resolve) => {
        const startedAt = Date.now();
        const check = () => {
          if (!this.networkDisconnectSending && this.networkDisconnectQueue.length === 0) {
            resolve(true);
            return;
          }
          if (Date.now() - startedAt >= 9000) {
            resolve(false);
            return;
          }
          window.setTimeout(check, 60);
        };
        check();
      });
    },
    isNetworkError(err) {
      if (!err) return false;
      if (err.message === 'Network Error') return true;
      if (err.code === 'ECONNABORTED') return true;
      return !navigator.onLine;
    },
    handleApiNetworkError(err) {
      if (this.isNetworkError(err)) {
        this.lastNetworkError =
          (err && (err.message || err.code)) || "network error";
        this.showNetworkErrorModal = true;
      }
    },
    handleGotoImagChanged(value) {
      //图片不需要跟着一起切换
      // this.currentSelectedImageIndex = this.gotoImageIndex-1
    },
    beforeShowSelectImageDialog() {
      this.gotoImageIndex = this.currentSelectedImageIndex + 1;
      this.showGotoPageDialog = true;
    },
    gotoSelectedImage() {
      if (this.$refs.pdfView.checkIsLoadingPdf()) {
        return;
      }
      this.resetViewPosition();
      const previousIndex = this.currentSelectedImageIndex;
      var index = this.gotoImageIndex;
      this.$refs.pdfView.gotoTargetPage(index);
      this.currentSelectedImageIndex = index;
      this.showGotoPageDialog = false;
      this.queueExamNavigation("material", "goto", previousIndex, index);
    },
    doExchangeLang() {
      if (this.currentLang === "中文") {
        this.currentLang = "英文";
      } else {
        this.currentLang = "中文";
      }
    },
    getShowWord(value) {
      return this.WORDS[this.currentLang][value];
    },

    async restoreSectionProgress() {
      if (!this.individualMode) return;
      try {
        const response = await getSectionProgress(
          this.user_id,
          this.template_id,
          this.project_code
        );
        const result = response && response.data ? response.data : null;
        const progress = result && result.code === 0 ? result.data : null;
        if (!progress || typeof progress !== "object") return;
        Object.keys(progress).forEach((sectionKey) => {
          const sectionIndex = Number(sectionKey);
          const entry = progress[sectionKey] || {};
          const questionIndex = Number(entry.current_index);
          if (Number.isInteger(sectionIndex) && Number.isInteger(questionIndex)) {
            this.$set(this.sectionProgress, sectionIndex, {
              currentIndex: Math.max(0, questionIndex),
              totalQuestions: 0
            });
          }
        });
      } catch (error) {
        console.warn("Unable to restore saved stage progress", error);
      }
    },

    studentSignIn() {
      return new Promise((resolve, reject) => {
        studentSignWithTemplate(
          this.user_id,
          this.template_id,
          this.project_code
        ).then(async (res) => {
          const response = res.data || {};
          var result = response.data;
          if (!result) {
            this.$message.error(response.msg || 'Unable to enter the exam.');
            this.forceShutDown = true;
            this.$router.replace({ path: '/studentlist' });
            reject(false);
            return;
          }

          this.setServerClockAnchor(result.server_time);

          if (this.individualMode) {
            this.individualExam = result.individual_exam || null;
            if (!this.individualExam || this.individualExam.status !== 'in_progress') {
              this.$message.error(response.msg || 'This individual exam is no longer available.');
              this.forceShutDown = true;
              this.$router.replace({ path: '/studentlist' });
              reject(false);
              return;
            }
            this.currentSectionIndex = parseInt(this.individualExam.current_section_index, 10) || 0;
            await this.restoreSectionProgress();
            this.startWindowCountDown(
              this.individualExam.window_end_time,
              this.individualExam.server_time
            );
          }
          var begin_time = result.begin_time;
          var end_time = result.end_time;
          var test_duration = result.test_duration;

          // 记录考试全局开始时间（所有学生中最早的begin_time）
          this.exam_start_time = result.exam_start_time || begin_time;

          //补考人员 获取补考时间
          if (result.repair_sign) {
            begin_time = result.repair_sign.repair_begin_time;
            end_time = result.repair_sign.repair_end_time;
            test_duration = result.repair_sign.repair_duration;
          }

          if (result.do_exercises_index != undefined){
            this.currentSelectedQuestionIndex = parseInt(result.do_exercises_index)
            if (
              this.individualMode &&
              !this.sectionProgress[this.currentSectionIndex] &&
              Number.isInteger(this.currentSelectedQuestionIndex)
            ) {
              this.$set(this.sectionProgress, this.currentSectionIndex, {
                currentIndex: Math.max(0, this.currentSelectedQuestionIndex),
                totalQuestions: 0
              });
            }
            
          }

          console.log("begin_time :" + begin_time);
          console.log("end_time :" + end_time);
          console.log("test_duration :" + test_duration);

          var currentTime = this.estimatedServerNow();

          // end_time = currentTime+ 1000;

          console.log("currentTime :" + currentTime);
          // if(currentTime<=begin_time){
          //   this.$router.go(-1)
          //   this.$message.error('考试时间还没有到！')
          //   return
          // }
          if (currentTime > end_time && !this.debugNetwork) {
            this.$message.error("You've already completed the exam.");
            this.$router.go(-1);
            reject(false);
            return;
          }

          this.showloadingView = true;
          // console.log('1231312312' + result.remain_wait_time)

          // const loading = this.$loading({
          //   lock: true,
          //   text: `需要等待时间${parseInt(result.remain_wait_time / 60)}分钟`,
          //   spinner: "el-icon-loading",
          //   background: "rgba(0, 0, 0, 0.7)",
          // });

          this.startWaitingCountDown(result.remain_wait_time, () => {
            var diff = this.individualMode && this.individualExam
              ? parseInt(this.individualExam.current_section_end_time, 10)
                - this.estimatedServerNow()
              : end_time - this.estimatedServerNow();
            var last_time = Math.floor(diff);
            if (this.debugNetwork && last_time <= 0) {
              last_time = 3600; // debug模式给1小时测试时间
            }
            this.startCountDown(last_time);
            this.showloadingView = false;
            resolve(true);
          });

          // setTimeout(() => {
          //   var diff = end_time - currentTime;
          //   var last_time = parseInt(diff);
          //   this.startCountDown(last_time);
          //   //  this.showloadingView = false
          //   // loading.close();
          //   resolve(true);
          // }, result.remain_wait_time * 1000);
        });
      });
    },

    formatShowTime(time) {
      // console.log('倒计时' + time)
      var theTime = time;
      var theTime1 = 0; // 分
      var theTime2 = 0; // 小时
      if (theTime > 60) {
        //超过1分钟
        theTime1 = parseInt(theTime / 60);
        theTime = parseInt(theTime % 60); //剩余秒数
        if (theTime1 > 60) {
          //超过60分钟
          theTime2 = parseInt(theTime1 / 60); //多少小时
          theTime1 = parseInt(theTime1 % 60);
        }
      }
      this.hourLab = this.formatTime1(theTime2);
      this.minuteLab = this.formatTime1(theTime1);
      this.secondLab = this.formatTime1(theTime);
    },
    formatTime1(time) {
      if (time >= 10) {
        return time;
      }
      return "0" + time;
    },

    checkStudentExam() {
      if (this.debugNetwork) return;
      checkIsUploadTest(this.template_id, getUserId()).then((res) => {
        var result = res.data;

        if (result) {
          this.canExam = false;
          console.log("---sorry--");
          this.clearCountDown();
        }
      });
    },
    async handleLeaveExam() {
      if (this.individualMode) {
        this.leaveExamMessage = 'Are you sure you want to leave? Saved answers will be kept, but the current stage timer and your 24-hour exam window will continue running.';
        this.showLeaveExamDialog = true;
        this.queueExamInteraction("exam", "leave_dialog_opened", { source: "individual" });
        return;
      }
      // Fetch latest entry count
      try {
        const res = await checkRelayEntryCount(this.user_id, this.template_id, this.project_code);
        if (res.data.code === 0) {
          this.currentRelayEntryCount = res.data.data.entry_count || 0;
        }
      } catch (e) {
        console.error('Failed to check entry count:', e);
      }

      const count = this.currentRelayEntryCount;
      const ordinal = count === 1 ? '1st' : '2nd';
      const remaining = 2 - count;
      this.leaveExamMessage = `Are you sure you want to exit? This is your ${ordinal} attempt. You have ${remaining} entr${remaining === 1 ? 'y' : 'ies'} left.`;
      this.showLeaveExamDialog = true;
      this.queueExamInteraction("exam", "leave_dialog_opened", { source: "relay" });
    },

    cancelLeaveExam() {
      this.showLeaveExamDialog = false;
      this.queueExamInteraction("exam", "leave_cancelled");
    },

    confirmLeaveExam() {
      this.queueExamInteraction("exam", "leave_confirmed");
      this.showLeaveExamDialog = false;
      if (this.relayMode) {
        leaveRelayExam(this.user_id, this.template_id, this.project_code);
      }
      this.forceShutDown = true;
      if (this.individualMode) {
        this.$router.replace({ path: '/studentlist' });
      } else {
        this.$router.replace({
          path: "/waiting-room",
          query: {
            template_id: this.template_id,
            exam_name: this.test_event,
          },
        });
      }
    },

    individualSubmitWaitRemainMin() {
      if (!this.individualMode || this.test_type === "mock_test") return 0;
      const startTime = Number(
        this.individualExam && this.individualExam.current_section_start_time
      );
      if (!startTime) return 0;
      const elapsed = this.estimatedServerNow() - startTime;
      if (elapsed >= 1800) return 0;
      return Math.ceil((1800 - elapsed) / 60);
    },
    async loadStageSubmitEmail() {
      if (this.stageSubmitExpectedEmail) return true;
      if (this.stageSubmitEmailLoading) return false;
      this.stageSubmitEmailLoading = true;
      try {
        const res = await getUser(this.user_id, "student", this.project_code, {
          timeout: 10000,
          skipNetworkErrorMessage: true
        });
        const user = res && res.data ? res.data.data : null;
        const studentInfo = user && user.student_info ? user.student_info : user;
        const email = String(studentInfo && (
          studentInfo.student_email || studentInfo.email || ""
        )).trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          throw new Error("The current student account has no valid email address");
        }
        this.stageSubmitExpectedEmail = String(email).trim();
        this.candidateEmail = this.stageSubmitExpectedEmail;
        setUserEmail(this.stageSubmitExpectedEmail);
        return true;
      } catch (error) {
        console.error("Unable to load the email required for stage submission", error);
        this.$message({
          message: "We could not verify the login email for this exam. This stage has not been submitted. Check your network connection and try again.",
          type: "error",
          duration: 10000,
          showClose: true
        });
        return false;
      } finally {
        this.stageSubmitEmailLoading = false;
      }
    },
    async loadCandidateIdentity() {
      try {
        const res = await getUser(this.user_id, "student", this.project_code, {
          timeout: 10000,
          skipNetworkErrorMessage: true
        });
        const result = res && res.data && res.data.code === 0 ? res.data.data : null;
        const studentInfo = result && result.student_info ? result.student_info : result;
        if (!studentInfo) return;

        const email = String(studentInfo.student_email || studentInfo.email || "").trim();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.candidateEmail = email;
          setUserEmail(email);
        }

        const fullName = String(
          studentInfo.name ||
          studentInfo.student_name ||
          [studentInfo.student_lastName, studentInfo.student_givenName].filter(Boolean).join(" ") ||
          ""
        ).trim();
        if (fullName) this.candidateName = fullName;
      } catch (error) {
        console.warn("Unable to refresh candidate identity", error);
      }
    },
    async doSubmitAnswer() {

      // 30分钟提交限制：基于考试全局开始时间，而非单个学生进入时间
      var currentTimestamp = new Date().getTime() / 1000;
      if (!this.individualMode && this.exam_start_time > 0 && (currentTimestamp - this.exam_start_time) < 1800 && this.test_type != 'mock_test'){

        this.$message({
          message: 'Submission allowed after 30 minutes into the exam',
          type: "warning",
        });
        return
      }

      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      // Relay mode shows team confirmations; individual mode confirms only the current stage.
      if (this.individualMode) {
        const remainMinutes = this.individualSubmitWaitRemainMin();
        if (remainMinutes > 0) {
          this.$message({
            message: `Submission is allowed 30 minutes after this stage starts. Please wait ${remainMinutes} more minute(s).`,
            type: "warning",
            duration: 5000,
            showClose: true
          });
          return;
        }
        if (!(await this.loadStageSubmitEmail())) return;
        this.stageSubmitConfirmationEmail = "";
        this.stageSubmitEmailTouched = false;
        this.showTeamSubmitDialog = true;
        this.queueExamInteraction("exam", "submit_dialog_opened", { source: "stage" });
        this.$nextTick(() => {
          const input = this.$refs.stageSubmitEmailInput;
          if (input && typeof input.focus === "function") input.focus();
        });
        return;
      } else if (this.relayMode) {
        this.loadTeamSubmissionStatus();
      }
      this.showTeamSubmitDialog = true;
      this.queueExamInteraction("exam", "submit_dialog_opened", { source: "team_exam" });
    },
    cancelSubmitDialog(source) {
      this.showSubmitAnswerDialog = false;
      this.showTeamSubmitDialog = false;
      this.stageSubmitConfirmationEmail = "";
      this.stageSubmitEmailTouched = false;
      this.queueExamInteraction("exam", "submit_cancelled", { source: source || "dialog" });
    },
    confirmExamUpload(source) {
      this.queueExamInteraction("exam", "submit_confirmed", { source: source || "manual" });
      this.uploadAnswers(source);
    },
    async onSubmitDialogOpen() {
      // Fetch team members when submit dialog opens (for relay mode)

      console.log(1111111)
      if (this.relayMode) {
        this.loadingTeamMembers = true;
        try {
          const res = await getRelayTeamMembers(
            this.user_id,
            this.template_id,
            this.project_code
          );
          if (res.data.code === 0) {
            this.teamMembers = res.data.data.members || [];
          }
        } catch (error) {
          console.error("Failed to fetch team members:", error);
        } finally {
          this.loadingTeamMembers = false;
        }
      }
      if (this.individualMode) {
        this.$nextTick(() => {
          const input = this.$refs.stageSubmitEmailInput;
          if (input && typeof input.focus === "function") input.focus();
        });
      }
    },
    handleClose() {
      this.messageDrawer = false;
    },
    showRedMessage() {
      getMyMessages(getUserId(), this.project_code).then((res) => {
        // console.log(1111,res)

        this.traversalData(res.data.data);
      });
      console.log(11);
    },
    traversalData(newData) {
      var haveNoReply = false;
      var newTip = false;

      for (var i = 0; i < newData.length; i++) {
        var message = newData[i];
        if (message.message_reply_id == "") {
          haveNoReply = true;
        }

        if (i < this.messageList.length) {
          var oldmessage = this.messageList[i];

          if (message.message_reply_id == oldmessage.message_reply_id) {
            console.log("是一致的");
          } else {
            console.log("是不一致的");
            newTip = true;
          }
        }
      }

      console.log("是否需要刷新", haveNoReply);
      console.log("是否需要提示新消息", newTip);

      if (haveNoReply == false) {
        clearInterval(this.timer);
      }

      if (newTip == true) {
        this.showRedTip = true;
      } else {
        this.showRedTip = false;
      }

      this.messageList = newData;
    },
    stripHtmlForSnapshot(value) {
      return String(value || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim();
    },
    buildSupportSnapshot() {
      const question = this.currentSelectedQuestion || {};
      const questionType = String(question.question_type || "");
      const isWritten = questionType === "input";
      const options = {};
      if (!isWritten) {
        ["A", "B", "C", "D", "E"].forEach((letter) => {
          const text = this.stripHtmlForSnapshot(question["question_option" + letter]);
          if (text) options[letter] = text;
        });
      }
      const materialContext = this.getCurrentMaterialDisplayContext();
      const questionNumber = Number(question.question_index) > 0
        ? Number(question.question_index)
        : Number(this.currentSelectedQuestionIndex) + 1;
      return {
        page: "exam",
        exam_name: this.test_event || (this.$route && this.$route.query && this.$route.query.exam_name) || "",
        template_name: this.test_subject || this.template_id || "",
        question_number: questionNumber,
        question_stem: this.stripHtmlForSnapshot(question.question_question).slice(0, 300),
        options,
        material_context: materialContext && materialContext.material_id
          ? materialContext.material_display
          : "",
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        client_time: new Date().toISOString(),
        client_time_ms: Date.now(),
        screen: {
          width: window.innerWidth || 0,
          height: window.innerHeight || 0,
        },
        online: navigator.onLine,
      };
    },
    showMessage() {
      this.queueExamInteraction("support", "message_opened");
      getMyMessages(getUserId(), this.project_code).then((res) => {
        // console.log(1111,res)
        this.messageList = res.data.data;
      });
      this.messageStr = "";
      this.messageDrawer = true;
      this.showRedTip = false;
      // clearInterval(this.timer);
    },
    getMyMessages() {
      getMyMessages(getUserId(), this.project_code).then((res) => {
        // console.log(1111,res)
        this.messageList = res.data.data;
      });
    },
    sendMessage() {
      if (this.messageType == "Message Types") {
        this.$message.error("Message Types");
        return;
      }
      if (this.messageStr == "") {
        this.$message.error("Please in your questions");
        return;
      }

      sendMessage(
        getUserId(),
        this.messageType,
        "student",
        this.messageStr,
        this.project_code,
        this.test_event,
        this.buildSupportSnapshot()
      ).then((res) => {
        this.queueExamInteraction("support", "message_sent");
        this.messageStr = "";
        this.messageType = "Message Types";

        this.$message({
          message: "Send message successfully",
          type: "success",
        });

        this.getMyMessages();
        // this.timer = setInterval(this.showRedMessage, 5000);
      });
    },
    showBigPic(path) {
      this.currentSelectedPicPath = "https://" + path;
      this.showBigPicDialog = true;
    },
    doSelectQuestion(index) {
      const previousIndex = this.currentSelectedQuestionIndex;
      const previousQuestion = this.currentSelectedQuestion;
      this.currentSelectedQuestionIndex = index;
      this.currentSelectedQuestion = this.questionList[index];
      this.persistCurrentQuestionPosition();
      this.refreshMath()
      this.showDrawer = false;
      this.resetViewPosition();
      this.queueExamNavigation(
        "question",
        "menu",
        previousIndex + 1,
        index + 1,
        {
          fromQuestionId: previousQuestion && previousQuestion.question_id,
          toQuestionId: this.currentSelectedQuestion && this.currentSelectedQuestion.question_id,
        }
      );
    },
    showExchangeTip() {
      this.$alert(
        "Screen switch monitoring is in place and will be reviewed by the invigilator. Any suspected cheating will be assessed manually based on your screen-sharing logs. If non-intentional switches occur, like pop-ups or accidental off-screen clicks, simply continue with the exam as usual.",
        "Screen Switches",
        {
          confirmButtonText: "OK",
          callback: (action) => {},
        }
      );
    },
    showBrowerTip() {
      this.$alert(
        "To ensure a smooth exam experience, please use the latest version of the Chrome browser. Other browsers may lead to unforeseen errors during the testing process.",
        "Browser",
        {
          confirmButtonText: "OK",
          callback: (action) => {},
        }
      );
    },
    //判断已经数量

    applyLiveScoreData(data) {
      if (data && data.current_score !== null && data.current_score !== undefined) {
        this.student_score = Number(data.current_score);
      }
    },

    loadAnswerNum(){
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }

      // Sync current section's answers back into sections data
      if (this.sections[this.currentSectionIndex]) {
        var sItems = this.sections[this.currentSectionIndex]['questions_item'];
        for (var qi = 0; qi < this.drawerQuestionList.length && qi < sItems.length; qi++) {
          if (this.drawerQuestionList[qi].choice && this.drawerQuestionList[qi].choice !== '') {
            sItems[qi]['user_answer'] = this.drawerQuestionList[qi].choice;
          }
        }
      }

      // Calculate progress across all sections. The live score itself is
      // calculated server-side so answer keys never need to reach the browser.
      var total = 0;
      var answered = 0;

      for (var si = 0; si < this.sections.length; si++) {
        var sectionQuestions = this.sections[si]['questions_item'];
        for (var q = 0; q < sectionQuestions.length; q++) {
          total++;
          var userAnswer = sectionQuestions[q]['user_answer'];
          if (userAnswer && userAnswer !== '') {
            answered++;
          }
        }
      }

      this.progess_num_total = total
      this.progess_num = answered
    },

    showMenu() {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      var choiceList = [];
      var noChoiceList = [];
      var temp = this.drawerQuestionList;
      var total = temp.length;
      var answered = 0;
      for (var i = 0; i < temp.length; i++) {
        var info = temp[i];
        var tempInfo = {
          question_question: info["question_question"],
          parentIndex: i,
        };
        if (info.choice !== "") {
          answered++;
          choiceList.push(tempInfo);
        } else {
          noChoiceList.push(tempInfo);
        }
      }
      this.drawerAnswerInfo = "Answered：" + answered + "/" + total;
      // drawerAnswerInfo
      this.drawerChoiceQuestionList = choiceList;
      this.drawerNoChoiceQuestionList = noChoiceList;
      this.showDrawer = true;
      this.queueExamInteraction("exam", "question_menu_opened");
    },
    showPreQuestion() {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      // this.resetViewPosition()
      const previousIndex = this.currentSelectedQuestionIndex;
      const previousQuestion = this.currentSelectedQuestion;
      var index = previousIndex;
      index--;
      if (index < 0) {
        this.$message({
          message: "This is the first question",
          type: "warning",
        });
        return;
      }
      this.currentSelectedQuestionIndex = index;
      this.currentSelectedQuestion = this.questionList[index];
      this.persistCurrentQuestionPosition();
      this.refreshMath()
      this.queueExamNavigation(
        "question",
        "previous",
        previousIndex + 1,
        index + 1,
        {
          fromQuestionId: previousQuestion && previousQuestion.question_id,
          toQuestionId: this.currentSelectedQuestion && this.currentSelectedQuestion.question_id,
        }
      );
    },

    //下一题

    isQuestionConfirmed(question) {
      if (!question || !question.question_id) return false;
      const savedAnswer = this.questionAnswers[question.question_id];
      return question.confirmed === true || Boolean(savedAnswer && savedAnswer.status === "yes");
    },
    advanceAfterConfirmedAnswer(currentIndex) {
      const previousQuestion = this.questionList[currentIndex];
      const nextIndex = currentIndex + 1;
      if (nextIndex >= this.examQuestionSize) {
        this.currentSectionCompleted = true;
        this.sectionProgress[this.currentSectionIndex] = {
          currentIndex: nextIndex,
          totalQuestions: this.examQuestionSize,
        };
        updateSectionProgress(
          this.user_id,
          this.template_id,
          this.project_code,
          this.currentSectionIndex,
          nextIndex
        );
        return;
      }

      this.currentSelectedQuestionIndex = nextIndex;
      this.currentSelectedQuestion = this.questionList[nextIndex];
      this.submitAnswrQuestionId = this.currentSelectedQuestion.question_id;
      this.submitAnswrQuestionChoice = this.currentSelectedQuestion.choice;
      this.submitAnswrQuestionImg_url = this.currentSelectedQuestion.answer_img_url || "";
      this.sectionProgress[this.currentSectionIndex] = {
        currentIndex: nextIndex,
        totalQuestions: this.examQuestionSize,
      };
      updateSectionProgress(
        this.user_id,
        this.template_id,
        this.project_code,
        this.currentSectionIndex,
        nextIndex
      );
      this.refreshMath();
      this.queueExamNavigation(
        "question",
        "next",
        currentIndex + 1,
        nextIndex + 1,
        {
          fromQuestionId: previousQuestion && previousQuestion.question_id,
          toQuestionId: this.currentSelectedQuestion && this.currentSelectedQuestion.question_id,
        }
      );
    },
    async showNextQuestion() {
      if (this.isConfirmingAnswer) return;
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      let index = this.currentSelectedQuestionIndex;
      const currentQu = this.drawerQuestionList[index];
      if (currentQu && currentQu.question_type === "input") {
        const writtenAnswer = String(currentQu.inputAnswer || currentQu.choice || "");
        this.$set(currentQu, "choice", writtenAnswer);
        if (this.questionAnswers[currentQu.question_id]) {
          this.questionAnswers[currentQu.question_id].choice = writtenAnswer;
        } else if (writtenAnswer !== "") {
          this.$set(this.questionAnswers, currentQu.question_id, {
            choice: writtenAnswer,
            status: "no",
          });
        }
      }
      if (!currentQu || currentQu.choice === "") {
        this.$message({
          message: "Please select",
          type: "warning",
        });
        return;
      }
      if (this.isQuestionConfirmed(currentQu)) {
        this.advanceAfterConfirmedAnswer(index);
        return;
      }

      const questionId = currentQu.question_id;
      const choice = currentQu.choice;
      const imageUrl = currentQu.question_type === "input" ? (currentQu.answer_img_url || "") : "";
      this.isConfirmingAnswer = true;
      try {
        const res = await submitAnswer(
          questionId,
          this.template_id,
          choice,
          this.user_id,
          this.ip,
          this.test_event,
          this.project_code,
          imageUrl,
          this.relayMode,
          true
        );
        if (!res.data) throw new Error("The answer could not be saved.");
        if (res.data.code !== 0) {
          const msg = res.data.msg || "The answer could not be saved.";
          if (/finished|submitted/i.test(msg)) {
            this.showRelayExamSubmittedDialog = true;
            this.stopRelayStatusCheck();
            await this.$alert(
              "The team exam paper has been submitted successfully. Please click Confirm to exit.",
              "Exam Submitted",
              {
                confirmButtonText: "Confirm",
                showClose: false,
                closeOnClickModal: false,
                closeOnPressEscape: false,
              }
            );
            this.forceShutDown = true;
            this.$router.replace({
              path: "/waiting-room",
              query: { template_id: this.template_id, exam_name: this.test_event },
            });
            return;
          }
          throw new Error(msg);
        }
        if (this.questionAnswers[questionId]) {
          this.questionAnswers[questionId].status = "yes";
        }
        this.$set(currentQu, "confirmed", true);
        currentQu._lastInteractionChoice = choice;
        this.applyLiveScoreData(res.data.data);
        this.loadAnswerNum();
        this.queueExamInteraction("answer", "answer_confirmed", {
          questionIndex: index + 1,
          questionId,
          currentChoice: choice,
          source: "click",
        });
        this.advanceAfterConfirmedAnswer(index);
      } catch (err) {
        this.handleApiNetworkError(err);
        if (!this.isNetworkError(err)) {
          this.$message({
            message: err.message || "The answer could not be saved. Please try again.",
            type: "error",
            duration: 5000,
            showClose: true,
          });
        }
      } finally {
        this.isConfirmingAnswer = false;
      }
    },
    showReload(){
      this.parentReloadPic++
      this.queueExamInteraction("display", "material_reloaded");
    },
    showPreImage() {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      if (this.$refs.pdfView.checkIsLoadingPdf()) {
        return;
      }
      this.resetViewPosition();
      const previousIndex = this.currentSelectedImageIndex;
      this.currentSelectedImageIndex = this.$refs.pdfView.prePage();
      this.queueExamNavigation(
        "material",
        "previous",
        previousIndex,
        this.currentSelectedImageIndex
      );
    },
    showNextImage() {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      if (this.$refs.pdfView.checkIsLoadingPdf()) {
        return;
      }
      this.resetViewPosition();
      const previousIndex = this.currentSelectedImageIndex;
      this.currentSelectedImageIndex = this.$refs.pdfView.nextPage();
      this.queueExamNavigation(
        "material",
        "next",
        previousIndex,
        this.currentSelectedImageIndex
      );
    },
    goBack() {
      if (this.forceShutDown) {
        this.$router.go(-1);
        return;
      }

      if (this.showAnswerResultClick) {
        this.$router.go(-1);
        return;
      }

      if (this.deviceStatus != 0 || !this.canExam) {
        this.$router.go(-1);
        return;
      }

      if (this.individualMode) {
        this.queueExamInteraction("exam", "leave_dialog_opened", { source: "go_back_individual" });
        this.$confirm(
          'Are you sure you want to leave? Saved answers will be kept, but the current stage timer and your 24-hour exam window will continue running.',
          'Warning',
          {
            confirmButtonText: 'Leave Exam',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        )
          .then(() => {
            this.queueExamInteraction("exam", "leave_confirmed", { source: "go_back_individual" });
            this.forceShutDown = true;
            this.$router.replace({ path: '/studentlist' });
          })
          .catch(() => {
            this.queueExamInteraction("exam", "leave_cancelled", { source: "go_back_individual" });
          });
      } else if (this.relayMode) {
        this.queueExamInteraction("exam", "leave_dialog_opened", { source: "go_back_relay" });
        this.$confirm(
          "Are you sure you want to leave? Your answers are saved. You can return to the exam from the waiting room.",
          "Warning",
          {
            confirmButtonText: "Leave Exam",
            cancelButtonText: "Cancel",
            type: "warning",
          }
        )
          .then(() => {
            this.queueExamInteraction("exam", "leave_confirmed", { source: "go_back_relay" });
            leaveRelayExam(this.user_id, this.template_id, this.project_code);
            this.$router.replace({
              path: "/waiting-room",
              query: {
                template_id: this.template_id,
                exam_name: this.test_event,
              },
            });
          })
          .catch(() => {
            this.queueExamInteraction("exam", "leave_cancelled", { source: "go_back_relay" });
          });
      } else {
        this.queueExamInteraction("exam", "leave_dialog_opened", { source: "go_back" });
        this.$confirm(
          "You are in the middle of a test. Leaving this page will automatically complete your exam. Are you sure you want to exit?",
          "Warning",
          {
            confirmButtonText: "Confirm to Exit",
            cancelButtonText: "Cancel",
            type: "warning",
          }
        )
          .then(() => {
            this.queueExamInteraction("exam", "leave_confirmed", { source: "go_back" });
            this.quizTrue();
          })
          .catch(() => {
            this.queueExamInteraction("exam", "leave_cancelled", { source: "go_back" });
          });
      }

      // this.$alert('确定要退出考试？', '提示', {
      //   confirmButtonText: '确定',
      //   callback: () => {
      //     this.$router.go(-1)
      //   }
      // });
    },
    fetchData(type) {
      this.questionAnswers = {};
      var student_id = getUserId();
      const fetchPromise = this.relayMode
        ? getExerciseTemplateByTeam(this.template_id, student_id, this.project_code)
        : getExerciseTemplateByStudent(this.template_id, student_id, this.project_code);
      return fetchPromise.then(async (res) => {
        if (!res.data || res.data.code !== 0 || !res.data.data) {
          throw new Error((res.data && res.data.msg) || 'Unable to load exam data');
        }
        this.test_subject = res.data.data.test_subject;
        this.test_type = res.data.data.test_type;

        this.initial_score = res.data.data.initial_score;
        this.keep_score_type = String(res.data.data.keep_score_type || 'post_exam').trim().toLowerCase();
        this.answer_deduction_points = res.data.data.answer_deduction_points || 'YES';
        this.student_score = Number(this.initial_score) || 0;
        this.applyLiveScoreData({ current_score: res.data.data.current_score });
        this.questionList = [];
        this.sections = res.data.data.sections_item;
        var sections = res.data.data.sections_item;

        // Initialize section progress for each section based on answered questions
        for (var si = 0; si < sections.length; si++) {
          var sectionQuestions = sections[si]['questions_item'];
          var firstUnanswered = -1;
          for (var qi = 0; qi < sectionQuestions.length; qi++) {
            var userAns = sectionQuestions[qi]['user_answer'];
            if (!userAns || userAns === '') {
              firstUnanswered = qi;
              break;
            }
          }
          const restoredProgress = this.sectionProgress[si];
          const restoredIndex = restoredProgress && Number(restoredProgress.currentIndex);
          const derivedIndex = firstUnanswered === -1 ? sectionQuestions.length : firstUnanswered;
          this.sectionProgress[si] = {
            // If all answered, currentIndex = total (marks section as completed)
            currentIndex: Number.isInteger(restoredIndex)
              ? Math.min(Math.max(0, restoredIndex), sectionQuestions.length)
              : derivedIndex,
            totalQuestions: sectionQuestions.length
          };
        }

        // Load only the current section's questions
        this.loadSectionQuestions(this.currentSectionIndex);

        if (this.currentSelectedQuestion) {
          this.submitAnswrQuestionId = this.currentSelectedQuestion.question_id;
          this.submitAnswrQuestionChoice = this.currentSelectedQuestion.choice;
        } else {
          this.submitAnswrQuestionId = '';
          this.submitAnswrQuestionChoice = '';
        }

        

        
        this.scheduleHeartBeat();


        //执行答题进度
        //取第一个字段为PDF文件地址
        const currentSection = this.sections[this.currentSectionIndex] || {};
        var urls = this.individualMode && Array.isArray(currentSection.pic_arr)
          ? currentSection.pic_arr
          : res.data.data.pic_arr;
        urls = Array.isArray(urls) ? urls : [];
        // var urls =[]
        // let url = 'https://mainbucket.learningfirst.cn/FBLA%E7%BB%8F%E6%B5%8E%E5%AD%A6%E7%BA%BF%E4%B8%8A%E8%80%83%E8%AF%95%E8%AE%BE%E5%A4%87%E8%A6%81%E6%B1%82.pdf'
        // urls.push(url)
        // urls.push(url)
        // urls.push(url)
        // urls.push(url)
        var newUrls = [];
        for (var j = 0; j < urls.length; j++) {
          var url = urls[j];
          if (url.indexOf("https://") != -1 || url.indexOf("http://") != -1) {
            newUrls.push(url);
          } else {
            newUrls.push("https://" + url);
          }
        }

        if (urls.length <= 0){
          this.havePicUrl = false
        }else{
          this.havePicUrl = true
        }

        if (type != 2){
          await this.$nextTick();
          if (this.havePicUrl) {
            await this.asyncIntPDF(newUrls);
          }
          this.loadAnswerNum();
        }

        // Exam data loaded successfully - hide loading overlay
        this.examDataLoading = false;

        // Confirm relay entry count now that data loaded successfully
        if (this.relayMode) {
          confirmRelayEntry(this.user_id, this.template_id, this.project_code);
        }

        // this.asyncIntPDF(newUrls);
      }).catch((error) => {
        this.examDataLoading = false;
        this.handleApiNetworkError(error);
        if (!this.isNetworkError(error)) {
          this.$message.error(error.message || 'Unable to load exam data');
        }
        throw error;
      });
    },
    async asyncIntPDF(urls) {
      this.$refs["pdfView"] && (await this.$refs["pdfView"].initPdfView(urls));
    },
    startCountDown(seconds) {
      if (this.countDownThread) {
        return;
      }
      const totalSeconds = Math.floor(Number(seconds));
      if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        this.examCountDownDeadline = 0;
        this.countDownTimes = "00:00:00";
        this.remainStamp = 0;
        this.handleExamCountDownExpiry();
        return;
      }

      this.examCountDownDeadline = this.monotonicNow() + totalSeconds * 1000;
      this.countDownThread = window.setInterval(() => {
        this.updateExamCountDown();
      }, 1000);
      this.updateExamCountDown();
    },
    updateExamCountDown() {
      if (!this.examCountDownDeadline) {
        return;
      }
      const remaining = Math.max(
        0,
        Math.ceil(
          (this.examCountDownDeadline - this.monotonicNow()) / 1000
        )
      );
      this.countDownTimes = this.formatSeconds(remaining);
      this.remainStamp = remaining;
      if (remaining <= 0) {
        if (this.countDownThread) {
          window.clearInterval(this.countDownThread);
          this.countDownThread = null;
        }
        this.examCountDownDeadline = 0;
        this.handleExamCountDownExpiry();
      }
    },
    handleExamCountDownExpiry() {
      if (this.individualMode) {
        this.handleIndividualModuleTimeUp();
        return;
      }
      this.verifyAndHandleExamTimeout();
    },
    async verifyAndHandleExamTimeout() {
      if (this.countDownTimeoutHandling || this.forceShutDown) {
        return;
      }
      this.countDownTimeoutHandling = true;
      try {
        const res = await studentSignWithTemplate(
          this.user_id,
          this.template_id,
          this.project_code
        );
        const result = res && res.data ? res.data.data : null;
        if (!result || !Number.isFinite(Number(result.server_time))) {
          throw new Error("Unable to confirm the exam end time");
        }

        let endTime = Number(result.end_time);
        if (result.repair_sign && result.repair_sign.repair_end_time) {
          endTime = Number(result.repair_sign.repair_end_time);
        }
        if (!Number.isFinite(endTime) || endTime <= 0) {
          throw new Error("The exam server returned an invalid end time");
        }
        this.setServerClockAnchor(result.server_time);
        const remaining = Math.floor(endTime - this.estimatedServerNow());

        if (Number.isFinite(remaining) && remaining > 0) {
          this.startCountDown(remaining);
          return;
        }

        await this.$alert(
          "The exam has ended. Your exam paper has been submitted automatically.",
          "Exam Time Up",
          {
            confirmButtonText: "Confirm",
            closeOnClickModal: false,
            closeOnPressEscape: false,
            showClose: false,
          }
        );
        this.uploadAnswers("timer_expired");
      } catch (error) {
        console.error("Unable to verify exam timeout", error);
        this.$message({
          message: "The exam server could not confirm that time has expired. Your exam was not submitted. Keep this page open while the system retries.",
          type: "warning",
          duration: 10000,
          showClose: true,
        });
        if (!this.countDownRetryTimer) {
          this.countDownRetryTimer = window.setTimeout(() => {
            this.countDownRetryTimer = null;
            this.verifyAndHandleExamTimeout();
          }, 5000);
        }
      } finally {
        this.countDownTimeoutHandling = false;
      }
    },
    startWindowCountDown(windowEndTime, serverTime) {
      if (!this.individualMode || !windowEndTime) return;
      if (this.windowCountDownThread) {
        window.clearInterval(this.windowCountDownThread);
      }
      this.individualServerOffset = Date.now() / 1000 - parseInt(serverTime || Date.now() / 1000, 10);
      const updateWindow = () => {
        const serverNow = Date.now() / 1000 - this.individualServerOffset;
        const remaining = Math.max(0, Math.floor(parseInt(windowEndTime, 10) - serverNow));
        this.windowCountDownTimes = this.formatSeconds(remaining);
        if (remaining <= 0) {
          window.clearInterval(this.windowCountDownThread);
          this.windowCountDownThread = null;
          this.handleIndividualWindowExpired();
        }
        return remaining;
      };
      if (updateWindow() > 0) {
        this.windowCountDownThread = window.setInterval(updateWindow, 1000);
      }
    },
    handleIndividualModuleTimeUp() {
      if (this.individualTimeUpHandled || this.submittingIndividualSection) return;
      this.individualTimeUpHandled = true;
      this.$alert(
        'The 2-hour time limit for this stage has ended. This stage is now final. Your next stage will start when you enter it from the exam list.',
        'Stage Time Up',
        {
          confirmButtonText: 'Exit to Exam List',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false,
          callback: () => this.advanceIndividualSection('time_expired', false, false),
        }
      );
    },
    handleIndividualWindowExpired() {
      if (!this.individualMode || this.individualTimeUpHandled || this.submittingIndividualSection) return;
      this.individualTimeUpHandled = true;
      this.$alert(
        'Your 24-hour exam window has ended. The exam is now closed.',
        'Exam Window Closed',
        {
          confirmButtonText: 'Exit',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false,
          callback: () => this.advanceIndividualSection('window_expired', false, false),
        }
      );
    },
    async flushPendingIndividualAnswers() {
      const pendingIds = Object.keys(this.questionAnswers).filter(
        questionId => this.questionAnswers[questionId].status === 'no'
      );
      for (const questionId of pendingIds) {
        const question = this.questionList.find(item => item.question_id === questionId);
        const answer = this.questionAnswers[questionId];
        const response = await submitAnswer(
          questionId,
          this.template_id,
          answer.choice,
          this.user_id,
          this.ip,
          this.test_event,
          this.project_code,
          question && question.answer_img_url ? question.answer_img_url : '',
          false,
          true
        );
        if (!response.data || response.data.code !== 0) {
          const error = new Error((response.data && response.data.msg) || 'Unable to save an answer');
          error.responseData = response.data;
          throw error;
        }
        this.applyLiveScoreData(response.data.data);
        this.questionAnswers[questionId].status = 'yes';
      }
    },
    async confirmIndividualSectionSubmit(startNext) {
      this.stageSubmitEmailTouched = true;
      if (!this.isStageSubmitEmailMatch || this.submittingIndividualSection) return;
      this.queueExamInteraction("exam", "submit_confirmed", {
        source: startNext ? "stage_start_next" : "stage_exit",
      });
      await this.advanceIndividualSection(
        'manual_submit',
        true,
        startNext,
        this.normalizedStageSubmitEmail
      );
    },
    async waitForIndividualExamFinalization(initialFinalization) {
      const initialStatus = initialFinalization && initialFinalization.status;
      if (initialStatus === 'success' || (initialFinalization && initialFinalization.finalized)) {
        return true;
      }

      for (let attempt = 0; attempt < 90; attempt++) {
        try {
          const response = await checkAMIndividualExamSubmitStatus(
            this.user_id,
            this.template_id,
            this.project_code
          );
          if (response.data && response.data.code === 0 && response.data.data) {
            const status = response.data.data.status;
            if (status === 'success' && response.data.data.finalized) {
              return true;
            }
            if (status === 'failed') {
              const finalizationError = new Error(
                response.data.data.error || 'The exam finalization failed after automatic retries.'
              );
              finalizationError.finalizationFailed = true;
              throw finalizationError;
            }
          }
        } catch (error) {
          if (error && error.finalizationFailed) {
            throw error;
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 1000));
      }
      return false;
    },
    async advanceIndividualSection(reason, flushPending, startNext = false, confirmationEmail = "") {
      if (!this.individualMode || this.submittingIndividualSection) return;
      this.submittingIndividualSection = true;
      try {
        if (flushPending) {
          await this.flushPendingIndividualAnswers();
        }
        await Promise.all([
          this.flushProctorPenaltyEvents(),
          this.waitForNetworkDisconnectLogs(),
        ]);
        await this.refreshPenaltySummary();
        const completedIndex = this.currentSectionIndex;
        const response = await completeIndividualExamSection(
          this.user_id,
          this.template_id,
          this.project_code,
          completedIndex,
          reason,
          startNext,
          confirmationEmail
        );
        if (!response.data || response.data.code !== 0) {
          throw new Error((response.data && response.data.msg) || 'Unable to complete this stage');
        }

        this.showTeamSubmitDialog = false;
        this.individualExam = response.data.data;
        if (this.countDownThread) {
          window.clearInterval(this.countDownThread);
          this.countDownThread = null;
        }
        this.examCountDownDeadline = 0;

        if (this.individualExam.status === 'in_progress') {
          this.currentSectionIndex = parseInt(this.individualExam.current_section_index, 10);
          if (!startNext) {
            this.forceShutDown = true;
            this.$message.success(
              `Stage ${completedIndex + 1} is final. Stage ${this.currentSectionIndex + 1} will start when you enter it from the exam list.`
            );
            this.$router.replace({ path: '/studentlist' });
            return;
          }
          this.individualTimeUpHandled = false;
          this.currentSectionCompleted = false;
          this.examDataLoading = true;
          await this.fetchData(1);
          const remaining = Math.max(
            0,
            parseInt(this.individualExam.current_section_end_time, 10)
              - Math.floor(Date.now() / 1000 - this.individualServerOffset)
          );
          this.startCountDown(remaining);
          this.$message.success(`Stage ${completedIndex + 1} is final. Stage ${this.currentSectionIndex + 1} has started.`);
        } else {
          this.forceShutDown = true;
          const finalized = await this.waitForIndividualExamFinalization(this.individualExam.finalization);
          if (finalized) {
            this.$message.success(
              this.individualExam.status === 'expired'
                ? 'The 24-hour exam window has closed and your exam has been submitted.'
                : 'All stages have been submitted successfully.'
            );
          } else {
            this.$message.warning('All stages are final. Your exam is still being processed safely in the background.');
          }
          this.$router.replace({ path: '/studentlist' });
        }
      } catch (error) {
        this.handleApiNetworkError(error);
        this.$message.error(error.message || 'Unable to complete this stage');
      } finally {
        this.submittingIndividualSection = false;
      }
    },
    saveAnswer(choice, item) {
      if (!this.checkDeviceCanWork()) {
        return;
      }
      if (!this.checkCanExam()) {
        return;
      }
      if (this.isQuestionConfirmed(item) || this.isConfirmingAnswer) {
        this.$message.warning("This answer has already been confirmed and cannot be changed.");
        return;
      }
      const previousChoice = item._lastInteractionChoice || "";
      const normalizedChoice = choice == null ? "" : String(choice);
      this.submitAnswrQuestionId = item.question_id;

      this.submitAnswrQuestionChoice = normalizedChoice;

      let question_id = item.question_id;
      let index = this.currentSelectedQuestionIndex;
      this.questionAnswers[question_id] = { choice: normalizedChoice, status: "no" };
      this.$set(item, "choice", normalizedChoice);
      this.$set(this.drawerQuestionList[index], "choice", normalizedChoice);

      if (item.question_type === "input") {
        this.queueExamInteraction(
          "answer",
          normalizedChoice === ""
            ? "written_answer_cleared"
            : (previousChoice === "" ? "written_answer_saved" : "written_answer_changed"),
          {
            questionIndex: index + 1,
            questionId: question_id,
            answerLength: normalizedChoice.length,
            source: "autosave",
          }
        );
      } else if (normalizedChoice !== previousChoice) {
        this.queueExamInteraction(
          "answer",
          normalizedChoice === ""
            ? "choice_cleared"
            : (previousChoice === "" ? "choice_selected" : "choice_changed"),
          {
            questionIndex: index + 1,
            questionId: question_id,
            previousChoice,
            currentChoice: normalizedChoice,
            source: "click",
          }
        );
      }
      item._lastInteractionChoice = normalizedChoice;

      let img_url = '';

      if (item.question_type == 'input'){

        if (item.answer_img_url != ''){
          img_url = item.answer_img_url;
        }
      }

      this.submitAnswrQuestionImg_url = img_url

      //放到 提交里面去
      // submitAnswer(
      //   question_id,
      //   this.template_id,
      //   choice,
      //   this.user_id,
      //   this.ip,
      //   this.test_event,
      //   getProjectCode(),
      //   img_url
      // ).then((res) => {
      //   if (res.data.code == 0) {
      //     this.questionAnswers[question_id]["status"] = "yes";
      //   } else {
      //     this.$message({
      //       message: res.data.msg,
      //       type: "error",
      //       duration: 5000,
      //       showClose: true,
      //     });
      //   }
      // });
    },
    async uploadAnswers(submit_source) {
      await Promise.all([
        this.flushProctorPenaltyEvents(),
        this.waitForNetworkDisconnectLogs(),
      ]);
      await this.refreshPenaltySummary();

      let temp_no_answers = {};
      var questionIds = Object.keys(this.questionAnswers);
      questionIds.forEach((key) => {
        var question = this.questionAnswers[key];
        if (question["status"] == "no") {
          temp_no_answers[key] = question["choice"];
        }
      });
      // let answerList = this.questionList.map(item => item.choice);
      var project_code = this.project_code;

      // If in relay mode, leave the relay exam before submitting
      if (this.relayMode) {
        leaveRelayExam(this.user_id, this.template_id, project_code);
      }

      // Close the submit dialog
      this.showSubmitAnswerDialog = false;

      uploadAnswersV3(
        project_code,
        this.user_id,
        this.template_id,
        this.app_id,
        this.ip,
        this.test_event,
        this.relayMode ? "team" : "personal",
        temp_no_answers,
        submit_source || 'unknown',
        this.student_score
      ).then((res) => {
        if (this.currentAudio) {
          this.currentAudio.pause();
        }
        if (!res.data) return;
        if (res.data.code === 0) {
          this.$message({
            message: "Your submission has been recorded.",
            type: "success",
            duration: 3000,
            showClose: true,
          });
          this.forceShutDown = true;
          // Navigate to waiting room for relay mode, otherwise go back
          if (this.relayMode) {
            this.$router.replace({
              path: "/waiting-room",
              query: {
                template_id: this.template_id,
                exam_name: this.test_event
              }
            });
          } else {
            this.$router.go(-1);
          }
        } else {
          this.$message({
            message: res.data.msg,
            type: "error",
            duration: 5000,
            showClose: true,
          });
          this.forceShutDown = true;
          if (this.relayMode) {
            this.$router.replace({
              path: "/waiting-room",
              query: {
                template_id: this.template_id,
                exam_name: this.test_event
              }
            });
          } else {
            this.$router.go(-1);
          }
        }
      }).catch((err) => {
        this.handleApiNetworkError(err);
      });
    },
    showAwardDialog(data) {
      this.answerResultDta = data;
      this.showAnswerResultDialog = true;
    },
    formatSeconds(endTime) {
      let secondTime = parseInt(endTime);
      let min = 0;
      let h = 0;
      let result = "";
      if (secondTime > 60) {
        min = parseInt(secondTime / 60);
        secondTime = parseInt(secondTime % 60);
        if (min > 60) {
          h = parseInt(min / 60);
          min = parseInt(min % 60);
        }
      }
      result = `${h.toString().padStart(2, "0")}:${min
        .toString()
        .padStart(2, "0")}:${secondTime.toString().padStart(2, "0")}`;
      return result;
    },
    closeView() {
      this.showAnswerResultClick = true;
      this.$router.go(-1);
    },
    checkCanExam() {
      if (this.DEBUG_DEV) {
        return true;
      }
      if (this.canExam) {
        return true;
      }
      this.$message.error(
        "The system detects your camera and screen sharing is disrupted. You can not continue under this condition."
      );
      return false;
    },
    checkDeviceCanWork() {
      // if(true){
      //   return true
      // }
      if (this.deviceStatus == 0) {
        return true;
      }
      if (this.deviceStatus == 1) {
        this.$message.error(`Your browser does not support screen sharing`);
        return false;
      }
      if (this.deviceStatus == 2) {
        this.$message.error(`Your browser does not support screen sharing`);
        return false;
      }
      if (this.deviceStatus == 3) {
        this.$message.error(`Camera sharing authorization failed`);
        return false;
      }
      if (this.deviceStatus == 4) {
        this.$message.error(`Screen sharing authorization failed`);
        return false;
      }
      return true;
    },
    resetViewPosition() {
      // Programmatic scroll-to-top on page turns must not be logged as a
      // candidate scroll (cheating analysis reads material_scrolled_*).
      this.flushMaterialScrollLog();
      this.materialScrollSuppressUntil = Date.now() + 400;
      var exam_image = document.getElementById("exam-image");
      if (!exam_image) return;
      var scrollTop = exam_image.scrollTop;
      scroll(scrollTop, 0, exam_image);
    },
    clearCountDown() {
      this.doUploadHeartBeat(false);
      if (this.heartBeatTimer) {
        window.clearInterval(this.heartBeatTimer);
        this.heartBeatTimer = null;
      }
    },
    refreshVisible() {
      getTestVisibleChangeCount(
        this.template_id,
        getUserId(),
        this.project_code
      ).then((res) => {
        let count = res.data.data;
        this.screenChangeCount = count;
      });
    },
    scheduleHeartBeat() {
      this.heartBeatTimer = window.setInterval(() => {
        this.doUploadHeartBeat(true);
      }, 30000);
    },
    doUploadHeartBeat(status) {
      uploadTestHeartBeat(getUserId(), this.template_id, status).then(
        (res) => {}
      );
    },
    invigilateCallback() {},
    onCorrectSessionPicSync(e) {
      for (let index in this.sections) {
        if (this.sections[index]._id.$id === e._id.$id) {
          this.sections[index].pic_arr = e.pic_arr;
          if (Number(index) === this.sectionSelect) {
            this.exam_images = e.pic_arr.map((item) => "https://" + item);
          }
        }
      }
    },
    onCorrectQuestionSync(e) {
      let question_id = e._id.$id;
      //循环所有的题目 如果找到该题目id就修改答案并跳出当前循环
      findQuestion: for (let i in this.sections) {
        let section = this.sections[i];
        for (let j in section["questions_item"]) {
          let question = section["questions_item"][j];
          if (question_id === question["_id"]["$id"]) {
            question["question_answer"] = e.question_answer;
            question["question_optionA"] = e.question_optionA;
            question["question_optionA_string"] = e.question_optionA_string;
            question["question_optionB"] = e.question_optionB;
            question["question_optionB_string"] = e.question_optionB_string;
            question["question_optionC"] = e.question_optionC;
            question["question_optionC_string"] = e.question_optionC_string;

            question["question_optionD"] = e.question_optionD;
            question["question_optionD_string"] = e.question_optionD_string;
            question["question_question"] = e.question_question;
            question["question_question_string"] = e.question_question_string;

            let questionIndex = this.questionList.findIndex(
              (x) => x.question_id === question_id
            );
            if (questionIndex !== -1) {
              this.questionList[questionIndex]["question_answer"] =
                e.question_answer;
              this.questionList[questionIndex]["question_optionA"] =
                e.question_optionA;
              this.questionList[questionIndex]["question_optionA_string"] =
                e.question_optionA_string;
              this.questionList[questionIndex]["question_optionB"] =
                e.question_optionB;
              this.questionList[questionIndex]["question_optionB_string"] =
                e.question_optionB_string;
              this.questionList[questionIndex]["question_optionC"] =
                e.question_optionC;
              this.questionList[questionIndex]["question_optionC_string"] =
                e.question_optionC_string;
              this.questionList[questionIndex]["question_optionD"] =
                e.question_optionD;
              this.questionList[questionIndex]["question_optionD_string"] =
                e.question_optionD_string;
              this.questionList[questionIndex]["question_question"] =
                e.question_question;
              this.questionList[questionIndex]["question_question_string"] =
                e.question_question_string;
            }

            break findQuestion;
          }
        }
      }
    },
    refreshMath(target) {
      this.examIntegrityMathJobs += 1;
      this.resetExamIntegrityRiskCandidate();
      let finished = false;
      const finishMathJob = () => {
        if (finished) return;
        finished = true;
        this.examIntegrityMathJobs = Math.max(0, this.examIntegrityMathJobs - 1);
        if (this.examIntegrityMathJobs === 0) {
          this.scheduleExamIntegrityCheck(500);
        }
      };
      this.$nextTick(() => {
        const mathJax = window.MathJax;
        if (mathJax && mathJax.Hub && mathJax.Hub.Queue) {
          mathJax.Hub.Queue([
            "Typeset",
            mathJax.Hub,
            target || this.$refs.examRoot || "app"
          ], finishMathJob);
          return;
        }
        finishMathJob();
      });
    },

    // Section-based navigation methods
    switchSection(sectionIndex) {
      if (sectionIndex === this.currentSectionIndex) return;

      if (this.individualMode) {
        const status = this.getIndividualSectionStatus(sectionIndex);
        this.$message({
          message: status === 'locked'
            ? 'This stage is locked. Submit or finish the current stage first.'
            : 'Submitted and expired stages cannot be reopened.',
          type: 'warning',
          duration: 2500
        });
        return;
      }

      const previousSection = this.currentSectionIndex;

      // Save current section progress
      this.saveSectionProgress();

      this.currentSectionIndex = sectionIndex;

      // Initialize section progress if not exists
      if (!this.sectionProgress[sectionIndex]) {
        this.sectionProgress[sectionIndex] = {
          currentIndex: 0,
          totalQuestions: this.sections[sectionIndex].questions_item.length
        };
      }

      // Load questions for the new section and set to saved progress
      this.loadSectionQuestions(sectionIndex);

      this.queueExamNavigation(
        "question",
        "section",
        previousSection + 1,
        sectionIndex + 1
      );

      this.$message({
        message: `Switched to Station ${sectionIndex + 1}`,
        type: 'success',
        duration: 2000
      });
    },

    loadSectionQuestions(sectionIndex) {
      const section = this.sections[sectionIndex];
      if (!section) return;

      this.questionList = [];
      const questions_items = Array.isArray(section['questions_item']) ? section['questions_item'] : [];

      for (let j = 0; j < questions_items.length; j++) {
        let pos = j + 1;
        if (pos < 10) {
          pos = '0' + pos;
        }
        const questionId = questions_items[j]['_id']['$id'];
        const existingAnswer = this.questionAnswers[questionId];
        const storedChoice = existingAnswer
          ? String(existingAnswer.choice || '')
          : String(questions_items[j]['user_answer'] || '');
        const confirmed = existingAnswer
          ? existingAnswer.status === 'yes'
          : questions_items[j]['answer_confirmed'] === true;
        const question = {
          question_id: questionId,
          question_index: pos,
          question_question: questions_items[j]['question_question'],
          question_question_string: questions_items[j]['question_question_string'],
          question_optionA: questions_items[j]['question_optionA'],
          question_optionA_string: questions_items[j]['question_optionA_string'],
          question_optionB: questions_items[j]['question_optionB'],
          question_optionB_string: questions_items[j]['question_optionB_string'],
          question_optionC: questions_items[j]['question_optionC'],
          question_optionC_string: questions_items[j]['question_optionC_string'],
          question_optionD: questions_items[j]['question_optionD'],
          question_optionD_string: questions_items[j]['question_optionD_string'],
          question_optionE: questions_items[j]['question_optionE'],
          question_optionE_string: questions_items[j]['question_optionE_string'],
          choice: storedChoice,
          _lastInteractionChoice: storedChoice,
          inputAnswer: storedChoice,
          confirmed,
          question_answer: questions_items[j]['question_answer'],
          question_type: questions_items[j]['question_type'],
          answer_img_url: questions_items[j]['user_answer_pic_url'],
          question_marks: parseFloat(questions_items[j]['question_marks'])
        };
        if (confirmed && !existingAnswer) {
          this.$set(this.questionAnswers, questionId, {
            choice: storedChoice,
            status: 'yes',
          });
        }
        this.questionList.push(question);
      }

      this.drawerQuestionList = this.questionList;
      this.examQuestionSize = this.questionList.length;

      // Calculate the correct position: find first unanswered question
      let firstUnansweredIdx = -1;
      for (let k = 0; k < this.questionList.length; k++) {
        if (!this.isQuestionConfirmed(this.questionList[k])) {
          firstUnansweredIdx = k;
          break;
        }
      }

      const savedProgress = this.sectionProgress[sectionIndex];
      const savedIndex = savedProgress && Number(savedProgress.currentIndex);

      if (this.questionList.length === 0) {
        this.currentSectionCompleted = true;
        this.currentSelectedQuestionIndex = 0;
        this.currentSelectedQuestion = null;
      } else if (firstUnansweredIdx === -1) {
        // All questions answered - show completion state
        this.currentSectionCompleted = true;
        this.currentSelectedQuestionIndex = this.questionList.length - 1;
        this.currentSelectedQuestion = this.questionList[this.questionList.length - 1];
      } else {
        this.currentSectionCompleted = false;
        const resumeIndex = Number.isInteger(savedIndex)
          ? Math.min(Math.max(0, savedIndex), this.questionList.length - 1)
          : firstUnansweredIdx;
        this.currentSelectedQuestionIndex = resumeIndex;
        this.currentSelectedQuestion = this.questionList[resumeIndex];
      }
      this.refreshMath();
    },

    getIndividualSectionStatus(sectionIndex) {
      if (!this.individualExam || !Array.isArray(this.individualExam.sections)) {
        return sectionIndex === this.currentSectionIndex ? 'in_progress' : 'locked';
      }
      const section = this.individualExam.sections[sectionIndex];
      return section && section.status ? section.status : 'locked';
    },

    saveSectionProgress() {
      if (this.currentSectionIndex !== undefined) {
        this.sectionProgress[this.currentSectionIndex] = {
          currentIndex: this.currentSelectedQuestionIndex,
          totalQuestions: this.examQuestionSize
        };

        // Save to backend
        updateSectionProgress(
          this.user_id,
          this.template_id,
          this.project_code,
          this.currentSectionIndex,
          this.currentSelectedQuestionIndex
        );
      }
    },

    persistCurrentQuestionPosition() {
      if (!this.individualMode || this.currentSectionIndex === undefined) return;
      const questionIndex = Math.max(0, Number(this.currentSelectedQuestionIndex) || 0);
      this.$set(this.sectionProgress, this.currentSectionIndex, {
        currentIndex: questionIndex,
        totalQuestions: this.examQuestionSize
      });
      updateSectionProgress(
        this.user_id,
        this.template_id,
        this.project_code,
        this.currentSectionIndex,
        questionIndex
      ).catch((error) => {
        console.warn("Unable to save current stage progress", error);
      });
    },

    getCurrentSectionQuestionIndex() {
      return this.currentSelectedQuestionIndex;
    },

    getCurrentSectionQuestionCount() {
      return this.examQuestionSize;
    },

    // Team Chat methods
    handleTeamChatClose() {
      this.showTeamChatDrawer = false;
    },

    sendTeamChatMessage() {
      if (!this.teamChatInput.trim()) return;

      const message = {
        type: 'teamChat',
        sender_id: this.user_id,
        sender_name: this.student_name,
        content: this.teamChatInput,
        time: Date.now(),
        group_id: this.group_id
      };

      // Send via WebSocket
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(`teamChat&${JSON.stringify(message)}`);
      }

      // Add to local messages
      this.teamChatMessages.push(message);
      this.teamChatInput = '';

      // Scroll to bottom
      this.$nextTick(() => {
        const container = this.$refs.teamChatMessagesRef;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    formatChatTime(timestamp) {
      const date = new Date(timestamp);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    },

    loadTeamMembers() {
      getTeamMembersWithId(this.project_code, this.user_id).then((res) => {
        if (res.data && res.data.data) {
          this.teamMembers = Array.isArray(res.data.data) ? res.data.data : (res.data.data.members || []);
        }
      });
    },

    // Team submission methods
    loadTeamSubmissionStatus() {
      getTeamSubmissionStatus(this.project_code, this.template_id, this.group_id).then((res) => {
        if (res.data && res.data.data) {
          this.teamSubmissionStatus = res.data.data.status || {};
          this.teamSubmissionCount = res.data.data.count || 0;
        }
      });
    },
    backWaitingRoom() {


      this.$confirm("Confirm that the submission has been made. Return Waiting Room", "Warning", {
        confirmButtonText: "Confirm to Waiting Room",
        cancelButtonText: "Cancel",
        type: "warning",
      })
        .then(() => {
          this.$router.replace({
            path: "/waiting-room",
            query: {
              template_id: this.template_id,
              exam_name: this.test_event,
            },
          });
        })
        .catch(() => {});


    },
    confirmTeamSubmit() {
      this.queueExamInteraction("exam", "submit_confirmed", { source: "team_confirmation" });
      confirmTeamSubmission(
        this.project_code,
        this.template_id,
        this.user_id,
        this.group_id
      ).then((res) => {
        if (res.data && res.data.code === 0) {
          this.$message({
            message: 'Your confirmation has been recorded',
            type: 'success'
          });

          this.teamSubmissionStatus[this.user_id] = true;
          this.teamSubmissionCount++;

          // Notify other team members via socket
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(`teamSubmit&${JSON.stringify({
              user_id: this.user_id,
              user_name: this.student_name,
              group_id: this.group_id,
              template_id: this.template_id
            })}`);
          }

          // Check if 3 members have confirmed
          if (this.teamSubmissionCount >= 3) {
            this.showTeamSubmitDialog = false;
            this.uploadAnswers('team_confirm_3_members');
          }else{
            this.$router.replace({
              path: "/waiting-room",
              query: {
                template_id: this.template_id,
                exam_name: this.test_event,
              },
            });
          }

          



        } else {
          this.$message({
            message: res.data.msg || 'Failed to confirm submission',
            type: 'error'
          });
        }
      });
    },

    handleTeamSubmitNotification(data) {
      // Update local submission status when notified by socket
      if (data.user_id && data.group_id === this.group_id) {
        this.teamSubmissionStatus[data.user_id] = true;
        this.teamSubmissionCount++;

        this.$message({
          message: `${data.user_name} has confirmed submission`,
          type: 'info',
          duration: 3000
        });

        // Auto-submit if 3 members confirmed
        if (this.teamSubmissionCount >= 3) {
          this.$alert('3 team members have confirmed. The exam will be submitted now.', 'Team Submission', {
            confirmButtonText: 'OK',
            callback: () => {
              this.uploadAnswers('socket_team_confirm_3_members');
            }
          });
        }
      }
    },

    handleTeamChatNotification(data) {
      // Add received message to chat if from same group
      if (data.group_id === this.group_id && data.sender_id !== this.user_id) {
        this.teamChatMessages.push(data);

        // Scroll to bottom if drawer is open
        if (this.showTeamChatDrawer) {
          this.$nextTick(() => {
            const container = this.$refs.teamChatMessagesRef;
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          });
        }
      }
    },

    onGetWsOut(ws) {
      this.ws = ws;
    },

    handleOnlineStatusUpdate(data) {
      // Legacy - online status now derived from WebSocket onlineMembers
    },
    // Relay mode WebSocket methods
    connectRelayWebSocket() {
      const wsUrl = "wss://team-chat.learningfirst.cn:9000";
      this.relayWs = new WebSocket(wsUrl);

      this.relayWs.onopen = () => {
        console.log("Relay WebSocket connected (exam)");
        const groupId = `relay_${this.template_id}_${this.project_code}`;
        this.relayWs.send(`user&${groupId}&${this.user_id}&exam`);
        setTimeout(() => this.refreshOnlineList(), 500);
      };

      this.relayWs.onmessage = (event) => {
        const data = event.data;
        if (data instanceof Blob) {
          data.text().then(text => this.handleRelayWebSocketMessage(text));
        } else {
          this.handleRelayWebSocketMessage(data);
        }
      };

      this.relayWs.onclose = () => {
        console.log("Relay WebSocket disconnected");
        setTimeout(() => {
          if (!this._isBeforeDestroy) {
            this.connectRelayWebSocket();
          }
        }, 3000);
      };

      this.relayWs.onerror = (error) => {
        console.error("Relay WebSocket error:", error);
      };
    },

    sendRelayWsMessage(type, payload) {
      if (this.relayWs && this.relayWs.readyState === WebSocket.OPEN) {
        this.relayWs.send(`${type}&${JSON.stringify(payload)}`);
      }
    },

    disconnectRelayWebSocket() {
      this._isBeforeDestroy = true;
      if (this.relayWs) {
        this.relayWs.close();
        this.relayWs = null;
      }
    },

    refreshOnlineList() {
      if (this.relayWs && this.relayWs.readyState === WebSocket.OPEN) {
        this.relayWs.send('relay_get_online');
      }
    },

    handleRelayWebSocketMessage(data) {
      try {
        const str = typeof data === 'string' ? data : data.toString();
        if (str.indexOf("relay_online_list&") === 0) {
          this.onlineMembers = JSON.parse(str.substring("relay_online_list&".length));
        } else if (str.indexOf("relay_exam_takeover&") === 0) {
          const takeoverData = JSON.parse(str.substring("relay_exam_takeover&".length));
          if (takeoverData.kicked_user_id === this.user_id) {
            this.stopRelayStatusCheck();
            this.relayKickedByName = takeoverData.new_taker_name;
            this.showRelayKickedDialog = true;
          }
        } else if (str.indexOf("relay_chat&") === 0) {
          const msgData = JSON.parse(str.substring("relay_chat&".length));
          if (msgData.sender_id !== this.user_id) {
            this.teamChatMessages.push(msgData);
          }
        } else if (str.indexOf("relay_member_update&") === 0) {
          try {
            const updateData = JSON.parse(str.substring("relay_member_update&".length));
            if (updateData.reason === 'connect' && updateData.user_id) {
              const existing = this.onlineMembers.find(e => e.id === updateData.user_id);
              if (existing) {
                existing.status = updateData.status || 'online';
              } else {
                this.onlineMembers.push({ id: updateData.user_id, status: updateData.status || 'online' });
              }
            } else if (updateData.reason === 'disconnect' && updateData.user_id) {
              this.onlineMembers = this.onlineMembers.filter(e => e.id !== updateData.user_id);
            }
          } catch(e) {
            this.fetchTeamMembers();
          }
        }
      } catch (error) {
        console.error("Failed to parse relay WebSocket message:", error);
      }
    },

    handleRelayKickedConfirm() {
      this.showRelayKickedDialog = false;
      this.stopRelayStatusCheck();
      this.forceShutDown = true;
      this.$router.replace({
        path: "/waiting-room",
        query: {
          template_id: this.template_id,
          exam_name: this.test_event
        }
      });
    },

    // Team Chat methods
    async fetchTeamMembers() {
      try {
        const res = await getRelayTeamMembers(
          this.user_id,
          this.template_id,
          this.project_code
        );
        if (res.data.code === 0) {
          this.teamMembers = res.data.data.members || [];
        }
      } catch (error) {
        console.error("Failed to fetch team members:", error);
      }
    },

    async fetchTeamChatMessages() {
      try {
        const res = await getRelayTeamChat(
          this.user_id,
          this.template_id,
          this.project_code
        );
        if (res.data.code === 0) {
          this.teamChatMessages = res.data.data.messages || [];
          this.$nextTick(() => {
            this.scrollTeamChatToBottom();
          });
        }
      } catch (error) {
        console.error("Failed to fetch team chat messages:", error);
      }
    },

    scrollTeamChatToBottom() {
      const chatContainer = this.$refs.teamChatMessages;
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    },

    formatChatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp * 1000);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    async sendTeamMessage() {
      if (!this.newTeamMessage.trim()) {
        this.$message.warning("Please enter a message");
        return;
      }

      const messageContent = this.newTeamMessage.trim();
      this.sendingTeamMessage = true;
      try {
        const res = await sendRelayTeamMessage(
          this.user_id,
          this.student_name,
          this.template_id,
          this.project_code,
          messageContent
        );
        if (res.data.code === 0) {
          const msgObj = {
            sender_id: this.user_id,
            sender_name: this.student_name,
            content: messageContent,
            send_time: Math.floor(Date.now() / 1000)
          };
          this.teamChatMessages.push(msgObj);
          this.sendRelayWsMessage("relay_chat", msgObj);
          this.newTeamMessage = "";
          this.$message.success("Message sent");
          this.$nextTick(() => {
            this.scrollTeamChatToBottom();
          });
          await this.fetchTeamChatMessages();
        } else {
          this.$message.error(res.data.msg || "Failed to send message");
        }
      } catch (error) {
        console.error("Failed to send team message:", error);
        this.$message.error("Failed to send message");
      } finally {
        this.sendingTeamMessage = false;
      }
    },

    handleTeamChatClose(done) {
      this.stopTeamChatRefresh();
      done();
    },

    startTeamChatRefresh() {
      // Refresh team members and messages every 5 seconds
      this.teamChatRefreshTimer = setInterval(() => {
        this.fetchTeamMembers();
        this.fetchTeamChatMessages();
      }, 5000);
    },

    stopTeamChatRefresh() {
      if (this.teamChatRefreshTimer) {
        clearInterval(this.teamChatRefreshTimer);
        this.teamChatRefreshTimer = null;
      }
    },

    startRelayStatusCheck() {
      this.relayStatusCheckTimer = setInterval(() => {
        this.checkRelayStatus();
      }, 10000);
    },

    stopRelayStatusCheck() {
      if (this.relayStatusCheckTimer) {
        clearInterval(this.relayStatusCheckTimer);
        this.relayStatusCheckTimer = null;
      }
    },

    async checkRelayStatus() {
      if (!this.relayMode) return;
      try {
        const entryRes = await checkRelayEntryCount(this.user_id, this.template_id, this.project_code);
        if (entryRes.data.code === 0) {
          const relayInExam = entryRes.data.data.relay_in_exam;
          if (!relayInExam && !this.showRelayKickedDialog && !this.showRelayExamSubmittedDialog) {
            this.stopRelayStatusCheck();
            const entryCount = entryRes.data.data.entry_count || 0;
            const msg = entryCount >= 2
              ? 'You have used up your 2 chances to join the relay.'
              : 'Another team member has taken over the exam. You will be redirected to the waiting room.';
            const title = entryCount >= 2 ? 'Entry Limit Reached' : 'Notice';
            this.$alert(msg, title, {
              confirmButtonText: 'OK',
              showClose: false,
              callback: () => {
                this.$router.replace({
                  path: "/waiting-room",
                  query: { template_id: this.template_id, exam_name: this.test_event }
                });
              }
            });
          }
        }
      } catch (e) {
        console.error('Failed to check relay status:', e);
      }
    },

    notifyLeaveExam() {
      if (this._hasNotifiedLeave) return;
      this._hasNotifiedLeave = true;
      const leaveData = new FormData();
      leaveData.append('function', 'leaveRelayExam');
      leaveData.append('student_id', this.user_id);
      leaveData.append('template_id', this.template_id);
      leaveData.append('project_code', this.project_code);
      navigator.sendBeacon('./index.php', leaveData);

      this.sendRelayWsMessage("relay_member_update", {
        reason: "leave_exam",
        user_id: this.user_id
      });
    }
  },

  beforeDestroy() {
    this.flushMaterialScrollLog();
    this.persistNetworkDisconnectState();
    // Stop relay status check timer
    this.stopRelayStatusCheck();
    // Stop team chat refresh timer
    this.stopTeamChatRefresh();
    // If in relay mode, disconnect WebSocket.
    // NOTE: notifyLeaveExam() is intentionally NOT called here to avoid a race condition:
    // the sendBeacon (leaveRelayExam) could arrive at the server AFTER a new enterRelayExam
    // call (from WaitingRoom re-entry) has already set relay_in_exam=true, overwriting it
    // with false. leaveRelayExam is only called from the beforeunload handler (actual page close).
    if (this.relayMode) {
      if (this.relayWs) {
        this.disconnectRelayWebSocket();
      }
    }
    if (this._handleBeforeUnload) {
      window.removeEventListener('beforeunload', this._handleBeforeUnload);
    }
    if (this.countDownThread) {
      window.clearInterval(this.countDownThread);
      this.countDownThread = null;
    }
    this.examCountDownDeadline = 0;
    this.clearWaitingCountDown();
    if (this.countDownRetryTimer) {
      window.clearTimeout(this.countDownRetryTimer);
      this.countDownRetryTimer = null;
    }
    if (this.windowCountDownThread) {
      window.clearInterval(this.windowCountDownThread);
      this.windowCountDownThread = null;
    }
    window.removeEventListener("offline", this.networkErrorCallback);
    window.removeEventListener("online", this.networkSuccessCallback);
    window.removeEventListener("focus", this.syncCountDownClock);
    window.removeEventListener("pageshow", this.syncCountDownClock);
    document.removeEventListener("visibilitychange", this.syncCountDownClock);
    document.removeEventListener("resume", this.syncCountDownClock);
    window.removeEventListener("popstate", this.goBack, false);
    this.stopExamIntegrityGuard();
    this.clearCountDown();
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
};
</script>

<style>
.exam-root {
  -webkit-user-select: none;
  user-select: none;
}

.exam-material-pane {
  position: relative;
  isolation: isolate;
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.exam-material-scroll {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 0;
}

.exam-material-scroll,
.exam-material-scroll * {
  cursor: none !important;
}

.proctoring-required-overlay {
  position: fixed;
  inset: 0;
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px;
  background: rgba(7, 45, 118, 0.76);
}

.proctoring-required-modal {
  width: min(520px, 100%);
  box-sizing: border-box;
  padding: 36px 42px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(0, 22, 68, 0.32);
  color: #072d76;
  text-align: center;
}

.proctoring-required-icon {
  margin-bottom: 14px;
  color: #4e8afc;
  font-size: 46px;
  line-height: 1;
}

.proctoring-required-title {
  margin-bottom: 14px;
  font-size: 23px;
  font-weight: 700;
  line-height: 1.3;
}

.proctoring-required-desc {
  color: #586785;
  font-size: 14px;
  line-height: 1.7;
}

.proctoring-required-clock {
  margin: 20px 0;
  padding: 12px 16px;
  border: 1px solid #d8eaff;
  border-radius: 8px;
  background: #f4f8ff;
  color: #315b9f;
  font-size: 14px;
}

.proctoring-required-error {
  margin: 0 0 18px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff2f0;
  color: #c43d32;
  font-size: 13px;
  line-height: 1.5;
}

.proctoring-required-note {
  margin-top: 14px;
  color: #878fb1;
  font-size: 12px;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .proctoring-required-modal {
    padding: 28px 22px;
  }
}

.network-error-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.network-error-modal {
  background: #fff;
  border-radius: 12px;
  padding: 40px 50px;
  text-align: center;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.network-error-icon {
  margin-bottom: 16px;
}

.network-error-title {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 12px;
}

.network-error-desc {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.network-error-report {
  display: block;
  margin-top: 14px;
}

.showAnswerResultDialogStyle {
  width: 100vw;
  height: 100vh;
  background-color: transparent;
  padding: 0;
  box-shadow: 0px 0px 0px 0px transparent;
}

.showMenuDialogStyle {
  background-color: transparent;
  padding: 0;
  box-shadow: 0px 0px 0px 0px transparent;
}

.showSubmitAnswerDialogStyle {
  background-color: transparent;
  padding: 0;
  box-shadow: 0px 0px 0px 0px transparent;
}

/* Submit Dialog Team Members Status Styles */
.submit-dialog-content {
  padding: 0 10px;
}

.team-members-status {
  margin-top: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  background-color: #f9f9f9;
}

.team-members-status .status-header {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 12px;
  color: #333;
}

.team-members-status .loading-members,
.team-members-status .no-members {
  color: #909399;
  font-size: 13px;
  text-align: center;
  padding: 10px 0;
}

.team-members-status .members-list {
  max-height: 200px;
  overflow-y: auto;
}

.team-members-status .member-status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
}

.team-members-status .member-status-item:last-child {
  border-bottom: none;
}

.team-members-status .member-name {
  font-size: 14px;
  color: #333;
}

.team-members-status .member-submission-status {
  font-size: 13px;
}

.team-members-status .status-submitted {
  color: #67c23a;
  font-weight: 500;
}

.team-members-status .status-not-submitted {
  color: #e6a23c;
  font-weight: 500;
}

.exam-integrity-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.72);
}

.exam-integrity-modal {
  width: min(90%, 520px);
  box-sizing: border-box;
  padding: 40px 50px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.32);
  text-align: center;
}

.exam-integrity-icon {
  margin-bottom: 16px;
}

.exam-integrity-title {
  margin-bottom: 16px;
  color: #333;
  font-size: 22px;
  font-weight: 700;
}

.exam-integrity-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.8;
}

.exam-integrity-detail {
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px solid #e6e9f2;
  border-radius: 6px;
  background: #f7f8fb;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}
</style>

<style lang="scss">
.exam-btn-qa {
  margin-left: 15px;
  cursor: pointer;
  font-size: 18px;
  color: white;
  font-family: "Microsoft YaHei-Bold";
  box-sizing: border-box;
  background-color: #f28801;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 6px;
}
.exam-btn-exchange-lang {
  margin-left: 15px;
  cursor: pointer;
  font-size: 18px;
  color: #4267c0;
  font-family: "Microsoft YaHei-Bold";
  box-sizing: border-box;
  background-color: #b1ccff;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 18px;
}

/deep/ .el-tabs__item {
  color: white !important;
}

/deep/ .el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
  color: white !important;
}

.drawer-question-list {
  width: 100%;
  height: 100%;
  background-color: #2d50b0;
  display: flex;
  flex-direction: column;
}

.drawer-answered {
  margin-left: 15px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  width: 80px;
  color: white;
  background-color: #2d50b0;
  border-radius: 17px;
  font-weight: 600;
  font-size: 16px;
  font-family: Montserrat-SemiBold;
}

.drawer-question-item-root {
  padding: 15px 15px 0 15px;
  cursor: pointer;
  background: #2d50b0;

  .drawer-question-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    color: white;
    font-size: 20px;
    background: #2d50b0;
    padding: 12px;
    box-sizing: border-box;
    font-family: Montserrat-SemiBold;
    border-radius: 16px;
  }

  .selected {
    color: #2d50b0;
    font-family: Montserrat-SemiBold;
    background: #ffffff;
    border-radius: 16px;
  }
}

.drawer-question-item-root:last-child {
  padding-bottom: 30px;
}

.exam-empty-width-view {
  width: 2.184vw;
  height: 100%;
  background-color: #d8eaff;
}

.exam-empty-height-view {
  height: 20px;
  width: 100%;
  background-color: #d8eaff;
}

.exam-question-content {
  padding: 12px;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
}

.exam-question-content::-webkit-scrollbar {
  display: none;
}

.exam-image {
  padding: 12px;
  width: 100%;
  box-sizing: border-box;
  overflow-y: scroll;
}
.exam-image1 {
  padding: 0px;
  width: 100%;
  box-sizing: border-box;
  overflow-y: scroll;
}

.exam-image::-webkit-scrollbar {
  display: none;
}

.module-material {
  box-sizing: border-box;
  margin: 18px 18px 6px;
  padding: 22px 24px;
  color: #18345f;
  text-align: left;
  background: #f7faff;
  border: 1px solid #c8d9f2;
  border-radius: 10px;
}

.module-material-kicker {
  margin-bottom: 6px;
  color: #42679f;
  font-family: Montserrat-SemiBold;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.module-material-copy {
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: Montserrat-Regular, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
}

.module-material-copy,
.module-material-copy * {
  font-family: Montserrat-Regular, sans-serif;
  font-weight: 400;
  font-synthesis: none;
}

.module-material-copy + .module-material-copy {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #dbe6f6;
}

.module-material-copy p {
  margin: 0 0 0.9em;
  font-weight: 400;
}

.module-material-copy p:last-child {
  margin-bottom: 0;
}

.module-material-copy strong,
.module-material-copy b {
  font-family: Montserrat-SemiBold, sans-serif;
  font-weight: 700;
}

.module-material-copy ul,
.module-material-copy ol {
  margin: 0.55em 0 1em;
  padding-left: 1.75em;
  list-style-position: outside;
}

.module-material-copy li {
  margin-bottom: 0.38em;
  padding-left: 0.2em;
}

.module-material-copy table {
  width: 100%;
  margin: 1em 0 1.2em;
  border: 1px solid #9fb4d6;
  border-collapse: collapse;
  table-layout: auto;
  background: #fff;
}

.module-material-copy th,
.module-material-copy td {
  padding: 8px 10px;
  border: 1px solid #9fb4d6;
  text-align: left;
  vertical-align: top;
}

.module-material-copy th {
  background: #eaf1fb;
}

.module-material-copy pre {
  display: block;
  max-width: 100%;
  margin: 1em 0 1.2em;
  padding: 12px 14px;
  overflow-x: auto;
  box-sizing: border-box;
  border: 1px solid #b8c9e4;
  border-radius: 6px;
  background: #e9f0fa;
  color: #17345f;
  white-space: pre;
}

.module-material-copy pre code {
  display: block;
  max-width: 100%;
  padding: 0;
  overflow-wrap: normal;
  background: transparent;
  white-space: inherit;
}

.module-material-copy code {
  padding: 0.12em 0.35em;
  border-radius: 3px;
  background: #e9f0fa;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 0.92em;
  font-weight: 400;
}

.module-material-copy img,
.module-material-copy video,
.module-material-copy table {
  max-width: 100%;
}

.module-material-copy img,
.module-material-copy video {
  height: auto;
}

.module-material-audio {
  width: 100%;
  margin-top: 16px;
}

.returnStyle {
  font-family: Montserrat-SemiBold;
  cursor: pointer;
  position: absolute;
  // display: flex;
  // margin-bottom: 10px;
  // align-items: center;
  bottom: 40px;
  margin-left: 45%;
  font-weight: 600;
  width: 140px;
  height: 30px;
  text-align: center;
  line-height: 30px;
  // align-content: center;
  // align-items: center;
  border-radius: 15px;
  background-color: #2d50b0;
  color: white;
}

.infoStyle {
  display: flex;
  border: 1px solid #2d50b0;
  border-radius: 16px;
  color: #2d50b0;
  margin-left: 20px;
  margin-top: -25px;
}

.infoImg {
  width: 14px;
  height: 14px;
}

.box {
  display: flex;
  width: 90%;
  justify-content: left;
  margin-left: 80px;
  margin-right: 80px;
  margin-top: 20px;
  // height: 250px;
  // background-color: #FD5900;
  height: 20vh;
  overflow-x: hidden;
  overflow-y: scroll;
  flex-wrap: wrap;
  align-content: flex-start;
  align-items: left;
  align-content: left;
  text-align: left;
  float: left;
}

.child {
  // width: 50px;
  // background-color: aqua;
  height: 30px;
  padding-left: 10px;
}

.awardSubTitle {
  text-align: left;
  color: #2d50b0;
  font-size: 15px;
  margin-top: 20px;
  margin-left: 80px;
}

.awardLine {
  background-color: #00359f;
  margin-left: 80px;
  margin-right: 80px;
  height: 1px;
  margin-top: 10px;
}

.awardTitle1 {
  font-family: Montserrat-SemiBold;
  font-weight: bold;
  text-align: left;
  color: #0036a0;
  font-size: 30px;
  margin-top: 60px;
  margin-left: 80px;
}

.scoreLab1 {
  font-family: Montserrat-SemiBold;
  margin-top: 60px;
  margin-right: 10px;
  height: 40px;
  //   background-color: rebeccapurple;
  text-align: right;
  font-size: 30px;
  color: #fd5900;
  float: right;
}

.scoreLab2 {
  font-family: Montserrat-SemiBold;
  margin-top: 65px;
  margin-right: 80px;
  height: 40px;
  //   background-color: rebeccapurple;
  text-align: right;
  color: #0f429e;
  font-size: 25px;
  float: right;
}

.awardTitle {
  font-family: Montserrat-SemiBold;
  font-weight: bold;
  text-align: center;
  color: #0f429e;
  font-size: 60px;
  margin-top: 30px;
  padding-top: 10px;
}

.answer-result-root {
  margin: 50px auto;
  padding: 0;
  background-image: url("../../assets/awardDetailBg.png");
  width: 80%;
  height: 70vh;
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: cover;
  position: relative;
  border-radius: 20px;
}

.menu-content {
  display: flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  margin: 0 auto;
  padding: 0;
  height: 100%;
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: cover;
}

.submitAnswer-root {
  position: relative;
  margin: 0 auto;
  padding: 0;
  width: 27vw;
  height: 59vh;
  background-image: url("../../assets/bg_submit_answer.png");
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: cover;
  border-radius: 20px;
}

.option {
  cursor: none;
  text-align: left;
  display: block;
  width: 100%;
  font-size: 1vw;
  margin-top: 24px;
  border-radius: 10px;
  font-weight: bold;
  color: #072d76;
  font-family: Montserrat-SemiBold;
}

.option-main {
  display: flex;
  align-items: center;
  width: 100%;
}

.candidate-inline-mark {
  position: relative;
  z-index: 3;
  margin: 6px 0 0 42px;
  color: rgba(25, 58, 119, 0.62);
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.035em;
  line-height: 1.4;
  text-align: right;
  overflow-wrap: anywhere;
}

.candidate-question-mark,
.candidate-material-mark {
  margin: 7px 0 0;
}

.div_p {
  font-family: Montserrat-SemiBold;
  font-size: 1vw;
  display: inline-block;
  color: #072d76;
}

.questionTitle {
  cursor: none;
  font-family: Montserrat-SemiBold;
  color: #072d76;
  font-size: 1.2vw;
  font-weight: bold;
  text-align: left;
  width: 100%;
}
.exam-goto-page {
  cursor: pointer;
  text-align: center;
  line-height: 50px;
  width: 200px;
  height: 100%;
  background-color: #072d76;
  font-family: "Microsoft YaHei-Bold";
  color: white;
  font-size: 18px;
  font-weight: bold;
}
.exam-menu {
  cursor: pointer;
  text-align: center;
  line-height: 50px;
  width: 140px;
  height: 100%;
  background-color: #072d76;
  font-family: "Microsoft YaHei-Bold";
  color: white;
  font-size: 18px;
  font-weight: bold;
}

.exam-page-bottom {
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content:center;
  // justify-content: space-around;
  background: #ffffff;
  // box-shadow: 0px -1px 24px 1px rgba(0, 14, 138, 0.1);
  border-radius: 0px 0px 0px 0px;
  opacity: 1;
  line-height: 50px;
  background-color: white;
}

.exam-page-bottom-text {
  cursor: pointer;
  font-size: 1.144vw;
  font-family: Microsoft YaHei-Bold;
  font-weight: bold;
  color: #2d50b0;
}

.exam-content-page {
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  height: 100%;
  border-radius: 16px;
  opacity: 1;
}

.exam-leave {
  cursor: pointer;
  font-size: 13px;
  height: 40px;
  line-height: 40px;
  color: white;
  box-sizing: border-box;
  background-color: #E6A23C;
  margin-bottom: 2px;
  text-align: center;
}

.exam-submit {
  // margin-right: 30px;

  // position: absolute;
  justify-content: flex-end;
  cursor: pointer;
  font-size: 13px;
  height: 40px;
  line-height: 40px;
  color: white;
  // font-family: "Microsoft YaHei-Bold";
  box-sizing: border-box;
  background-color: #BE3739;
  margin-bottom: 2px;

  // font-weight: bold;
  // border-radius: 4px;
}

.exam-submit-warning {
  display: inline-flex;
  align-items: flex-start;
  gap: 5px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 3px 8px;
  border-left: 3px solid #e6a23c;
  border-radius: 3px;
  background: #fff7e6;
  color: #7a4d0b;
  font-family: Montserrat-Regular, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
  i {
    flex-shrink: 0;
    margin-top: 2px;
    color: #e6a23c;
    font-size: 12px;
  }
}
.exam-right-rail {
  width: 150px;
  height: 100%;
  background-color: white;
  align-items: center;
  position: relative;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
.exam-submit-english {
  top: 80px;
  // margin-right: 30px;
  right: 130px;
  position: absolute;
  justify-content: flex-end;
  cursor: pointer;
  color: white;
  font-family: "Microsoft YaHei-Bold";
  box-sizing: border-box;
  background-color: #072d76;
  padding: 6px 14px;
  // font-weight: bold;
  border-radius: 4px;
}

.exam-count-down {
  color: #11448d;
  font-weight: bolder;
  font-size: 22px;
  padding-left: 5px;
  padding-right: 5px;
  // margin-right: 60px;
  margin-top: 5px;
  width: 70%;
  margin-left: 14%;
  text-align: center;
  color: white;
  background-color: #00359f;
}
.exam-count-down-lable {
  color: #333;
  // font-weight: bolder;
  font-size: 13px;
  height: 20px;
  // margin-right: 60px;
  // top: 60px;
  margin-top: 10px;
  margin-left: 10px;
  // width: 100%;
  // left: 15%;
  text-align: left;
}
.exam-progess-down{
  color: #11448d;
  font-weight: bolder;
  font-size: 25px;
  height:40px;
  padding-left: 5px;
  padding-right: 5px;
  // margin-right: 60px;
  margin-top: 5px;
  width: 100%;
  margin-left: 0;
  text-align: center;
  // background-color: rebeccapurple;
}
.exam-top-title {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
  height: 176px;
}

.exam-content {
  width: 100%;
  background-color: #d8eaff;
  // background-image: url("../../assets/magin_right_bg.png");
  flex: 1;
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: cover;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.exam-top {
  height: 128px;
  flex: 0 0 128px;
  display: grid;
  grid-template-rows: 68px 46px;
  gap: 4px;
  box-sizing: border-box;
  padding: 5px clamp(12px, 1.25vw, 22px);
  background: #be3739;
  position: relative;
}

.exam-top-main {
  display: grid;
  grid-template-columns: minmax(320px, 410px) minmax(0, 1fr);
  align-items: center;
  gap: clamp(14px, 1.5vw, 28px);
  min-width: 0;
}

.exam-proctor-summary {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 68px;
}

.exam-session-summary {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  color: #fff8f5;
  text-align: right;
}

.exam-penalty-bar {
  --penalty-accent: #78dfb6;
  min-width: 0;
  height: 46px;
  display: grid;
  grid-template-columns: 152px minmax(0, 1fr);
  grid-template-areas:
    "total metrics"
    "total rule";
  align-items: center;
  box-sizing: border-box;
  padding: 4px 10px 4px 0;
  color: #fff8f5;
  background: rgba(85, 20, 28, 0.94);
  border: 1px solid rgba(255, 240, 235, 0.28);
  font-family: Montserrat-Regular, sans-serif;
  text-align: left;
}

.exam-penalty-bar.is-warning {
  --penalty-accent: #f3bd59;
}

.exam-penalty-bar.is-danger {
  --penalty-accent: #ff938b;
}

.exam-penalty-total {
  grid-area: total;
  align-self: stretch;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-content: center;
  align-items: baseline;
  gap: 0 6px;
  padding: 0 12px;
  border-right: 1px solid rgba(255, 240, 235, 0.2);
}

.exam-penalty-total > span {
  color: rgba(255, 241, 237, 0.74);
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.exam-penalty-total strong {
  color: var(--penalty-accent);
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 18px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
}

.exam-penalty-total strong small,
.exam-penalty-metric strong small {
  font-size: 10px;
  font-weight: 600;
}

.exam-penalty-total em {
  grid-column: 1 / -1;
  color: var(--penalty-accent);
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 8px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.exam-penalty-metrics {
  grid-area: metrics;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  min-width: 0;
}

.exam-penalty-metric {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 0 6px;
  padding: 0 10px;
  border-right: 1px solid rgba(255, 240, 235, 0.14);
}

.exam-penalty-metric:last-child {
  border-right: 0;
}

.exam-penalty-metric > span {
  min-width: 0;
  overflow: hidden;
  color: #fff8f5;
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exam-penalty-metric strong {
  color: var(--penalty-accent);
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.exam-penalty-metric > small {
  grid-column: 1 / -1;
  margin-top: 1px;
  color: rgba(255, 238, 234, 0.64);
  font-size: 8px;
  line-height: 1.2;
}

.exam-penalty-rule {
  grid-area: rule;
  min-width: 0;
  margin: 2px 10px 0;
  color: rgba(255, 238, 234, 0.72);
  font-size: 8px;
  line-height: 1.25;
}

.exam-root {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0 auto;
  text-align: center;
}

.messageList {
  width: 500px;
  height: 90%;
  left: 5%;
  background-color: white;
  margin-top: 30px;
  overflow-x: hidden;
  overflow-y: scroll;
  border-radius: 10px;
  text-align: left;
}

.inputStyle {
  width: 100%;
  height: 60px;
}

.inputSelect {
  margin-left: 20px;
  width: 150px;
  height: 40px;
}

.inputStyle1 {
  width: 60%;
  height: 40px;
  margin-left: 20px;
  margin-top: 10px;
}

.sendStyle {
  margin-left: 10px;
  background-color: #072d76;
  color: white;
  font-family: Montserrat-SemiBold;
}

.messageList {
  width: 90%;
  height: 90%;
  background-color: white;
  margin-top: 30px;
  overflow-x: hidden;
  overflow-y: scroll;
  border-radius: 10px;
  text-align: left;
}

.list {
  overflow-x: hidden;
  overflow-y: scroll;
}

.messageDetail {
  margin-left: 20px;
  margin-top: 30px;
  width: 90%;
  font-family: Montserrat-SemiBold;
  // height: 50px;
  text-align: left;
}

.studentmessageList {
  width: calc(80% + 30px);
  height: calc(90% - 90px);
  background-color: #072d76;
  margin-top: 30px;
  text-align: left;
}

.redTip {
  position: absolute;
  margin-left: 3vw;
  margin-top: 5px;
  background-color: red;
  width: 12px;
  height: 12px;
  border-radius: 6px;
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: #f3f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-container {
  width: 600px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.loading-header {
  background: linear-gradient(135deg, #3e93f3 0%, #6366f1 100%);
  border-radius: 12px;
  padding: 28px 32px;
  text-align: center;
  color: #fff;
}
.loading-project {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.85;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.loading-exam-name {
  font-size: 22px;
  font-weight: 700;
}
.loading-countdown-section {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.loading-countdown-label {
  font-size: 14px;
  color: #878fb1;
  margin-bottom: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.loading-countdown-boxes {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.countdown-box {
  width: 80px;
  height: 72px;
  line-height: 72px;
  font-size: 36px;
  font-weight: 700;
  color: #3e93f3;
  background: #f0f7ff;
  border: 2px solid #d8eaff;
  border-radius: 10px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.countdown-sep {
  font-size: 32px;
  font-weight: 700;
  color: #3e93f3;
  line-height: 72px;
}
.loading-countdown-hint {
  margin-top: 16px;
  font-size: 13px;
  color: #a0a6c0;
  line-height: 1.6;
}
.loading-instructions {
  background: #fff;
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.instructions-title {
  font-size: 16px;
  font-weight: 600;
  color: #3e4a6b;
  margin-bottom: 14px;
}
.instructions-list {
  margin: 0;
  padding-left: 20px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.8;
}
.instructions-list li {
  margin-bottom: 4px;
}
.projectStyle {
  width: 100%;
  overflow: hidden;
  color: #fff8f5;
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: clamp(14px, 1.05vw, 19px);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invigilateStyle {
  display: flex;
  width: 100%;
  height: 68px;
  margin: 0;
}

@media (max-width: 1100px) {
  .exam-top-main {
    grid-template-columns: minmax(270px, 340px) minmax(0, 1fr);
    gap: 10px;
  }

  .exam-penalty-bar {
    grid-template-columns: 132px minmax(0, 1fr);
  }

  .exam-penalty-total,
  .exam-penalty-metric {
    padding-right: 7px;
    padding-left: 7px;
  }

  .exam-penalty-rule {
    margin-right: 6px;
    margin-left: 6px;
    font-size: 7px;
  }
}
.changeCountStyle {
  
  width: 100%;
  text-align: center;
  justify-content: flex-end;
  cursor: pointer;
  color: #BE3739;
  font-family: "Microsoft YaHei-Bold";
  background-color: #EFB93F;
  height: 40px;
  line-height: 40px;
  font-size: 13px;
}

.inputAnswerStyle {
  cursor: none;
  text-align: left;
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 1vw;
  margin-top: 24px;
  border-radius: 10px;
  font-weight: bold;
  color: #072d76;
  font-family: Montserrat-SemiBold;
}
.attach{
  float: left;
}
.rightMiddleDiv{
  // overflow: auto;
  width: 100%;
  // height: calc(100% - 376px);
  background-color: #f2f2f2;
}
.bottomDiv{
  position:absolute;
  height: 250px;
  bottom: 20px;
  // margin-bottom: 20px;
  width: 100%;
  background-color: #f2f2f2;
}
.exam-message{
  width: 100%;
  height: 40px;
  font-size: 13px;
  line-height: 40px;
  color: white;
  background-color: #36AB7B;
  margin-bottom: 2px;
  cursor: pointer;

}
.exam-team-chat{
  width: 100%;
  height: 40px;
  font-size: 13px;
  line-height: 40px;
  color: white;
  background-color: rgb(54, 171, 123);
  margin-bottom: 2px;
  cursor: pointer;

}
.exam-content-nourl-page{
  height: calc(100% - 40px);
  overflow-y: auto;
  word-break: normal;
  text-align: left;
  color: #072d76;
  font-size: 15px;
  padding: 10px;
}
.reloadDiv{
  width: 100px;
  height: 40px;
  font-size: 13px;
  line-height: 40px;
  color: white;
  background-color: #36AB7B;
  margin-left: 5px;
  cursor: pointer;

}
.reloadDiv.is-disabled {
  opacity: 0.58;
  pointer-events: none;
}

/* Station Navigation Tabs */
.station-nav-tabs {
  display: flex;
  flex-direction: column;
  width: 80px;
  background-color: #2d50b0;
  padding: 10px 5px;
  gap: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
}

.station-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 5px;
  background-color: #4267c0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 60px;
}

.station-tab:hover {
  background-color: #5a7fd6;
}

.station-tab-locked {
  cursor: not-allowed;
  opacity: 0.45;
}

.station-tab-completed:not(.station-tab-active) {
  background-color: #5274ba;
  opacity: 0.72;
}

.station-tab-locked:hover,
.station-tab-completed:hover {
  background-color: #4267c0;
}

.station-tab-active {
  background-color: #ffffff;
  color: #2d50b0;
}

.station-tab-active .station-tab-number {
  color: #2d50b0;
}

.station-tab-active .station-tab-label {
  color: #2d50b0;
}

.station-tab-number {
  font-size: 20px;
  font-weight: bold;
  color: white;
}

.station-tab-label {
  font-size: 11px;
  color: white;
  margin-top: 4px;
  text-align: center;
}

/* Team Chat Styles */
.exam-team-chat {
  background-color: #2d50b0 !important;
}

.team-chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  padding: 15px;
}

.team-chat-warning {
  background-color: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #e6a23c;
  line-height: 1.5;
}

.team-chat-members {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 15px;
}

.team-chat-members-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
}

.team-chat-members-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.team-member-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #606266;
  gap: 6px;
}

.exam-member-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #909399;
  flex-shrink: 0;
}

.exam-member-dot.dot-online {
  background-color: #67c23a;
}

.exam-member-dot.dot-in-exam {
  background-color: #ff9800;
}

.exam-member-name {
  flex: 1;
}

.exam-member-status-text {
  font-size: 12px;
  margin-left: auto;
}

.team-chat-messages {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #fafafa;
}

.chat-message {
  margin-bottom: 15px;
  max-width: 80%;
}

.chat-message-self {
  margin-left: auto;
  text-align: right;
}

.chat-message-other {
  margin-right: auto;
  text-align: left;
}

.chat-message-header {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.chat-message-name {
  font-weight: 500;
  margin-right: 8px;
}

.chat-message-time {
  font-size: 11px;
}

.chat-message-content {
  display: inline-block;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.chat-message-self .chat-message-content {
  background-color: #2d50b0;
  color: white;
}

.chat-message-other .chat-message-content {
  background-color: white;
  color: #303133;
  border: 1px solid #e4e7ed;
}

.team-chat-input {
  display: flex;
  gap: 10px;
}

.team-chat-input .el-input {
  flex: 1;
}

/* Team Submit Dialog Styles */
.team-submit-content {
  padding: 10px 0;
}

.stage-submit-dialog {
  max-width: calc(100vw - 32px);
}

.stage-submit-dialog .el-dialog__body {
  padding: 20px 24px 14px;
}

.stage-submit-dialog .el-dialog__footer {
  padding: 14px 24px 22px;
}

.stage-submit-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  width: 100%;
}

.stage-submit-actions .el-button {
  min-width: 0;
  height: 42px;
  margin: 0;
  padding: 0 13px;
  white-space: normal;
  line-height: 1.2;
}

.stage-submit-actions .el-button + .el-button {
  margin-left: 0;
}

.stage-submit-cancel {
  flex: 0.72;
}

.stage-submit-exit {
  flex: 1;
}

.stage-submit-start-next {
  flex: 1.4;
}

.stage-submit-actions .stage-submit-exit:not(.is-disabled) {
  border-color: #2d50b0;
  background-color: #2d50b0;
}

.stage-submit-actions .stage-submit-exit:not(.is-disabled):hover,
.stage-submit-actions .stage-submit-exit:not(.is-disabled):focus {
  border-color: #244393;
  background-color: #244393;
}

.stage-submit-actions .stage-submit-start-next:not(.is-disabled) {
  border-color: #df6b20;
  background-color: #df6b20;
}

.stage-submit-actions .stage-submit-start-next:not(.is-disabled):hover,
.stage-submit-actions .stage-submit-start-next:not(.is-disabled):focus {
  border-color: #be5412;
  background-color: #be5412;
}

.team-submit-notice {
  background-color: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #e6a23c;
  line-height: 1.5;
}

.stage-submit-confirmation {
  margin-top: 22px;
  text-align: left;
}

.stage-submit-label {
  display: block;
  margin-bottom: 8px;
  color: #243d6b;
  font-family: Montserrat-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 600;
}

.stage-submit-validation {
  min-height: 20px;
  padding-top: 6px;
  color: transparent;
  font-size: 12px;
  line-height: 1.4;
}

.stage-submit-validation.is-visible {
  color: #c9342f;
}

.team-submit-members {
  margin-bottom: 20px;
}

.team-submit-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 15px;
}

.team-submit-member-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 8px;
}

.member-name {
  font-size: 14px;
  color: #303133;
}

.member-submit-status {
  font-size: 13px;
  color: #909399;
  padding: 4px 12px;
  border-radius: 4px;
  background-color: #ebeef5;
}

.member-submit-status.submitted {
  color: #67c23a;
  background-color: #f0f9eb;
}

.team-submit-count {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: #2d50b0;
  padding: 15px;
  background-color: #ecf5ff;
  border-radius: 4px;
}
/* Team Chat Drawer Styles */
.team-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 15px 15px 15px;
}

.team-members-section {
  margin-bottom: 15px;
}

.section-header {
  font-size: 16px;
  font-weight: bold;
  color: #072d76;
  padding: 10px 0;
  border-bottom: 2px solid #072d76;
  margin-bottom: 10px;
}

.members-list {
  max-height: 150px;
  overflow-y: auto;
}

.exam-submit-member-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.exam-submit-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
}

.exam-submit-indicator.online {
  background-color: #4caf50;
}

.exam-submit-indicator.offline {
  background-color: #9e9e9e;
}

.exam-submit-member-name {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.exam-in-exam-tag {
  font-size: 12px;
  color: #ff9800;
  background-color: #fff3e0;
  padding: 2px 8px;
  border-radius: 10px;
}

.no-members {
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
}

.chat-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 15px;
}

.chat-warning {
  background: #fff3cd;
  color: #856404;
  padding: 10px;
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 10px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  background: #fafafa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #eee;
}

.chat-message {
  max-width: 80%;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.chat-message.my-message {
  margin-left: auto;
  background: #e3f2fd;
}

.message-sender {
  font-size: 12px;
  color: #666;
  font-weight: bold;
  margin-bottom: 4px;
  text-align: left;
}

.message-content {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  word-wrap: break-word;
  text-align: left;
}

.message-time {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: right;
}

.no-messages {
  text-align: center;
  color: #999;
  padding: 30px;
  font-size: 14px;
}

.send-message-section {
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.exam-completed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;

  .completion-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #4CAF50;
    color: white;
    font-size: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
  }

  .completion-title {
    font-size: 24px;
    font-weight: bold;
    color: #333;
    margin-bottom: 12px;
    font-family: "Microsoft YaHei", sans-serif;
  }

  .completion-subtitle {
    font-size: 16px;
    color: #666;
    max-width: 400px;
    line-height: 1.5;
    font-family: "Microsoft YaHei", sans-serif;
  }
}
</style>
